import { useState, useEffect, useId, useRef, useCallback } from 'react';
import {
  ChevronRight,
  Trophy,
  Clock,
  MapPin,
  ArrowLeft,
  Plus,
  X,
  Play,
  Check,
  Shuffle,
  Save,
  Edit2,
  Calendar,
  Trash2,
} from 'lucide-react';
import { TOURNAMENT_TYPES, ELO_LEVELS, getTournamentType, getEloLevel } from '../../engine';
import { Storage } from '../../lib/storage';
import { RichText, Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { BookingPaymentModal } from '../booking';
import { PlayerEditModal } from '../players';
import { AddSingleMatchModal } from '../singlematches';
import { useT } from '../../i18n';

export interface ActivitiesViewProps {
  /** Effective (tenant-scoped) player database. */
  effectivePlayerDB?: any[];
  /** Current club info (name, courts, logo). */
  clubInfo?: any;
  /** The series being signed up for (seriespill_season activities); null when unused. */
  seriesForSignup?: any;
  /** When true, re-opens the "Opprett aktivitet" picker (after returning from a setup flow). */
  reopenPicker?: boolean;
  /** Called once the picker has been re-opened so the parent can reset its flag; null when unused. */
  onPickerReopened?: ((...args: any[]) => void) | null;
  /** Returns to the home surface. */
  onBack: (...args: any[]) => void;
  /** Starts (or previews) a tournament-format activity. */
  onStartActivity: (...args: any[]) => void;
  /** Persists a brand-new player created inline from the partner picker; null when unused. */
  onPersistNewPlayer?: ((...args: any[]) => void) | null;
  /** Court bookings (the club calendar). */
  bookings?: any[];
  /** Reserves courts for an activity. */
  onReserveCourts: (...args: any[]) => void;
  /** Releases all courts of an activity. */
  onReleaseCourts: (...args: any[]) => void;
  /** Releases only future (date ≥ today) courts of an activity. */
  onReleaseFutureCourts: (...args: any[]) => void;
  /** Releases specific court numbers of an activity. */
  onReleaseCourtNumbers: (...args: any[]) => void;
  /** Adds one more court to an activity; returns `{ created, court }`. */
  onAddCourtToActivity: (...args: any[]) => void;
  /** Activities list. */
  activities?: any[];
  /** Persists the full activities list. */
  onPersist: (...args: any[]) => void;
  /** Activity id to open immediately; null for the list view. */
  initialSelectedId?: any;
  /** Opens the visual court-reservation calendar for an activity id. */
  onOpenCourtReservation: (...args: any[]) => void;
  /** Opens the full setup editor for an activity; null when unused. */
  onEditFullSetup?: ((...args: any[]) => void) | null;
  /**
   * Deletes a recurring activity series by scope; null when unused. Answers
   * `{ refused: true }` when the server refused it and nothing changed (#1380).
   */
  onDeleteActivitySeries?: ((...args: any[]) => unknown) | null;
  /** Deletes all activities (demo wipe); null when unused. */
  onDeleteAllActivities?: ((...args: any[]) => void) | null;
  /** Creates a new activity/tournament from the Aktiviteter picker; null when unused. */
  onCreateViaActivities?: ((...args: any[]) => void) | null;
  /**
   * Completes a single-match activity: takes the captured best-of-3 result,
   * applies ELO via the single-match pipeline and marks the activity done.
   * Returns { success, error? }. Null when unused.
   */
  onCompleteSingleMatchActivity?:
    ((activity: any, data: any) => Promise<{ success: boolean; error?: string }>) | null;
}

/**
 * Activities surface ("Aktiviteter") — the club's sign-up hub for tournament
 * formats and custom sessions (coaching/arrangement/egendefinert). It covers the
 * filterable/grouped activity list, the create-activity pickers, and the full
 * per-activity detail/sign-up flow (admin + simulated "logged-in" user view) for
 * every format: standard, team, mix, club, club-team, KM (klubbmesterskap),
 * Padel24 with standby-reserves, custom sessions, and the seriespill season
 * sign-up. Extracted verbatim (byte-for-byte body) from the former monolith
 * (legacy/App.jsx); markup/logic unchanged. All engine calls (`getTournamentType`,
 * `getEloLevel`, `ELO_LEVELS`, `TOURNAMENT_TYPES`) are bare names resolving to the
 * engine-barrel imports above; `Storage` comes from `../../lib/storage`; the
 * extracted children `RichText`, `BookingPaymentModal` and `PlayerEditModal` are
 * imported from their barrels (none take a `Padel24Engine` prop, so this view
 * needs none).
 */
export function ActivitiesView({
  effectivePlayerDB = [],
  clubInfo,
  seriesForSignup = null,
  reopenPicker = false,
  onPickerReopened = null,
  onBack,
  onStartActivity,
  onPersistNewPlayer = null,
  bookings = [],
  onReserveCourts,
  onReleaseCourts,
  onReleaseFutureCourts,
  onReleaseCourtNumbers,
  onAddCourtToActivity,
  activities = [],
  onPersist,
  initialSelectedId = null,
  onOpenCourtReservation,
  onEditFullSetup = null,
  onDeleteActivitySeries = null,
  onDeleteAllActivities = null,
  onCreateViaActivities = null,
  onCompleteSingleMatchActivity = null,
}: ActivitiesViewProps) {
  // `tr`, not `t`: `t` is a tournament type / search term throughout.
  const tr = useT();
  // Ids that name and describe this view's dialogs and tie its labels to their
  // controls (#900). `safeId` strips React's colons so the ids stay valid CSS.
  const uid = safeId('activities', useId());
  const dlg = {
    standbyTitle: `${uid}-standby-title`,
    standbyBody: `${uid}-standby-body`,
    standbyDays: `${uid}-standby-days`,
    swapTitle: `${uid}-swap-title`,
    swapBody: `${uid}-swap-body`,
    delMenuTitle: `${uid}-delmenu-title`,
    delMenuBody: `${uid}-delmenu-body`,
    delTitle: `${uid}-del-title`,
    delBody: `${uid}-del-body`,
    createTitle: `${uid}-create-title`,
    createBody: `${uid}-create-body`,
    createCustom: `${uid}-create-custom`,
    createFormats: `${uid}-create-formats`,
    customTitle: `${uid}-custom-title`,
    wipeTitle: `${uid}-wipe-title`,
    wipeBody: `${uid}-wipe-body`,
    sort: `${uid}-filter-sort`,
    status: `${uid}-filter-status`,
    type: `${uid}-filter-type`,
    venue: `${uid}-filter-venue`,
  };
  // Where focus starts in the two delete confirmations: «Avbryt», so a stray
  // Enter cancels rather than deletes (#900, as on the home screen).
  const delCancelRef = useRef<HTMLButtonElement | null>(null);
  const wipeCancelRef = useRef<HTMLButtonElement | null>(null);
  // THE PARTNER SEARCH TAKES FOCUS WHEN IT APPEARS, as its `autoFocus` did
  // (#900) — `autoFocus` is what jsx-a11y forbids, not the behaviour. A STABLE
  // callback ref, so it runs once when the field mounts and not on every
  // keystroke's re-render. It also records the field, so the swap dialog can
  // name it as its `initialFocus`: the Dialog moves focus in an effect, AFTER
  // this ref has run, and would otherwise put it on the ✕.
  const partnerSearchRef = useRef<HTMLInputElement | null>(null);
  const focusPartnerSearch = useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    partnerSearchRef.current = el;
    el.focus();
  }, []);
  // Short and long weekday/month names, for DISPLAY only — every stored or
  // compared date below is still built from local date parts (DOMAIN_RULES §4).
  const wdShort = [
    tr('home.weekday.short.sun'),
    tr('home.weekday.short.mon'),
    tr('home.weekday.short.tue'),
    tr('home.weekday.short.wed'),
    tr('home.weekday.short.thu'),
    tr('home.weekday.short.fri'),
    tr('home.weekday.short.sat'),
  ];
  const wdLong = [
    tr('home.weekday.long.sun'),
    tr('home.weekday.long.mon'),
    tr('home.weekday.long.tue'),
    tr('home.weekday.long.wed'),
    tr('home.weekday.long.thu'),
    tr('home.weekday.long.fri'),
    tr('home.weekday.long.sat'),
  ];
  const moShort = [
    tr('home.month.short.jan'),
    tr('home.month.short.feb'),
    tr('home.month.short.mar'),
    tr('home.month.short.apr'),
    tr('home.month.short.may'),
    tr('home.month.short.jun'),
    tr('home.month.short.jul'),
    tr('home.month.short.aug'),
    tr('home.month.short.sep'),
    tr('home.month.short.oct'),
    tr('home.month.short.nov'),
    tr('home.month.short.dec'),
  ];
  const moLong = [
    tr('home.month.long.jan'),
    tr('home.month.long.feb'),
    tr('home.month.long.mar'),
    tr('home.month.long.apr'),
    tr('home.month.long.may'),
    tr('home.month.long.jun'),
    tr('home.month.long.jul'),
    tr('home.month.long.aug'),
    tr('home.month.long.sep'),
    tr('home.month.long.oct'),
    tr('home.month.long.nov'),
    tr('home.month.long.dec'),
  ];
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [showCreatePicker, setShowCreatePicker] = useState(false);
  // Re-open the "Opprett aktivitet" picker when the user has returned from a
  // tournament setup that was started from here (via "Tilbake").
  useEffect(() => {
    if (reopenPicker) {
      setShowCreatePicker(true);
      onPickerReopened?.();
    }
    // deviation (lint): monolith intentionally watches only `reopenPicker`; the
    // omitted `onPickerReopened` is a stable callback prop. Behaviour unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopenPicker]);
  const [customDraft, setCustomDraft] = useState(null); // {type, name, date, startTime, endTime, maxParticipants, allowWaitlist, maxReserves, reserveCourts, location, description}
  // Result-entry modal for a single-match activity (true while open).
  const [singleMatchResultOpen, setSingleMatchResultOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSearchA, setPickerSearchA] = useState(''); // mix: men / club: Club A
  const [pickerSearchB, setPickerSearchB] = useState(''); // mix: women / club: Club B
  // deviation (lint): `confirmDel` value is set but never read in this range (the
  // confirm dialog reads `delConfirm`); `_`-prefixed to satisfy no-unused-vars.
  const [_confirmDel, setConfirmDel] = useState(false);
  const [delFuture, setDelFuture] = useState(true); // remove future bookings (date ≥ today) on delete
  const [delMenu, setDelMenu] = useState(false); // popup: choice of delete scope (series)
  const [delConfirm, setDelConfirm] = useState(null); // popup: confirmation { scope, count, bk } | { scope:'single' }
  const [busy, setBusy] = useState(false);
  const [actSort, setActSort] = useState('new'); // new|old|name|date
  // Tab ids are INTERNAL state, never persisted and never sent anywhere. They
  // were `alle|turneringer|aktiviteter`; renamed to English so the surface
  // carries no Norwegian identifiers that a prose detector has to be taught
  // to ignore (#310a). Behaviour is unchanged — the default is still «Alle».
  const [actTab, setActTab] = useState('all'); // all|tournaments|activities
  const [pastExpanded, setPastExpanded] = useState(false); // "Tidligere" collapsed by default
  const [actStatus, setActStatus] = useState('all'); // all|open|waitlist|closed|cancelled
  const [actType, setActType] = useState('all'); // all|<typeId>
  const [actVenue, setActVenue] = useState('all'); // all|<venueName>
  const [adminMode, setAdminMode] = useState(true); // true = admin (all options, default for you), false = user view (only "Meld på")
  const [wipeConfirm, setWipeConfirm] = useState(false); // DEMO: confirmation for "Slett alle"
  const [wiping, setWiping] = useState(false);
  // deviation (lint): `setSignupSearch` is never called in this range; `_`-prefixed
  // to satisfy no-unused-vars (the `signupSearch` value is still read below).
  const [signupSearch, _setSignupSearch] = useState(''); // search in the "Meld deg på" picker
  const [pendingOverride, setPendingOverride] = useState(null); // player outside the level that admin is considering adding anyway
  const [pendingTeamP1, setPendingTeamP1] = useState(null); // team: selected player 1, awaiting partner
  const [selfPairPicking, setSelfPairPicking] = useState(false); // self-service: picking a partner
  const [selfPartnerSearch, setSelfPartnerSearch] = useState('');
  // Standby reserve — draft saved with its own "Lagre" button (null = use the saved value)
  const [standbyDraftOn, setStandbyDraftOn] = useState(null);
  const [standbyDraftDays, setStandbyDraftDays] = useState(null);
  // "+ Ny spiller" in the partner picker opens the shared NewPlayerModal wizard.
  // npModalCtx = { onPick, lockGender, initialName } — null = closed.
  const [npModalCtx, setNpModalCtx] = useState(null);
  const [kmTipKey, setKmTipKey] = useState(null); // bucketKey for the hover info on the men's class (female player)
  const [selfSwap, setSelfSwap] = useState(false); // self-service: swapping an existing partner
  const [adminSwapReg, setAdminSwapReg] = useState(null); // admin: the pair swapping a partner
  const [teamChoiceFor, setTeamChoiceFor] = useState(null); // admin: selected person, awaiting "single or partner" choice (both mode)
  const [clubAddFor, setClubAddFor] = useState('clubA'); // club: which club admin is adding to
  const [pendingQuotaForce, setPendingQuotaForce] = useState(null); // {player, label, onConfirm} — override the gender/club quota
  const [selfClub, setSelfClub] = useState(null); // club_team: the player's chosen club for their own sign-up
  const [demoUserId, setDemoUserId] = useState(null); // which player profile we pretend is "logged in" (demo)
  const [profileSwitch, setProfileSwitch] = useState(false); // profile switcher open
  const [profileSearch, setProfileSearch] = useState('');
  const [releaseSel, setReleaseSel] = useState(null); // which courts admin has chosen to release (null = default)
  // deviation (lint): `armLock` value is set but never read in this range;
  // `_`-prefixed to satisfy no-unused-vars (`setArmLock` is still called).
  const [_armLock, setArmLock] = useState(false); // true once admin has pressed lock and is about to pick courts
  const [payIntent, setPayIntent] = useState(null); // payment: null | { kind:'individual'|'single'|'pair', partnerId?, amount, label }
  const [standbyPrompt, setStandbyPrompt] = useState(null); // padel24 reserve: standby choice BEFORE payment | { amount, label, days, on }
  useEffect(() => {
    setReleaseSel(null);
    setArmLock(false);
    setSelfClub(null);
    setPendingTeamP1(null);
    setTeamChoiceFor(null);
    setNpModalCtx(null);
  }, [selectedId]);
  useEffect(() => {
    (async () => {
      try {
        const r = await Storage.get('demo_user_v1');
        if (r?.value) setDemoUserId(JSON.parse(r.value));
      } catch {
        /* ignore: best-effort, non-critical */
      }
    })();
  }, []);
  const pickDemoUser = (id) => {
    setDemoUserId(id);
    setProfileSwitch(false);
    setProfileSearch('');
    try {
      Storage.set('demo_user_v1', JSON.stringify(id));
    } catch {
      /* ignore: best-effort, non-critical */
    }
  };

  const typeOf = (a) =>
    typeof getTournamentType === 'function'
      ? getTournamentType(a?.type || a?.config?.tournamentType || 'americano')
      : null;
  const courtsOf = (a) =>
    [...new Set((bookings || []).filter((b) => b.seriesId === a?.id).map((b) => b.court))].filter(
      (n) => n != null,
    ).length;
  const isCustomType = (t) => t === 'coaching' || t === 'arrangement' || t === 'custom';
  // Custom activities (coaching/arrangement/custom) with pair sign-up reuse
  // the whole team sign-up module (give a partner + pay for both).
  const isTeamActivity = (a) => {
    const t = a?.config?.tournamentType || '';
    return /team/.test(t) || (isCustomType(t) && a?.config?.teamSignupMode === 'pairs');
  };
  // Custom pairs are priced per team (the field = team price, paid once). Otherwise price per player → a pair pays ×2.
  const isPerTeamPriced = (a) =>
    isCustomType(a?.config?.tournamentType || '') && a?.config?.teamSignupMode === 'pairs';
  const pairPriceOf = (a) => {
    const p = a?.config?.pricePerPlayer || 0;
    return isPerTeamPriced(a) ? p : p * 2;
  };
  // MIX (gender quota — half men, half women): Mix Americano (individual)
  // and Mix Mexicano ('mixicano', 1 woman + 1 man per team). Both require gender balance.
  const isMixActivity = (a) => {
    const t = a?.config?.tournamentType || '';
    return t === 'mix_americano' || t === 'mixicano';
  };
  const genderOf = (pid) => effectivePlayerDB.find((p) => p.id === pid)?.gender || 'M';
  const mixHalf = (a) => {
    const m = unitMax(a);
    return m != null ? Math.floor(m / 2) : null;
  };
  // CLUB (club quota — equal numbers from each of 2 clubs): the player chooses a club at sign-up.
  const isClubActivity = (a) => {
    const t = a?.config?.tournamentType || '';
    return (
      (t === 'club_americano' || t === 'club_mexicano') &&
      Array.isArray(a?.config?.clubs) &&
      a.config.clubs.length === 2
    );
  };
  // CLUB TEAM AMERICANO: team + club combined — teams per club, singles are paired only within their own club.
  const isClubTeamActivity = (a) =>
    (a?.config?.tournamentType || '') === 'club_team_americano' &&
    Array.isArray(a?.config?.clubs) &&
    a.config.clubs.length === 2;
  const clubTeamSlotsPerClub = (a) => {
    const u = unitMax(a);
    return u != null ? Math.floor(u / 2) : null;
  }; // team slots per club
  // KLUBBMESTERSKAP (KM): pairs sign up for a specific class (bucket). Pairs only.
  // Gender rule per class: Men = 2 men, Women = 2 women, Mix = 1 man + 1 woman.
  const isKmActivity = (a) =>
    (a?.config?.tournamentType || '') === 'klubbmesterskap' &&
    Array.isArray(a?.config?.kmBuckets) &&
    a.config.kmBuckets.length > 0;
  const kmBucketsOf = (a) => (Array.isArray(a?.config?.kmBuckets) ? a.config.kmBuckets : []);
  // Price per TEAM for a class (fallback: old price-per-player × 2, then default).
  const kmBucketPrice = (a, bk) => {
    const b = kmBucketsOf(a).find((x) => x.bucketKey === bk);
    if (b?.priceNok != null) return Math.max(0, Number(b.priceNok) || 0);
    const legacy = a?.config?.pricePerPlayer;
    return legacy != null ? Math.max(0, Number(legacy) || 0) : 0;
  };
  // Category (catId) for a bucket.
  const kmCatOfBucket = (a, bk) => kmBucketsOf(a).find((x) => x.bucketKey === bk)?.catId || null;
  // Is the player already signed up in ANOTHER class in the same category? (Men+Mix is OK, but not two men's classes.)
  const kmPlayerInCat = (a, playerId, catId, excludeBucketKey = null) => {
    if (!playerId || !catId) return false;
    const catKeys = new Set(
      kmBucketsOf(a)
        .filter((b) => b.catId === catId && b.bucketKey !== excludeBucketKey)
        .map((b) => b.bucketKey),
    );
    return (a.registrations || []).some(
      (r) =>
        r.status === 'confirmed' &&
        catKeys.has(r.bucketKey) &&
        (r.playerId === playerId || r.partnerId === playerId),
    );
  };
  // Max teams for a class (null = no limit).
  const kmBucketMax = (a, bk) => {
    const b = kmBucketsOf(a).find((x) => x.bucketKey === bk);
    if (b?.maxTeams != null) return Math.max(0, Number(b.maxTeams) || 0);
    const legacy = a?.config?.kmMaxTeamsPerClass;
    return legacy != null ? Math.max(0, Number(legacy) || 0) : null;
  };
  const kmGenderOk = (p1Id, p2Id, bucket) => {
    if (!bucket || !p1Id || !p2Id || p1Id === p2Id) return false;
    const g1 = genderOf(p1Id),
      g2 = genderOf(p2Id);
    if (bucket.catId === 'women') return g1 === 'F' && g2 === 'F';
    if (bucket.catId === 'mix') return (g1 === 'M' && g2 === 'F') || (g1 === 'F' && g2 === 'M');
    return true; // men: all allowed — women can play "up" (raises a warning)
  };
  // Warning: a woman playing in the men's class (allowed, but should be flagged).
  const kmWomanInMen = (p1Id, p2Id, bucket) =>
    bucket?.catId === 'men' && (genderOf(p1Id) === 'F' || genderOf(p2Id) === 'F');
  const kmRuleText = (bucket) =>
    bucket?.catId === 'women'
      ? tr('activities.km.rule.women')
      : bucket?.catId === 'mix'
        ? tr('activities.km.rule.mix')
        : tr('activities.km.rule.men');
  const clubsOf = (a) => (Array.isArray(a?.config?.clubs) ? a.config.clubs : []);
  const clubHalf = (a) => {
    const m = unitMax(a);
    return m != null ? Math.floor(m / 2) : null;
  };
  const unitMax = (a) => {
    let maxPlayers;
    if (a?.config?.maxPlayers != null) maxPlayers = a.config.maxPlayers;
    else {
      const bc = courtsOf(a);
      maxPlayers = bc > 0 ? bc * 4 : a?.config?.courts ? a.config.courts * 4 : null;
    }
    if (maxPlayers == null) return null;
    return isTeamActivity(a) ? Math.floor(maxPlayers / 2) : maxPlayers; // team: unit = pair
  };
  const cap = (a) => {
    const regs = a?.registrations || [];
    if (isKmActivity(a)) {
      const conf = regs.filter((r) => r.status === 'confirmed');
      const bks = kmBucketsOf(a).map((b) => {
        const pairs = conf.filter((r) => r.bucketKey === b.bucketKey).length;
        const maxPer = kmBucketMax(a, b.bucketKey);
        return {
          ...b,
          pairs,
          maxPairs: maxPer,
          priceNok: kmBucketPrice(a, b.bucketKey),
          full: maxPer != null && pairs >= maxPer,
        };
      });
      return {
        confirmed: conf.length * 2,
        waitlist: 0,
        max: null,
        free: null,
        km: true,
        kmBuckets: bks,
        pairs: conf.length,
      };
    }
    if (isClubTeamActivity(a)) {
      const conf = regs.filter((r) => r.status === 'confirmed');
      const wl = regs.filter((r) => r.status === 'waitlist');
      const slotsPerClub = clubTeamSlotsPerClub(a); // team slots per club
      const maxPerClub = slotsPerClub != null ? slotsPerClub * 2 : null; // players per club
      const cl = clubsOf(a);
      const perClub = cl.map((club) => {
        const cc = conf.filter((r) => r.clubId === club.id);
        const players = cc.reduce((s, r) => s + (r.partnerId ? 2 : 1), 0);
        const singles = cc.filter((r) => !r.partnerId).length;
        return {
          id: club.id,
          name: club.name,
          logoData: club.logoData || null,
          confirmed: players,
          max: maxPerClub,
          slots: slotsPerClub,
          pendingSingle: singles % 2,
        };
      });
      const confTot = perClub.reduce((s, c) => s + c.confirmed, 0);
      const wlPlayers = wl.reduce((s, r) => s + (r.partnerId ? 2 : 1), 0);
      const totMax = maxPerClub != null ? maxPerClub * cl.length : null;
      return {
        confirmed: confTot,
        waitlist: wlPlayers,
        max: totMax,
        free: totMax != null ? Math.max(0, totMax - confTot) : null,
        club: true,
        team: true,
        clubTeam: true,
        clubs: perClub,
      };
    }
    if (isTeamActivity(a)) {
      const conf = regs.filter((r) => r.status === 'confirmed');
      const wl = regs.filter((r) => r.status === 'waitlist');
      const confPlayers = conf.reduce((s, r) => s + (r.partnerId ? 2 : 1), 0);
      const wlPlayers = wl.reduce((s, r) => s + (r.partnerId ? 2 : 1), 0);
      const confSingles = conf.filter((r) => !r.partnerId).length;
      let maxP;
      if (a?.config?.maxPlayers != null) maxP = a.config.maxPlayers;
      else {
        const bc = courtsOf(a);
        maxP = bc > 0 ? bc * 4 : a?.config?.courts ? a.config.courts * 4 : null;
      }
      const free = maxP != null ? Math.max(0, maxP - confPlayers) : null;
      return {
        confirmed: confPlayers,
        waitlist: wlPlayers,
        max: maxP,
        free,
        team: true,
        pendingSingle: confSingles % 2,
      };
    }
    if (isClubActivity(a)) {
      const conf = regs.filter((r) => r.status === 'confirmed');
      const wl = regs.filter((r) => r.status === 'waitlist');
      const maxP = unitMax(a);
      const half = maxP != null ? Math.floor(maxP / 2) : null;
      const cl = clubsOf(a);
      const perClub = cl.map((club) => ({
        id: club.id,
        name: club.name,
        logoData: club.logoData || null,
        confirmed: conf.filter((r) => r.clubId === club.id).length,
        max: half,
      }));
      const confTot = perClub.reduce((s, c) => s + c.confirmed, 0);
      const totMax = half != null ? half * cl.length : null;
      return {
        confirmed: confTot,
        waitlist: wl.length,
        max: totMax,
        free: totMax != null ? Math.max(0, totMax - confTot) : null,
        club: true,
        clubs: perClub,
      };
    }
    if (isMixActivity(a)) {
      const conf = regs.filter((r) => r.status === 'confirmed');
      const wl = regs.filter((r) => r.status === 'waitlist');
      const maxP = unitMax(a);
      const half = maxP != null ? Math.floor(maxP / 2) : null;
      const cm = conf.filter((r) => (r.gender || genderOf(r.playerId)) === 'M').length;
      const cf = conf.filter((r) => (r.gender || genderOf(r.playerId)) === 'F').length;
      const totMax = half != null ? half * 2 : null;
      return {
        confirmed: cm + cf,
        waitlist: wl.length,
        max: totMax,
        free: totMax != null ? Math.max(0, totMax - cm - cf) : null,
        mix: true,
        men: { confirmed: cm, max: half },
        women: { confirmed: cf, max: half },
      };
    }
    const confirmed = regs.filter((r) => r.status === 'confirmed').length;
    const waitlist = regs.filter((r) => r.status === 'waitlist').length;
    const max = unitMax(a);
    const free = max != null ? Math.max(0, max - confirmed) : null;
    return { confirmed, waitlist, max, free, team: false };
  };
  const fmtWhen = (a) => {
    const mine = (bookings || []).filter((b) => b.seriesId === a.id);
    if (mine.length > 0) {
      const dates = [...new Set(mine.map((b) => b.date))].sort();
      const from = mine.map((b) => b.startTime).sort()[0];
      const til = mine
        .map((b) => b.endTime)
        .sort()
        .slice(-1)[0];
      if (dates.length > 1)
        return tr('activities.when.days', { count: String(dates.length), from });
      return [dates[0], from && til ? `${from}–${til}` : from].filter(Boolean).join(' · ');
    }
    let d = a?.config?.activityDate,
      t = a?.config?.activityTime,
      e = a?.config?.activityEndTime;
    // Older Padel24 activities without activityDate: derive from the setup's days.
    if (!d && !t && a?.config?.p24Snapshot?.days) {
      const _pd = (a.config.p24Snapshot.days || [])
        .filter((x) => x && x.date)
        .sort((x, y) => x.date.localeCompare(y.date));
      if (_pd[0]) {
        d = _pd[0].date;
        t = _pd[0].startTime || null;
        e = _pd[0].endTime || null;
      }
    }
    if (!d && !t) return tr('activities.when.notSet');
    const time = t ? (e ? `${t}–${e}` : t) : '';
    return [d, time].filter(Boolean).join(' · ');
  };
  const playerName = (pid) =>
    effectivePlayerDB.find((p) => p.id === pid)?.name || tr('activities.unknownPlayer');
  const playerElo = (pid) => {
    const p = effectivePlayerDB.find((x) => x.id === pid);
    return p?.elo != null ? Math.round(p.elo) : null;
  };

  // Normalize sign-ups: oldest sign-up first, the first "max" are confirmed,
  // the rest waitlisted. Handles promotion on withdrawal + overflow when full.
  const normalizeRegs = (regs, max) => {
    const sorted = [...(regs || [])].sort((a, b) => (a.signedUpAt || 0) - (b.signedUpAt || 0));
    return sorted.map((r, i) => ({
      ...r,
      status: max == null || i < max ? 'confirmed' : 'waitlist',
    }));
  };
  // TEAM: pairs are prioritized for the team slots; singles fill the rest (2 per slot).
  // A leftover single (odd one out among the confirmed) is "waiting for a partner" until
  // another player signs up or a slot frees up.
  // TEAM normalization (FIFO, in sign-up order):
  // - A pair takes one team slot.
  // - Two singles share one slot; an odd single "half-occupies" a slot and waits for a partner.
  // - Pairs are prioritized over singles ONLY for the last slot: a pair can push the odd
  //   single to reserve. A pair can NEVER push out singles that are already in complete
  //   pairs (even count) — only the loose odd single.
  const normalizeTeamRegs = (regs, maxSlots) => {
    const sorted = [...(regs || [])].sort((a, b) => (a.signedUpAt || 0) - (b.signedUpAt || 0));
    const st = new Map();
    const cap = maxSlots == null ? Infinity : maxSlots;
    let slotsUsed = 0;
    let halfSingle = null; // reg for the odd single that half-occupies a slot
    for (const r of sorted) {
      if (r.partnerId) {
        if (slotsUsed < cap) {
          st.set(r, 'confirmed');
          slotsUsed++;
        } else if (halfSingle) {
          st.set(r, 'confirmed');
          st.set(halfSingle, 'waitlist');
          halfSingle = null;
        } // push the odd single
        else st.set(r, 'waitlist');
      } else {
        if (halfSingle) {
          st.set(r, 'confirmed');
          halfSingle = null;
        } // completes the slot with the waiting single
        else if (slotsUsed < cap) {
          st.set(r, 'confirmed');
          slotsUsed++;
          halfSingle = r;
        } else st.set(r, 'waitlist');
      }
    }
    return sorted.map((r) => ({ ...r, status: st.get(r) }));
  };
  // MIX: confirm per gender — men fill the men's slots, women the women's slots
  // (oldest sign-up first within each gender). Full gender → waitlist.
  const normalizeMixRegs = (regs, menMax, womenMax) => {
    const sorted = [...(regs || [])].sort((a, b) => (a.signedUpAt || 0) - (b.signedUpAt || 0));
    let m = 0,
      w = 0;
    return sorted.map((r) => {
      const g = r.gender || genderOf(r.playerId);
      const max = g === 'F' ? womenMax : menMax;
      const cnt = g === 'F' ? w : m;
      const withinCap = max == null || cnt < max;
      if (withinCap) {
        if (g === 'F') w++;
        else m++;
        return { ...r, status: 'confirmed', overQuota: false };
      }
      if (r.adminForced) return { ...r, status: 'confirmed', overQuota: true }; // confirmed as extra (over the quota now)
      return { ...r, status: 'waitlist', overQuota: false };
    });
  };
  // CLUB: confirm per club — each club fills its slots (oldest first). Full club → waitlist.
  const normalizeClubRegs = (regs, perClubMax) => {
    const sorted = [...(regs || [])].sort((a, b) => (a.signedUpAt || 0) - (b.signedUpAt || 0));
    const counts = {};
    return sorted.map((r) => {
      const cid = r.clubId || '?';
      counts[cid] = counts[cid] || 0;
      const withinCap = perClubMax == null || counts[cid] < perClubMax;
      if (withinCap) {
        counts[cid]++;
        return { ...r, status: 'confirmed', overQuota: false };
      }
      if (r.adminForced) return { ...r, status: 'confirmed', overQuota: true };
      return { ...r, status: 'waitlist', overQuota: false };
    });
  };
  // CLUB TEAM: run team normalization PER CLUB (pairs + singles), singles are paired only within the club.
  const normalizeClubTeamRegs = (regs, slotsPerClub, cl) => {
    const out = [];
    for (const club of cl) {
      const clubRegs = (regs || []).filter((r) => r.clubId === club.id);
      out.push(...normalizeTeamRegs(clubRegs, slotsPerClub));
    }
    // regs without a valid club (should not happen) → waitlist
    const known = new Set(cl.map((c) => c.id));
    for (const r of regs || []) if (!known.has(r.clubId)) out.push({ ...r, status: 'waitlist' });
    return out;
  };
  // KM: no capacity limit — all pairs are confirmed (min. pairs per class is checked at start).
  const normalizeKmRegs = (regs) => (regs || []).map((r) => ({ ...r, status: 'confirmed' }));
  const normalizeFor = (a, regs) => {
    // Standby-ONLY reserves (padel24) are kept entirely out of the waitlist/confirmed pool.
    const standbyOnlies = (regs || [])
      .filter((r) => r.standbyOnly)
      .map((r) => ({ ...r, status: 'standby' }));
    const pool = (regs || []).filter((r) => !r.standbyOnly);
    const normalized = isKmActivity(a)
      ? normalizeKmRegs(pool)
      : isClubTeamActivity(a)
        ? normalizeClubTeamRegs(pool, clubTeamSlotsPerClub(a), clubsOf(a))
        : isTeamActivity(a)
          ? normalizeTeamRegs(pool, unitMax(a))
          : isMixActivity(a)
            ? normalizeMixRegs(pool, mixHalf(a), mixHalf(a))
            : isClubActivity(a)
              ? normalizeClubRegs(pool, clubHalf(a))
              : normalizeRegs(pool, unitMax(a));
    return [...normalized, ...standbyOnlies];
  };

  const persist = async (next) => {
    await onPersist?.(next);
  };
  const mutate = (id, fn) => {
    const next = (activities || []).map((a) => (a.id === id ? fn(a) : a));
    persist(next);
  };

  const selected = (activities || []).find((a) => a.id === selectedId) || null;
  // ──── Detail page for a single activity ──────────────────────────────
  if (selected) {
    const c = cap(selected);
    const type = typeOf(selected);
    // A single enkeltmatch run as an activity (4 players, one best-of-3). When
    // the booker paid the whole court, each signup is covered (no charge) and a
    // settle-between-players note is shown.
    const isSingleMatch = selected.config?.tournamentType === 'single_match';
    const matchBookerPaid = isSingleMatch && selected.config?.bookerPaidAll === true;
    const regs = selected.registrations || [];
    const confirmed = regs.filter((r) => r.status === 'confirmed');
    const waitlist = regs.filter((r) => r.status === 'waitlist');
    const registeredIds = new Set(regs.flatMap((r) => [r.playerId, r.partnerId].filter(Boolean)));
    const teamMode_ = selected.config?.teamSignupMode || 'both';
    // Level requirement (1-9). Null = open level. A player's level is derived from ELO via getEloLevel.
    const lvMin = selected.config?.levelMin ?? null;
    const lvMax = selected.config?.levelMax ?? null;
    const hasLevelReq = lvMin != null || lvMax != null;
    const lvName = (n) => ELO_LEVELS.find((l) => l.level === n)?.name || '';
    const levelOk = (elo) => {
      if (!hasLevelReq) return true;
      if (elo == null) return false; // unknown level when there is a requirement
      const lv = getEloLevel(elo).level;
      if (lvMin != null && lv < lvMin) return false;
      if (lvMax != null && lv > lvMax) return false;
      return true;
    };
    const levelLabel = hasLevelReq
      ? lvMin != null && lvMin === lvMax
        ? tr('activities.level.exact', { level: String(lvMin), name: lvName(lvMin) })
        : tr('activities.level.range', {
            min: String(lvMin != null ? lvMin : 1),
            max: String(lvMax != null ? lvMax : 9),
          })
      : null;
    // Manual add: all matches, sorted by ELO (highest first), with level markings.
    const candidates = effectivePlayerDB
      .filter((p) => !registeredIds.has(p.id))
      .filter((p) => p.id !== pendingTeamP1)
      .filter(
        (p) => !pickerSearch || (p.name || '').toLowerCase().includes(pickerSearch.toLowerCase()),
      )
      .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1))
      .slice(0, 12);
    // Base selection without search/limit — used for column search (mix/club).
    const candidatesBase = effectivePlayerDB
      .filter((p) => !registeredIds.has(p.id))
      .filter((p) => p.id !== pendingTeamP1)
      .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1));
    const filtCands = (pool, term) =>
      pool
        .filter((p) => !term || (p.name || '').toLowerCase().includes(term.toLowerCase()))
        .slice(0, 12);
    // Self sign-up: ONLY players within the level.
    // deviation (lint): `signupCandidates` is computed in the monolith but unused in
    // this range; `_`-prefixed to satisfy no-unused-vars. Logic byte-identical.
    const _signupCandidates = effectivePlayerDB
      .filter((p) => !registeredIds.has(p.id))
      .filter((p) => levelOk(p.elo != null ? Math.round(p.elo) : null))
      .filter(
        (p) => !signupSearch || (p.name || '').toLowerCase().includes(signupSearch.toLowerCase()),
      )
      .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1))
      .slice(0, 8);

    // DEMO: which profile we pretend is "logged in". Falls back to favorite/first player.
    const demoUser =
      effectivePlayerDB.find((p) => p.id === demoUserId) ||
      effectivePlayerDB.find((p) => p.isFavorite) ||
      effectivePlayerDB[0] ||
      null;
    const demoElo = demoUser?.elo != null ? Math.round(demoUser.elo) : null;
    const demoLvl = demoElo != null ? getEloLevel(demoElo) : null;
    const demoReg = demoUser
      ? regs.find((r) => r.playerId === demoUser.id || r.partnerId === demoUser.id)
      : null;
    const isPadel24 = selected?.config?.tournamentType === 'padel24';
    const demoLevelOk = demoUser ? levelOk(demoElo) : false;
    const demoRelation =
      !demoLevelOk && hasLevelReq && demoLvl
        ? demoLvl.level < (lvMin != null ? lvMin : 1)
          ? 'under'
          : 'over'
        : null;
    // MIX: is the logged-in user's gender slot full? (then → waitlist, even if the total isn't full)
    const demoGender = isMixActivity(selected) && demoUser ? genderOf(demoUser.id) : null;
    const mixSlotFull =
      c.mix && demoGender
        ? demoGender === 'F'
          ? c.women.confirmed >= c.women.max
          : c.men.confirmed >= c.men.max
        : false;
    const indWillWait = c.mix ? mixSlotFull : c.max != null && c.confirmed >= c.max;
    // Waitlist cap: when the activity is full and either reserves are off, or max reserves is reached.
    const reservesAllowed = selected.config?.allowReserves !== false;
    const maxRes = selected.config?.maxReserves;
    const activityFull = c.max != null && c.confirmed >= c.max;
    const waitlistFull =
      activityFull && (!reservesAllowed || (maxRes != null && c.waitlist >= maxRes));
    const profileResults = effectivePlayerDB
      .filter(
        (p) => !profileSearch || (p.name || '').toLowerCase().includes(profileSearch.toLowerCase()),
      )
      .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1))
      .slice(0, 8);

    const addPlayer = (pid, payment = null, clubId = null, forced = false, standby = null) => {
      mutate(selected.id, (a) => {
        // deviation: type-only (monolith is @ts-nocheck) — annotate as any so the
        // conditional fields below (gender/clubId/adminForced/payment/standby*) resolve.
        // When the booker paid the whole court, every signup is already covered
        // — mark it paid (no charge) and record a booker-cover marker so the UI
        // can show "gjør opp med <booker>".
        const bookerCovers = a.config?.tournamentType === 'single_match' && a.config?.bookerPaidAll;
        const reg: any = {
          playerId: pid,
          status: 'confirmed',
          paidStatus: payment || bookerCovers ? 'paid' : 'free',
          signedUpAt: Date.now(),
        };
        if (isMixActivity(a)) reg.gender = genderOf(pid);
        if (isClubActivity(a) && clubId) reg.clubId = clubId;
        if (forced) reg.adminForced = true; // admin overrides quota (mix/club)
        if (payment) reg.payment = payment;
        else if (bookerCovers) reg.payment = { method: 'booker_cover' };
        if (standby && standby.on) {
          reg.standbyReserve = true;
          reg.standbyAt = Date.now();
          reg.standbyDays = standby.days || null;
        }
        // When you sign up for the tournament, any standby-ONLY registration is removed.
        const base = (a.registrations || []).filter((r) => !(r.playerId === pid && r.standbyOnly));
        const next = [...base, reg];
        return { ...a, registrations: normalizeFor(a, next) };
      });
      setPickerSearch('');
    };
    // PADEL24: sign up as standby reserve ONLY (free, not waitlist, no slot).
    const addStandbyOnly = (pid, days = null) => {
      mutate(selected.id, (a) => {
        const reg = {
          playerId: pid,
          status: 'standby',
          standbyReserve: true,
          standbyOnly: true,
          standbyAt: Date.now(),
          standbyDays: days || null,
          paidStatus: 'free',
          signedUpAt: Date.now(),
        };
        const next = [...(a.registrations || []), reg];
        return { ...a, registrations: normalizeFor(a, next) };
      });
      setPickerSearch('');
    };
    const addTeam = (p1, p2, payment = null, clubId = null, bucketKey = null) => {
      if (!p1 || !p2 || p1 === p2) return;
      // KM: enforce max teams per class + one class per category per player.
      if (isKmActivity(selected) && bucketKey) {
        const cat = kmCatOfBucket(selected, bucketKey);
        if (
          cat &&
          (kmPlayerInCat(selected, p1, cat, bucketKey) ||
            kmPlayerInCat(selected, p2, cat, bucketKey))
        )
          return; // already in a class in this category
        const maxPer = kmBucketMax(selected, bucketKey);
        if (maxPer != null) {
          const current = (selected.registrations || []).filter(
            (r) => r.status === 'confirmed' && r.bucketKey === bucketKey,
          ).length;
          if (current >= maxPer) return; // the class is full
        }
      }
      mutate(selected.id, (a) => {
        // deviation: type-only (monolith is @ts-nocheck) — annotate as any so the
        // conditional fields below (clubId/bucketKey/payment) resolve.
        const reg: any = {
          playerId: p1,
          partnerId: p2,
          isTeam: true,
          status: 'confirmed',
          paidStatus: payment ? 'paid' : 'free',
          signedUpAt: Date.now(),
          playerEloPre: playerElo(p1),
          partnerEloPre: playerElo(p2),
        };
        if (isClubTeamActivity(a) && clubId) reg.clubId = clubId;
        if (isKmActivity(a) && bucketKey) reg.bucketKey = bucketKey;
        if (payment) reg.payment = payment;
        const next = [...(a.registrations || []), reg];
        return { ...a, registrations: normalizeFor(a, next) };
      });
      setPickerSearch('');
      setPendingTeamP1(null);
    };
    // TEAM (single mode): sign up ONE player — paired at start.
    const addSingle = (pid, payment = null, clubId = null) => {
      mutate(selected.id, (a) => {
        // deviation: type-only (monolith is @ts-nocheck) — annotate as any so the
        // conditional fields below (clubId/payment) resolve.
        const reg: any = {
          playerId: pid,
          isSingle: true,
          status: 'confirmed',
          paidStatus: payment ? 'paid' : 'free',
          signedUpAt: Date.now(),
          playerEloPre: playerElo(pid),
        };
        if (isClubTeamActivity(a) && clubId) reg.clubId = clubId;
        if (payment) reg.payment = payment;
        const next = [...(a.registrations || []), reg];
        return { ...a, registrations: normalizeFor(a, next) };
      });
      setPickerSearch('');
    };
    const removePlayer = (pid) => {
      mutate(selected.id, (a) => {
        // Remove the reg where the player is either the registrant OR the partner (the whole pair goes).
        const next = (a.registrations || []).filter(
          (r) => r.playerId !== pid && r.partnerId !== pid,
        );
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    // TEAM: swap the partner in an existing pair. keepId is kept, the other is replaced.
    const swapPartner = (reg, keepId, newOtherId) => {
      if (!newOtherId || newOtherId === keepId) return;
      mutate(selected.id, (a) => {
        const next = (a.registrations || []).map((r) => {
          if (r.playerId !== reg.playerId || r.partnerId !== reg.partnerId) return r;
          return {
            ...r,
            playerId: keepId,
            partnerId: newOtherId,
            isTeam: true,
            playerEloPre: playerElo(keepId),
            partnerEloPre: playerElo(newOtherId),
          };
        });
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    // Demo: add N random players from the database that meet the level requirement (in one batch).
    const addManyPlayers = (pids) => {
      if (!pids.length) return;
      mutate(selected.id, (a) => {
        const now = Date.now();
        const cl = isClubActivity(a) ? clubsOf(a) : [];
        const additions = pids.map((pid, i) => ({
          playerId: pid,
          status: 'confirmed',
          paidStatus: 'free',
          signedUpAt: now + i,
          ...(isMixActivity(a) ? { gender: genderOf(pid) } : {}),
          ...(cl.length === 2 ? { clubId: cl[i % 2].id } : {}),
        }));
        const next = [...(a.registrations || []), ...additions];
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    // TEAM batch: add several complete pairs
    const addManyTeams = (pairs) => {
      if (!pairs.length) return;
      mutate(selected.id, (a) => {
        const now = Date.now();
        const cid = isClubTeamActivity(a) ? clubAddFor : null;
        const additions = pairs.map(([p1, p2], i) => ({
          playerId: p1,
          partnerId: p2,
          isTeam: true,
          status: 'confirmed',
          paidStatus: 'free',
          signedUpAt: now + i,
          playerEloPre: playerElo(p1),
          partnerEloPre: playerElo(p2),
          ...(cid ? { clubId: cid } : {}),
        }));
        const next = [...(a.registrations || []), ...additions];
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    // TEAM batch (single mode): add several individual singles
    const addManySingles = (pids) => {
      if (!pids.length) return;
      mutate(selected.id, (a) => {
        const now = Date.now();
        const cid = isClubTeamActivity(a) ? clubAddFor : null;
        const additions = pids.map((pid, i) => ({
          playerId: pid,
          isSingle: true,
          status: 'confirmed',
          paidStatus: 'free',
          signedUpAt: now + i,
          playerEloPre: playerElo(pid),
          ...(cid ? { clubId: cid } : {}),
        }));
        const next = [...(a.registrations || []), ...additions];
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    // KM batch: pairs with a class (bucketKey)
    const addManyKmTeams = (triples) => {
      if (!triples.length) return;
      mutate(selected.id, (a) => {
        const now = Date.now();
        const additions = triples.map(([p1, p2, bk], i) => ({
          playerId: p1,
          partnerId: p2,
          isTeam: true,
          status: 'confirmed',
          paidStatus: 'free',
          signedUpAt: now + i,
          playerEloPre: playerElo(p1),
          partnerEloPre: playerElo(p2),
          bucketKey: bk,
        }));
        const next = [...(a.registrations || []), ...additions];
        return { ...a, registrations: normalizeFor(a, next) };
      });
    };
    const demoFill = (n) => {
      const pool = effectivePlayerDB
        .filter((p) => !registeredIds.has(p.id))
        .filter((p) => levelOk(p.elo != null ? Math.round(p.elo) : null));
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      if (isKmActivity(selected)) {
        const buckets = kmBucketsOf(selected);
        if (!buckets.length) return;
        const countIn = {};
        buckets.forEach((b) => {
          countIn[b.bucketKey] = (selected.registrations || []).filter(
            (r) => r.status === 'confirmed' && r.bucketKey === b.bucketKey,
          ).length;
        });
        const males = shuffled.filter((p) => genderOf(p.id) === 'M').map((p) => p.id);
        const females = shuffled.filter((p) => genderOf(p.id) === 'F').map((p) => p.id);
        const triples = [];
        const nPairs = Math.floor(n / 2);
        for (let k = 0; k < nPairs; k++) {
          const bucket = buckets[k % buckets.length];
          const maxPer = kmBucketMax(selected, bucket.bucketKey);
          if (maxPer != null && countIn[bucket.bucketKey] >= maxPer) continue; // the class is full
          let p1 = null,
            p2 = null;
          if (bucket.catId === 'women') {
            p1 = females.shift();
            p2 = females.shift();
          } else if (bucket.catId === 'mix') {
            p1 = males.shift();
            p2 = females.shift();
          } else {
            p1 = males.shift() || females.shift();
            p2 = males.shift() || females.shift();
          } // men: preferably men, otherwise women
          if (p1 && p2) {
            triples.push([p1, p2, bucket.bucketKey]);
            countIn[bucket.bucketKey]++;
          }
        }
        addManyKmTeams(triples);
        return;
      }
      if (isTeamActivity(selected)) {
        const tMode = selected.config?.teamSignupMode || 'both';
        if (tMode === 'singles') {
          addManySingles(shuffled.slice(0, n).map((p) => p.id));
        } else {
          // n players → n/2 random pairs
          const take = shuffled.slice(0, n - (n % 2));
          const pairs = [];
          for (let i = 0; i + 1 < take.length; i += 2) pairs.push([take[i].id, take[i + 1].id]);
          addManyTeams(pairs);
        }
      } else {
        addManyPlayers(shuffled.slice(0, n).map((p) => p.id));
      }
    };
    const demoPoolCount = effectivePlayerDB
      .filter((p) => !registeredIds.has(p.id))
      .filter((p) => levelOk(p.elo != null ? Math.round(p.elo) : null)).length;
    // Shared row rendering for confirmed/waitlist (a pair shows both names; a single shows one).
    const RegRow = (r, label, waiting) => {
      const team = !!r.partnerId;
      const e = playerElo(r.playerId);
      const lvl = e != null ? getEloLevel(e) : null;
      const pe = team ? playerElo(r.partnerId) : null;
      const plvl = pe != null ? getEloLevel(pe) : null;
      return (
        <div
          key={r.playerId}
          className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-bg-soft"
        >
          <span
            className={`text-sm truncate flex items-center gap-1.5 min-w-0 ${waiting ? 'text-ink-2' : 'text-ink-1'}`}
          >
            <span className="font-mono text-[11px] text-ink-3">{label}</span>
            {lvl && (
              <span
                className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
                style={{ background: lvl.color, color: lvl.text }}
                title={tr('activities.levelBadge', { level: String(lvl.level), name: lvl.name })}
              >
                {lvl.level}
              </span>
            )}
            {adminMode && team ? (
              <button
                onClick={() => {
                  setAdminSwapReg({ reg: r, keepId: r.partnerId, replaceId: r.playerId });
                  setSelfPartnerSearch('');
                }}
                className="truncate underline decoration-dotted underline-offset-2 hover:text-lime-deep"
                title={tr('activities.swapThisPlayer')}
              >
                {playerName(r.playerId)}
              </button>
            ) : (
              <span className="truncate">{playerName(r.playerId)}</span>
            )}
            {team ? (
              <>
                <span className="text-ink-3 flex-shrink-0">&</span>
                {plvl && (
                  <span
                    className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
                    style={{ background: plvl.color, color: plvl.text }}
                    title={tr('activities.levelBadge', {
                      level: String(plvl.level),
                      name: plvl.name,
                    })}
                  >
                    {plvl.level}
                  </span>
                )}
                {adminMode ? (
                  <button
                    onClick={() => {
                      setAdminSwapReg({ reg: r, keepId: r.playerId, replaceId: r.partnerId });
                      setSelfPartnerSearch('');
                    }}
                    className="truncate underline decoration-dotted underline-offset-2 hover:text-lime-deep"
                    title={tr('activities.swapThisPlayer')}
                  >
                    {playerName(r.partnerId)}
                  </button>
                ) : (
                  <span className="truncate">{playerName(r.partnerId)}</span>
                )}
              </>
            ) : (
              <>
                {isMixActivity(selected) &&
                  (() => {
                    const g = r.gender || genderOf(r.playerId);
                    return (
                      <span
                        className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{
                          background: g === 'F' ? '#fce7f3' : '#dbeafe',
                          color: g === 'F' ? '#be185d' : '#1d4ed8',
                        }}
                      >
                        {tr(g === 'F' ? 'activities.woman' : 'activities.man')}
                      </span>
                    );
                  })()}
                {isClubActivity(selected) &&
                  r.clubId &&
                  (() => {
                    const cl = clubsOf(selected).find((x) => x.id === r.clubId);
                    return cl ? (
                      <span
                        className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{
                          background: r.clubId === 'clubA' ? '#ecfccb' : '#e0e7ff',
                          color: r.clubId === 'clubA' ? '#3f6212' : '#3730a3',
                        }}
                      >
                        {cl.name}
                      </span>
                    ) : null;
                  })()}
                {e != null && (
                  <span className="font-mono text-[11px] text-ink-3 flex-shrink-0">{e} ELO</span>
                )}
              </>
            )}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!waiting && r.overQuota && (
              <span
                className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: '#fef3c7', color: '#b45309' }}
                title={tr('activities.overQuotaTitle')}
              >
                {tr('activities.extra')}
              </span>
            )}
            {!waiting && r.paidStatus && (
              <span
                className="font-mono text-[9px] px-1.5 py-0.5 rounded"
                style={{
                  background:
                    r.paidStatus === 'paid'
                      ? 'var(--lime-bg)'
                      : r.paidStatus === 'refunded'
                        ? '#fee2e2'
                        : '#f3f4f6',
                  color:
                    r.paidStatus === 'paid'
                      ? 'var(--lime-deep)'
                      : r.paidStatus === 'refunded'
                        ? '#b91c1c'
                        : '#6b7280',
                }}
              >
                {r.paidStatus === 'paid'
                  ? r.payment?.method === 'booker_cover'
                    ? tr('activities.coveredByBooker', {
                        booker:
                          selected.config?.bookerName?.trim() || tr('activities.bookerFallback'),
                      })
                    : tr('activities.paid')
                  : r.paidStatus === 'refunded'
                    ? tr('activities.refunded')
                    : r.paidStatus === 'free'
                      ? tr('activities.manual')
                      : tr('activities.pendingPayment')}
              </span>
            )}
            {adminMode && (
              <button
                onClick={() => removePlayer(r.playerId)}
                className="text-rose hover:bg-rose-soft/30 rounded p-1"
                title={tr('activities.remove')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      );
    };
    // Confirmed list: large logo centered ABOVE the club name + counter below (no overlap).
    const clubColHeader = (cl) => (
      <div
        className="flex flex-col items-center text-center mb-3 pb-2.5"
        style={{ borderBottom: `2px solid ${cl.id === 'clubA' ? '#65a30d' : '#4f46e5'}` }}
      >
        {cl.logoData ? (
          <img src={cl.logoData} alt="" className="w-16 h-16 rounded-xl object-contain" />
        ) : (
          <span
            className="w-16 h-16 rounded-xl flex items-center justify-center text-lg font-bold text-white"
            style={{ background: cl.id === 'clubA' ? '#65a30d' : '#4f46e5' }}
          >
            {(cl.name || '').slice(0, 2).toUpperCase()}
          </span>
        )}
        <span
          className="font-display text-sm font-semibold mt-2 truncate max-w-full"
          style={{ color: cl.id === 'clubA' ? '#3f6212' : '#3730a3' }}
        >
          {cl.name}
        </span>
        <span className="font-mono text-[11px] text-ink-3 mt-0.5 whitespace-nowrap">
          {tr('activities.clubPlayersCount', {
            confirmed: String(cl.confirmed),
            max: String(cl.max),
          })}
        </span>
      </div>
    );
    // "Legg til": name + counter only (no logo).
    const clubColHeaderSlim = (cl) => (
      <div
        className="flex items-center gap-2 mb-2 pb-1.5"
        style={{ borderBottom: `2px solid ${cl.id === 'clubA' ? '#65a30d' : '#4f46e5'}` }}
      >
        <span
          className="font-display text-sm font-semibold truncate min-w-0"
          style={{ color: cl.id === 'clubA' ? '#3f6212' : '#3730a3' }}
        >
          {cl.name}
        </span>
        <span className="font-mono text-[11px] text-ink-3 ml-auto flex-shrink-0 whitespace-nowrap">
          {cl.confirmed}/{cl.max}
        </span>
      </div>
    );
    // Shared partner picker (search + list). onPick(id) decides what happens.
    const renderPartnerPicker = (excludeId, onPick, extraFilter = null, lockGender = null) => {
      const cands = effectivePlayerDB
        .filter((p) => !registeredIds.has(p.id) && p.id !== excludeId)
        .filter((p) => levelOk(p.elo != null ? Math.round(p.elo) : null))
        .filter((p) => !extraFilter || extraFilter(p))
        .filter(
          (p) =>
            !selfPartnerSearch ||
            (p.name || '').toLowerCase().includes(selfPartnerSearch.toLowerCase()),
        )
        .sort((a, b) => (b.elo ?? -1) - (a.elo ?? -1))
        .slice(0, 8);
      return (
        <>
          <input
            ref={focusPartnerSearch}
            type="text"
            className="input w-full text-sm mb-2"
            placeholder={tr('activities.searchName')}
            aria-label={tr('activities.searchName')}
            value={selfPartnerSearch}
            onChange={(e) => setSelfPartnerSearch(e.target.value)}
          />
          {cands.length === 0 ? (
            <p className="text-[12px] text-ink-3">
              {tr(selfPartnerSearch ? 'activities.noMatches' : 'activities.noAvailablePlayers')}
            </p>
          ) : (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {cands.map((p) => {
                const lvl = p.elo != null ? getEloLevel(Math.round(p.elo)) : null;
                return (
                  <button
                    key={p.id}
                    onClick={() => onPick(p.id)}
                    className="w-full flex items-center gap-2 py-2 px-2 rounded hover:bg-lime-bg/30 text-left"
                  >
                    {lvl && (
                      <span
                        className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
                        style={{ background: lvl.color, color: lvl.text }}
                      >
                        {lvl.level}
                      </span>
                    )}
                    <span className="text-sm text-ink-1 truncate">{p.name}</span>
                    {p.elo != null && (
                      <span className="font-mono text-[11px] text-ink-3 flex-shrink-0">
                        {Math.round(p.elo)} ELO
                      </span>
                    )}
                    <Plus
                      size={14}
                      className="ml-auto flex-shrink-0"
                      style={{ color: 'var(--lime-text)' }}
                    />
                  </button>
                );
              })}
            </div>
          )}
          {onPersistNewPlayer && selfPartnerSearch.trim().length > 1 && (
            <button
              type="button"
              onClick={() =>
                setNpModalCtx({ onPick, lockGender, initialName: selfPartnerSearch.trim() })
              }
              className="mt-2 w-full text-xs font-semibold py-2 rounded-lg border border-dashed border-line text-ink-2 hover:border-lime-deep hover:text-lime-deep flex items-center justify-center gap-1"
            >
              <Plus size={13} /> {tr('activities.createPlayer', { name: selfPartnerSearch.trim() })}
            </button>
          )}
        </>
      );
    };
    const saveEdit = async () => {
      const a = selected;
      const cfg = { ...a.config };
      cfg.name = editDraft.name || a.config?.name;
      cfg.maxPlayers =
        editDraft.maxPlayers !== '' ? Math.max(1, Number(editDraft.maxPlayers) || 0) : null;
      cfg.pricePerPlayer =
        editDraft.pricePerPlayer !== '' ? Math.max(0, Number(editDraft.pricePerPlayer) || 0) : 0;
      cfg.activityDate = editDraft.activityDate || null;
      cfg.activityTime = editDraft.activityTime || null;
      cfg.activityEndTime = editDraft.activityEndTime || null;
      cfg.deregisterCutoffHours =
        editDraft.deregisterCutoffHours !== ''
          ? Math.max(0, Number(editDraft.deregisterCutoffHours) || 0)
          : 24;
      cfg.signupCloseHours =
        editDraft.signupCloseHours !== ''
          ? Math.max(0, Number(editDraft.signupCloseHours) || 0)
          : 24;
      // Keep the booking draft's time window in sync with the edited time, so that a (re)reservation
      // that reads slotRooms actually moves to the new time.
      let bd = a.bookingDraft;
      if (bd && Array.isArray(bd.slotRooms) && bd.slotRooms.length) {
        const r0 = { ...bd.slotRooms[0] };
        if (cfg.activityDate) r0.singleDate = cfg.activityDate;
        if (cfg.activityTime) r0.startTime = cfg.activityTime;
        if (cfg.activityEndTime) r0.endTime = cfg.activityEndTime;
        bd = { ...bd, slotRooms: [r0, ...bd.slotRooms.slice(1)] };
      }
      const updated = {
        ...a,
        config: cfg,
        bookingDraft: bd,
        registrations: normalizeRegs(a.registrations, cfg.maxPlayers),
      };
      mutate(selected.id, () => updated);
      setEditing(false);
      // If the time/date changed AND the activity has reserved courts: move them.
      const timeChanged =
        a.config?.activityDate !== cfg.activityDate ||
        a.config?.activityTime !== cfg.activityTime ||
        a.config?.activityEndTime !== cfg.activityEndTime;
      const hadBookings = (bookings || []).some((b) => b.seriesId === selected.id);
      if (
        timeChanged &&
        hadBookings &&
        onReserveCourts &&
        cfg.activityDate &&
        cfg.activityTime &&
        cfg.activityEndTime
      ) {
        await onReserveCourts(updated);
      }
    };
    const startActivity = async () => {
      // Single-match activity: don't build a tournament — capture one best-of-3
      // result (needs the 4 confirmed players) and apply ELO via the single-match
      // pipeline.
      if (isSingleMatch) {
        if (c.confirmed < 4) return;
        setSingleMatchResultOpen(true);
        return;
      }
      const custom = isCustomType(selected.config?.tournamentType || '');
      if (custom) {
        // Simple session: start regardless of the number of sign-ups. No tournament/matches
        // are created — the session is just marked as "in progress" and lives in Activities.
        setBusy(true);
        await persist(
          (activities || []).map((a) =>
            a.id === selected.id ? { ...a, sessionStarted: true, startedAt: Date.now() } : a,
          ),
        );
        setBusy(false);
        return;
      }
      const enough = isKmActivity(selected)
        ? (c.kmBuckets || []).some((b) => b.pairs >= 2)
        : c.confirmed >= 4;
      if (!enough || !onStartActivity) return;
      setBusy(true);
      await onStartActivity(selected);
      setBusy(false);
    };
    // End a custom session → flagged as completed and moves under "Tidligere".
    const finishCustomSession = async () => {
      setBusy(true);
      await persist(
        (activities || []).map((a) =>
          a.id === selected.id ? { ...a, sessionStarted: false, completedAt: Date.now() } : a,
        ),
      );
      setBusy(false);
    };
    const deleteActivity = async () => {
      if (delFuture) await onReleaseFutureCourts?.(selected); // only date ≥ today — historical ones are kept
      persist((activities || []).filter((a) => a.id !== selected.id));
      setSelectedId(null);
      setConfirmDel(false);
      setEditing(false);
    };
    // SIGN-UP: closes manually (signupClosed) or automatically N hours before
    // the reserved start time (earliest booked slot). signupCloseHours default 24.
    const myBookings = (bookings || []).filter((b) => b.seriesId === selected.id);
    const startMs = (() => {
      const stamps = myBookings
        .map((b) => new Date(`${b.date}T${b.startTime || '00:00'}:00`).getTime())
        .filter((n) => !isNaN(n));
      if (stamps.length) return Math.min(...stamps);
      // Standalone: use date/time from the time window in the draft.
      if (selected.bookingDraft?.slotMode === 'standalone') {
        const r = (selected.bookingDraft.slotRooms || [])[0];
        if (r?.singleDate) {
          const ms = new Date(`${r.singleDate}T${r.startTime || '00:00'}:00`).getTime();
          if (!isNaN(ms)) return ms;
        }
      }
      return null;
    })();
    const signupCloseHours =
      selected.config?.signupCloseHours != null
        ? Math.max(0, Number(selected.config.signupCloseHours) || 0)
        : 24;
    const autoCloseMs = startMs != null ? startMs - signupCloseHours * 3600000 : null;
    // KM: separate sign-up deadline (date). Sign-up is allowed through the entire deadline day.
    const kmDeadlineMs =
      isKmActivity(selected) && selected.config?.kmSignupDeadline
        ? (() => {
            const [y, m, d] = String(selected.config.kmSignupDeadline).split('-').map(Number);
            if (!y || !m || !d) return null;
            return new Date(y, m - 1, d + 1, 0, 0, 0).getTime();
          })()
        : null;
    const kmDeadlinePassed = kmDeadlineMs != null && Date.now() >= kmDeadlineMs;
    const kmDeadlineLabel =
      isKmActivity(selected) && selected.config?.kmSignupDeadline
        ? (() => {
            const [y, m, d] = String(selected.config.kmSignupDeadline).split('-').map(Number);
            const wd = wdShort[new Date(y, m - 1, d).getDay()];
            const mo = moShort[m - 1];
            return tr('activities.date.dayMonth', { wd, day: String(d), mo });
          })()
        : null;
    const autoClosed = (autoCloseMs != null && Date.now() >= autoCloseMs) || kmDeadlinePassed;
    const signupClosed = !!selected.signupClosed || autoClosed;
    const fmtCloseAt =
      autoCloseMs != null
        ? (() => {
            const d = new Date(autoCloseMs);
            const wd = wdShort[d.getDay()];
            return tr('activities.date.closeAt', {
              wd,
              day: String(d.getDate()),
              month: String(d.getMonth() + 1),
              hh: String(d.getHours()).padStart(2, '0'),
              mm: String(d.getMinutes()).padStart(2, '0'),
            });
          })()
        : null;
    const toggleSignup = () =>
      mutate(selected.id, (a) => ({ ...a, signupClosed: !a.signupClosed }));
    // Clear "when and where" from the reserved courts.
    // STANDALONE: time/place/courts are not in the calendar — read from the activity's bookingDraft.
    const saDraft = selected.bookingDraft;
    const isStandalone = saDraft?.slotMode === 'standalone';
    const saRoom = isStandalone ? (saDraft.slotRooms || [])[0] || null : null;
    const saCourtNums = saRoom
      ? (saRoom.courtList || [])
          .map((c) => Number(c.num))
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b)
      : [];
    const courtNums = myBookings.length
      ? [...new Set(myBookings.map((b) => b.court))].sort((a, b) => a - b)
      : saCourtNums;
    const fmtNiceDate = (date) => {
      let dateLabel = date;
      try {
        const [y, m, d] = date.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        const wd = wdLong[dt.getDay()];
        const mo = moLong[m - 1];
        dateLabel = tr('activities.date.dayMonthYear', { wd, day: String(d), mo, year: String(y) });
      } catch {
        /* ignore: best-effort, non-critical */
      }
      return dateLabel;
    };
    const whenWhere = (() => {
      if (myBookings.length) {
        const date = myBookings
          .map((b) => b.date)
          .filter(Boolean)
          .sort()[0];
        const start = myBookings
          .map((b) => b.startTime)
          .filter(Boolean)
          .sort()[0];
        const end = myBookings
          .map((b) => b.endTime)
          .filter(Boolean)
          .sort()
          .slice(-1)[0];
        return { dateLabel: fmtNiceDate(date), start, end };
      }
      // Standalone: use date/time from the time window in the draft.
      if (isStandalone && saRoom?.singleDate && saRoom?.startTime) {
        return {
          dateLabel: fmtNiceDate(saRoom.singleDate),
          start: saRoom.startTime,
          end: saRoom.endTime || saRoom.startTime,
        };
      }
      return null;
    })();
    const venueName =
      isStandalone && (saDraft.venueName || '').trim()
        ? saDraft.venueName.trim()
        : clubInfo?.name || tr('activities.venueFallback');
    const venueLine2 = isStandalone
      ? [
          saDraft.venueAddress,
          [saDraft.venuePostnr, saDraft.venuePoststed].filter(Boolean).join(' '),
        ]
          .map((s) => (s || '').trim())
          .filter(Boolean)
          .join(', ')
      : '';
    const desc = selected.config?.description || '';
    // Sign-up deadline + minimum + right-sizing of courts (4 players per court).
    const minPlayers = selected.config?.minPlayers != null ? selected.config.minPlayers : null;
    // The minimum decision is made by default when SIGN-UP CLOSES (autoCloseMs).
    // Admin can override: a custom deadline = N days/hours before start (config.minDeadlineBefore/Unit).
    const minCustomMs =
      selected.config?.minDeadlineBefore != null && startMs != null
        ? startMs -
          Math.max(0, Number(selected.config.minDeadlineBefore) || 0) *
            (selected.config.minDeadlineUnit === 'hours' ? 3600000 : 86400000)
        : null;
    const usesCustomDeadline = minCustomMs != null && !isNaN(minCustomMs);
    const deadlineMs = minPlayers == null ? null : usesCustomDeadline ? minCustomMs : autoCloseMs;
    const deadlineLabel =
      deadlineMs != null
        ? (() => {
            const dt = new Date(deadlineMs);
            const wd = wdLong[dt.getDay()];
            const mo = moShort[dt.getMonth()];
            return tr('activities.date.dayMonthAt', {
              wd,
              day: String(dt.getDate()),
              mo,
              hh: String(dt.getHours()).padStart(2, '0'),
              mm: String(dt.getMinutes()).padStart(2, '0'),
            });
          })()
        : null;
    const deadlinePassed = deadlineMs != null && Date.now() >= deadlineMs;
    const confirmedCount = confirmed.length;
    const courtsNeeded = Math.max(1, Math.ceil(confirmedCount / 4));
    const bookedCourtsSorted = [...courtNums].sort((a, b) => a - b);
    const excessCount = Math.max(0, bookedCourtsSorted.length - courtsNeeded);
    const defaultRelease = bookedCourtsSorted.slice(courtsNeeded); // default: the highest-numbered ones
    const effRelease = (releaseSel ?? defaultRelease)
      .filter((cn) => bookedCourtsSorted.includes(cn))
      .sort((a, b) => a - b);
    const keptCourts = bookedCourtsSorted.filter((cn) => !effRelease.includes(cn));
    const releaseValid = keptCourts.length >= courtsNeeded; // must keep enough courts for the players
    // deviation (lint): `excessCourts` is unused in this range; `_`-prefixed.
    const _excessCourts = effRelease; // the courts that are actually released (admin-chosen)
    const meetsMin = minPlayers == null || confirmedCount >= minPlayers;
    const isCancelled = selected.status === 'cancelled';
    const isConfirmedLocked = selected.status === 'confirmed';
    const isFinalized = isCancelled || isConfirmedLocked;
    const finalizeNow = async () => {
      setBusy(true);
      if (!meetsMin) {
        await onReleaseFutureCourts?.(selected);
        mutate(selected.id, (a) => ({
          ...a,
          status: 'cancelled',
          cancelledReason: 'too_few',
          cancelledAt: Date.now(),
          signupClosed: true,
          registrations: (a.registrations || []).map((r) =>
            r.paidStatus === 'paid' ? { ...r, paidStatus: 'refunded', refundedAt: Date.now() } : r,
          ),
        }));
      } else {
        // Enough sign-ups → the tournament goes ahead. No automatic court change
        // (courts are managed via "Baner & plasser"). Sign-up is closed.
        mutate(selected.id, (a) => ({
          ...a,
          status: 'confirmed',
          finalizedAt: Date.now(),
          signupClosed: true,
        }));
      }
      setBusy(false);
      setArmLock(false);
    };
    const reopenActivity = () =>
      mutate(selected.id, (a) => {
        const bc = [
          ...new Set((bookings || []).filter((b) => b.seriesId === a.id).map((b) => b.court)),
        ].filter((n) => n != null);
        return {
          ...a,
          status: 'awaiting_signup',
          cancelledReason: null,
          cancelledAt: null,
          finalizedAt: null,
          signupClosed: false,
          config: { ...a.config, maxPlayers: null, courts: bc.length || a.config?.courts },
        };
      });
    // Manual lock: admin locks to the current sign-ups and releases the chosen courts (regardless of the deadline).
    // deviation (lint): `lockToRegistered` is defined in the monolith but not wired
    // into this range's JSX; `_`-prefixed to satisfy no-unused-vars. Body unchanged.
    const _lockToRegistered = async () => {
      setBusy(true);
      if (effRelease.length && onReleaseCourtNumbers)
        await onReleaseCourtNumbers(selected, effRelease);
      mutate(selected.id, (a) => {
        const nd = a.bookingDraft
          ? {
              ...a.bookingDraft,
              slotRooms: (a.bookingDraft.slotRooms || []).map((r) => ({
                ...r,
                slotCount: keptCourts.length,
                courtList: keptCourts.map((num, i) => ({ id: `crt_${num}_${i}`, num, name: '' })),
              })),
            }
          : a.bookingDraft;
        return {
          ...a,
          status: 'confirmed',
          finalizedAt: Date.now(),
          signupClosed: true,
          bookingDraft: nd,
          config: {
            ...a.config,
            maxPlayers: confirmedCount,
            courts: keptCourts.length,
            lockedCourts: keptCourts,
          },
        };
      });
      setBusy(false);
      setArmLock(false);
    };
    // Court picker: let admin decide which courts are released on lock.
    // deviation (lint): `courtReleasePicker` is built in the monolith but not rendered
    // in this range; `_`-prefixed to satisfy no-unused-vars. Markup unchanged.
    const _courtReleasePicker =
      excessCount > 0 && bookedCourtsSorted.length > 1 ? (
        <div className="mb-3">
          <p className="text-[12px] text-ink-2 mb-1.5">
            {tr('activities.release.pick', {
              chosen: String(effRelease.length),
              needed: String(courtsNeeded),
              players: String(confirmedCount),
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            {bookedCourtsSorted.map((cn) => {
              const rel = effRelease.includes(cn);
              const toggle = () => {
                const cur = (releaseSel ?? defaultRelease).slice();
                if (rel) {
                  setReleaseSel(cur.filter((x) => x !== cn)); // keep this one again
                } else {
                  const next = [...cur, cn];
                  while (next.length > excessCount) next.shift(); // keep the count = excess (swap out the oldest)
                  setReleaseSel(next);
                }
              };
              return (
                <button
                  key={cn}
                  type="button"
                  onClick={toggle}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition-colors"
                  style={
                    rel
                      ? {
                          background: '#fef2f2',
                          borderColor: 'var(--rose)',
                          color: 'var(--danger)',
                        }
                      : {
                          background: 'var(--lime-bg)',
                          borderColor: 'var(--lime-deep)',
                          color: 'var(--lime-text)',
                        }
                  }
                >
                  {tr('activities.release.court', {
                    court: String(cn),
                    state: tr(rel ? 'activities.release.released' : 'activities.release.kept'),
                  })}
                </button>
              );
            })}
          </div>
          {!releaseValid && (
            <p className="text-[11px] mt-1.5" style={{ color: 'var(--danger)' }}>
              {tr(
                courtsNeeded === 1
                  ? 'activities.release.keepMinOne'
                  : 'activities.release.keepMinMany',
                { needed: String(courtsNeeded), players: String(confirmedCount) },
              )}
            </p>
          )}
          {releaseValid && keptCourts.length > courtsNeeded && (
            <p className="text-[11px] mt-1.5" style={{ color: '#b45309' }}>
              {tr('activities.release.keepingMore', {
                kept: String(keptCourts.length),
                needed: String(courtsNeeded),
              })}
            </p>
          )}
        </div>
      ) : null;
    // Direct court adjustment (works even when the activity is locked).
    const addCourt = async () => {
      setBusy(true);
      // deviation: type-only (monolith is @ts-nocheck) — cast to any; the prop's
      // inferred return is void, so .created/.court reads and the truthiness test fail.
      const res: any = await onAddCourtToActivity?.(selected);
      if (res && res.created) {
        const newCourts = [...new Set([...bookedCourtsSorted, Number(res.court)])]
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b);
        mutate(selected.id, (a) => {
          const nd = a.bookingDraft
            ? {
                ...a.bookingDraft,
                slotRooms: (a.bookingDraft.slotRooms || []).map((r) => ({
                  ...r,
                  slotCount: newCourts.length,
                  courtList: newCourts.map((num, i) => ({ id: `crt_${num}_${i}`, num, name: '' })),
                })),
              }
            : a.bookingDraft;
          return {
            ...a,
            status: a.status === 'cancelled' ? a.status : 'awaiting_signup',
            signupClosed: false,
            finalizedAt: null,
            bookingDraft: nd,
            config: { ...a.config, maxPlayers: null },
          };
        });
      }
      setBusy(false);
    };
    const releaseOneCourt = async () => {
      if (bookedCourtsSorted.length <= 1) return;
      setBusy(true);
      const highest = bookedCourtsSorted[bookedCourtsSorted.length - 1];
      await onReleaseCourtNumbers?.(selected, [highest]);
      const remaining = bookedCourtsSorted.filter((cn) => cn !== highest);
      mutate(selected.id, (a) => {
        const nd = a.bookingDraft
          ? {
              ...a.bookingDraft,
              slotRooms: (a.bookingDraft.slotRooms || []).map((r) => ({
                ...r,
                slotCount: remaining.length,
                courtList: remaining.map((num, i) => ({ id: `crt_${num}_${i}`, num, name: '' })),
              })),
            }
          : a.bookingDraft;
        return { ...a, bookingDraft: nd, config: { ...a.config, maxPlayers: null } };
      });
      setBusy(false);
    };

    // CUSTOM ACTIVITY (Coaching / Arrangement / Custom): participant sign-up.
    if (selected.config?.isCustomActivity) {
      const fmtNoC = (iso) => {
        if (!iso) return null;
        const [y, m, d] = iso.split('-').map(Number);
        const wd = wdLong[new Date(y, m - 1, d).getDay()];
        const mo = moShort[m - 1];
        return tr('activities.date.dayMonth', { wd, day: String(d), mo });
      };
      const ct = getTournamentType(selected.config?.tournamentType);
      const cMax = selected.config?.maxPlayers;
      const cMaxRes = selected.config?.maxReserves;
      const cAllowWait = selected.config?.allowReserves !== false;
      const regs = selected.registrations || [];
      const confirmed = regs.filter((r) => r.status === 'confirmed');
      const waitlist = regs.filter((r) => r.status === 'waitlist');
      const full = cMax != null && confirmed.length >= cMax;
      const waitFull = full && (!cAllowWait || (cMaxRes != null && waitlist.length >= cMaxRes));
      const meReg = demoUser ? regs.find((r) => r.playerId === demoUser.id) : null;
      const cand = effectivePlayerDB
        .filter(
          (p) =>
            !regs.some((r) => r.playerId === p.id) &&
            (!pickerSearch || (p.name || '').toLowerCase().includes(pickerSearch.toLowerCase())),
        )
        .slice(0, 30);
      return (
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 slide-up">
          <div className="flex items-center justify-between gap-2 mb-4">
            <button
              onClick={() => {
                setSelectedId(null);
              }}
              className="btn-outline text-sm"
            >
              <ArrowLeft size={14} /> {tr('activities.allActivities')}
            </button>
            <button
              onClick={() => setAdminMode(!adminMode)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${adminMode ? 'bg-amber-300 text-amber-900 border-amber-300 shadow' : 'bg-white text-ink-3 border-line hover:border-line-soft'}`}
            >
              {tr(adminMode ? 'activities.adminOn' : 'activities.adminOff')}
            </button>
          </div>

          <div className="card-elev p-5 mb-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                style={{ background: ct.color }}
              >
                {ct.label}
              </span>
              {full ? (
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: '#fee2e2', color: '#991b1b' }}
                >
                  {tr('activities.full')}
                </span>
              ) : (
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: '#dcfce7', color: '#166534' }}
                >
                  {tr('activities.freeSlots')}
                </span>
              )}
            </div>
            <h1 className="font-display text-xl font-semibold text-ink-1">
              {selected.config?.name}
            </h1>
            <div className="mt-2 flex items-center gap-3 text-sm text-ink-2 flex-wrap">
              <span className="flex items-center gap-1">
                <Clock size={15} className="text-ink-3" />
                {selected.config?.activityDate ? (
                  <>
                    <strong>{fmtNoC(selected.config.activityDate)}</strong>
                    {selected.config?.activityTime
                      ? ` · ${selected.config.activityTime}${selected.config?.activityEndTime ? `–${selected.config.activityEndTime}` : ''}`
                      : ''}
                  </>
                ) : (
                  tr('activities.when.notSet')
                )}
              </span>
              <span className="flex items-center gap-1">
                <MapPin size={15} className="text-ink-3" />
                {selected.config?.location || clubInfo?.name || tr('activities.venueFallback')}
              </span>
            </div>
            {selected.config?.description && (
              <p className="text-sm text-ink-2 mt-3 leading-relaxed whitespace-pre-wrap">
                {selected.config.description}
              </p>
            )}
            {selected.config?.reserveCourts && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  className="font-mono text-[10px] font-semibold px-2 py-1 rounded"
                  style={{ background: '#eff6ff', color: '#1e40af' }}
                >
                  {tr('activities.courtsReserved')}
                </span>
                {onOpenCourtReservation && !selected.config?.noCourts && (
                  <button
                    onClick={() => onOpenCourtReservation(selected.id)}
                    className="btn-outline text-[12px]"
                  >
                    <Calendar size={13} /> {tr('activities.reserveCourts')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sign-up */}
          <div className="card-elev p-5 mb-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-display text-base font-semibold text-ink-1">
                {tr('activities.signedUpCount', {
                  count: String(confirmed.length),
                  max: cMax != null ? `/${cMax}` : '',
                })}
              </h3>
              {!adminMode &&
                (meReg ? (
                  <button
                    onClick={() => removePlayer(demoUser.id)}
                    className="text-[12px] px-3 py-1.5 rounded-lg"
                    style={{ color: 'var(--danger)' }}
                  >
                    {tr('activities.signMeOff')}
                  </button>
                ) : (
                  <button
                    onClick={() => addSingle(demoUser.id)}
                    disabled={!demoUser || (full && waitFull)}
                    className="btn-lime text-sm disabled:opacity-40"
                  >
                    <Plus size={14} />{' '}
                    {tr(
                      full && cAllowWait && !waitFull
                        ? 'activities.joinWaitlist'
                        : 'activities.signMeUp',
                    )}
                  </button>
                ))}
            </div>
            {confirmed.length === 0 ? (
              <p className="text-sm text-ink-3">{tr('activities.noSignupsYet')}</p>
            ) : (
              <div className="space-y-1.5">
                {confirmed.map((r) => (
                  <div
                    key={r.playerId}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-line bg-white"
                  >
                    <span className="flex-1 text-sm text-ink-1 truncate">
                      {playerName(r.playerId)}
                    </span>
                    {adminMode && (
                      <button
                        onClick={() => removePlayer(r.playerId)}
                        className="text-[11px] hover:underline"
                        style={{ color: 'var(--danger)' }}
                      >
                        {tr('activities.remove')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {waitlist.length > 0 && (
              <>
                <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-4 mb-1.5">
                  {tr('activities.waitlistCount', {
                    count: String(waitlist.length),
                    max: cMaxRes != null ? `/${cMaxRes}` : '',
                  })}
                </h4>
                <div className="space-y-1.5">
                  {waitlist.map((r, i) => (
                    <div
                      key={r.playerId}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-line bg-bg-soft/40"
                    >
                      <span className="font-mono text-[10px] text-ink-3">{i + 1}.</span>
                      <span className="flex-1 text-sm text-ink-2 truncate">
                        {playerName(r.playerId)}
                      </span>
                      {adminMode && (
                        <button
                          onClick={() => removePlayer(r.playerId)}
                          className="text-[11px] hover:underline"
                          style={{ color: 'var(--danger)' }}
                        >
                          {tr('activities.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Admin: add participant */}
          {adminMode && (
            <div className="card-elev p-5">
              <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
                {tr('activities.addParticipant')}
              </h3>
              <input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder={tr('activities.searchPlayer')}
                aria-label={tr('activities.searchPlayer')}
                className="w-full px-3 py-2 rounded-lg border border-line text-sm mb-2"
              />
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {cand.length === 0 ? (
                  <p className="text-sm text-ink-3">{tr('activities.noMatches')}</p>
                ) : (
                  cand.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addSingle(p.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-line hover:border-lime-deep hover:bg-lime-bg/20 text-left"
                    >
                      <span className="text-sm text-ink-1 truncate">{p.name}</span>
                      <Plus size={14} className="text-lime-deep flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    // SERIESPILL SEASON SIGN-UP: team-wise sign-up (pairs) + payment of the season fee.
    if (selected.config?.tournamentType === 'seriespill_season') {
      const fmtNo2 = (iso) => {
        if (!iso) return null;
        const [y, m, d] = iso.split('-').map(Number);
        const wd = wdLong[new Date(y, m - 1, d).getDay()];
        const mo = moShort[m - 1];
        return tr('activities.date.dayMonthYear', { wd, day: String(d), mo, year: String(y) });
      };
      const sf = selected.config?.seasonFee;
      const nm = (id) => effectivePlayerDB.find((p) => p.id === id)?.name || '?';
      const teamRegs = (selected.registrations || []).filter((r) => r.partnerId);
      // Existing teams in the series (this class) + payment status for the next season.
      const series =
        seriesForSignup && seriesForSignup.id === selected.config?.seriespillId
          ? seriesForSignup
          : null;
      const sClassId = selected.config?.classId;
      const sSeason = selected.config?.season;
      const seriesRegs = series?.seasonRegistrations || {};
      const sCls = (series?.config?.classes || []).find((c) => c.id === sClassId);
      const sDivName = (id) => (sCls?.divisions || []).find((d) => d.id === id)?.name || '—';
      const classPairs = (series?.pairs || []).filter((p) => p.classId === sClassId);
      const paidPairs = classPairs
        .filter((p) => seriesRegs[p.id]?.paidForSeason === sSeason)
        .slice()
        .sort((a, b) => (a.seed || 99) - (b.seed || 99));
      const unpaidPairs = classPairs
        .filter((p) => seriesRegs[p.id]?.paidForSeason !== sSeason)
        .slice()
        .sort((a, b) => (a.seed || 99) - (b.seed || 99));
      const myReg = demoUser
        ? teamRegs.find((r) => r.playerId === demoUser.id || r.partnerId === demoUser.id)
        : null;
      const myPartnerId = myReg
        ? myReg.playerId === demoUser?.id
          ? myReg.partnerId
          : myReg.playerId
        : null;
      const markTeamPaid = (reg, paid) =>
        mutate(selected.id, (a) => ({
          ...a,
          registrations: (a.registrations || []).map((r) =>
            r.playerId === reg.playerId && r.partnerId === reg.partnerId
              ? { ...r, paidStatus: paid ? 'paid' : 'free' }
              : r,
          ),
        }));
      const withdrawTeam = (reg) =>
        mutate(selected.id, (a) => ({
          ...a,
          registrations: (a.registrations || []).filter(
            (r) => !(r.playerId === reg.playerId && r.partnerId === reg.partnerId),
          ),
        }));
      return (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 slide-up">
          <div className="flex items-center justify-between gap-2 mb-4">
            <button
              onClick={() => {
                setSelectedId(null);
                setSelfPairPicking(false);
              }}
              className="btn-outline text-sm"
            >
              <ArrowLeft size={14} /> {tr('activities.allActivities')}
            </button>
            <button
              onClick={() => setAdminMode(!adminMode)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${adminMode ? 'bg-amber-300 text-amber-900 border-amber-300 shadow' : 'bg-white text-ink-3 border-line hover:border-line-soft'}`}
              title={tr('activities.toggleAdminTitle')}
            >
              {tr(adminMode ? 'activities.adminOn' : 'activities.adminOff')}
            </button>
          </div>
          <div className="card-elev p-5 mb-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                style={{ background: '#0891b2' }}
              >
                SERIE
              </span>
              <span
                className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: '#16a34a', color: '#fff' }}
              >
                {tr('activities.signupOpenBadge')}
              </span>
            </div>
            <h1 className="font-display text-xl font-semibold text-ink-1">
              {selected.config?.name || tr('activities.seasonFallbackName')}
            </h1>
            <div className="mt-2 flex items-center gap-2 text-sm text-ink-1">
              <Clock size={15} className="text-ink-3 flex-shrink-0" />
              <span>
                {tr('activities.seasonStart')}{' '}
                <strong>{fmtNo2(selected.config?.activityDate) || '—'}</strong>
                {selected.config?.signupDeadline
                  ? tr('activities.signupDeadlineSuffix', {
                      date: fmtNo2(selected.config.signupDeadline),
                    })
                  : ''}
              </span>
            </div>
            {sf != null && (
              <div
                className="mt-1.5 inline-block font-mono text-[12px] px-2 py-0.5 rounded"
                style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
              >
                {sf === 0
                  ? tr('activities.noSeasonFee')
                  : tr('activities.seasonFee', { amount: String(sf) })}
              </div>
            )}
            <p className="text-sm text-ink-2 mt-3 leading-relaxed">
              {tr('activities.seasonLeadPre')} <strong>{tr('activities.seasonLeadBold')}</strong>{' '}
              {tr('activities.seasonLeadPost')}
            </p>
          </div>

          {/* Sign up your team (user view) */}
          {!adminMode && (
            <div
              className="card-elev p-5 mb-4"
              style={{ borderColor: 'var(--lime-deep)', borderWidth: 1 }}
            >
              <div
                className="flex items-center justify-between gap-2 mb-3 p-2 rounded-lg"
                style={{ background: '#fef9c3', border: '1px dashed #ca8a04' }}
              >
                <div className="text-[12px] text-ink-2 min-w-0">
                  <span
                    className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5"
                    style={{ background: '#a16207', color: '#fff' }}
                  >
                    DEMO
                  </span>
                  {tr('activities.signedInAs')}{' '}
                  <strong className="text-ink-1">
                    {demoUser ? demoUser.name : tr('activities.noProfile')}
                  </strong>
                </div>
              </div>
              {myReg ? (
                <div>
                  <p className="text-sm text-ink-1 font-semibold mb-1">
                    {tr('activities.youAreSignedUpWith')} <strong>{nm(myPartnerId)}</strong>
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {myReg.paidStatus === 'paid' ? (
                      <span
                        className="font-mono text-[10px] font-bold px-2 py-1 rounded"
                        style={{ background: '#dcfce7', color: '#166534' }}
                      >
                        {tr('activities.seasonFeePaid')}
                      </span>
                    ) : (
                      <>
                        <span
                          className="font-mono text-[10px] font-bold px-2 py-1 rounded"
                          style={{ background: '#fef3c7', color: '#92400e' }}
                        >
                          {tr('activities.notPaid')}
                        </span>
                        {sf ? (
                          <button
                            onClick={() => markTeamPaid(myReg, true)}
                            className="btn-lime text-[12px]"
                          >
                            <Check size={13} /> {tr('activities.payAmount', { amount: String(sf) })}
                          </button>
                        ) : null}
                      </>
                    )}
                    <button
                      onClick={() => withdrawTeam(myReg)}
                      className="text-[12px] text-ink-3 hover:text-ink-1 underline"
                    >
                      {tr('activities.withdrawTeam')}
                    </button>
                  </div>
                </div>
              ) : selfPairPicking ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm text-ink-1 font-semibold">
                      {tr('activities.pickPartner')}
                    </p>
                    <button
                      onClick={() => setSelfPairPicking(false)}
                      className="text-xs text-ink-3"
                    >
                      {tr('home.common.cancel')}
                    </button>
                  </div>
                  {renderPartnerPicker(demoUser.id, (pid) => {
                    addTeam(demoUser.id, pid, null);
                    setSelfPairPicking(false);
                  })}
                </div>
              ) : (
                <button
                  onClick={() => setSelfPairPicking(true)}
                  disabled={!demoUser}
                  className="btn-lime text-sm disabled:opacity-40"
                >
                  <Plus size={14} /> {tr('activities.signUpYourTeam')}
                  {sf ? tr('activities.priceSuffix', { amount: String(sf) }) : ''}
                </button>
              )}
            </div>
          )}

          {/* Admin: add team manually */}
          {adminMode && (
            <div className="card-elev p-5 mb-4">
              <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
                {tr('activities.addTeamAdmin')}
              </h3>
              {pendingTeamP1 ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm text-ink-1">
                      {tr('activities.partnerFor')} <strong>{nm(pendingTeamP1)}</strong>?
                    </p>
                    <button onClick={() => setPendingTeamP1(null)} className="text-xs text-ink-3">
                      {tr('home.common.cancel')}
                    </button>
                  </div>
                  {renderPartnerPicker(pendingTeamP1, (pid) => {
                    addTeam(pendingTeamP1, pid, null);
                    setPendingTeamP1(null);
                  })}
                </div>
              ) : (
                <div>
                  <p className="text-[12px] text-ink-3 mb-2">
                    {tr('activities.pickP1ThenPartner')}
                  </p>
                  {renderPartnerPicker(null, (pid) => setPendingTeamP1(pid))}
                </div>
              )}
            </div>
          )}

          {/* Signed-up teams */}
          <div className="card-elev p-4">
            <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
              {tr('activities.signedUpTeams', { count: String(teamRegs.length) })}
            </h3>
            {teamRegs.length === 0 ? (
              <p className="text-sm text-ink-3">{tr('activities.noTeamsYet')}</p>
            ) : (
              <div className="space-y-1.5">
                {teamRegs.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white"
                    style={{ borderColor: r.paidStatus === 'paid' ? '#bbf7d0' : '#fde68a' }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-ink-1 truncate">
                        {nm(r.playerId)} / {nm(r.partnerId)}
                      </div>
                    </div>
                    {r.paidStatus === 'paid' ? (
                      <span
                        className="font-mono text-[10px] font-bold px-2 py-1 rounded flex-shrink-0"
                        style={{ background: '#dcfce7', color: '#166534' }}
                      >
                        {tr('activities.paid')}
                      </span>
                    ) : (
                      <span
                        className="font-mono text-[10px] font-bold px-2 py-1 rounded flex-shrink-0"
                        style={{ background: '#fef3c7', color: '#92400e' }}
                      >
                        {tr('activities.notPaid')}
                      </span>
                    )}
                    {adminMode &&
                      (r.paidStatus === 'paid' ? (
                        <button
                          onClick={() => markTeamPaid(r, false)}
                          className="text-[11px] text-ink-3 hover:text-ink-1 underline flex-shrink-0"
                        >
                          {tr('activities.undo')}
                        </button>
                      ) : (
                        <button
                          onClick={() => markTeamPaid(r, true)}
                          className="text-[11px] text-ink-3 hover:text-ink-1 underline flex-shrink-0"
                        >
                          {tr('activities.markPaid')}
                        </button>
                      ))}
                    {adminMode && (
                      <button
                        onClick={() => withdrawTeam(r)}
                        className="text-[11px] flex-shrink-0"
                        style={{ color: 'var(--danger)' }}
                      >
                        {tr('activities.removeLower')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-ink-3 mt-3">{tr('activities.newTeamsNote')}</p>
          </div>

          {series && (
            <>
              {/* Teams that have paid the season fee */}
              <div className="card-elev p-4 mt-4">
                <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
                  {tr('activities.paidSeasonFee', { count: String(paidPairs.length) })}
                </h3>
                {paidPairs.length === 0 ? (
                  <p className="text-sm text-ink-3">
                    {tr('activities.nonePaidForSeason', { season: String(sSeason) })}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {paidPairs.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white"
                        style={{ borderColor: '#bbf7d0' }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-ink-1 truncate">
                            {p.p1Name} / {p.p2Name}
                          </div>
                          <div className="text-[11px] text-ink-3">{sDivName(p.divisionId)}</div>
                        </div>
                        <span
                          className="font-mono text-[10px] font-bold px-2 py-1 rounded flex-shrink-0"
                          style={{ background: '#dcfce7', color: '#166534' }}
                        >
                          {tr('activities.paid')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active teams in the current season that have not paid */}
              <div className="card-elev p-4 mt-4">
                <h3 className="font-display text-base font-semibold text-ink-1 mb-1">
                  {tr('activities.activeUnpaid', { count: String(unpaidPairs.length) })}
                </h3>
                <p className="text-[12px] text-ink-3 mb-2">
                  {tr('activities.activeUnpaidLead', { season: String(sSeason) })}
                </p>
                {unpaidPairs.length === 0 ? (
                  <p className="text-sm text-ink-3">{tr('activities.allActiveTeamsPaid')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {unpaidPairs.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white"
                        style={{ borderColor: '#fde68a' }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-ink-1 truncate">
                            {p.p1Name} / {p.p2Name}
                          </div>
                          <div className="text-[11px] text-ink-3">{sDivName(p.divisionId)}</div>
                        </div>
                        <span
                          className="font-mono text-[10px] font-bold px-2 py-1 rounded flex-shrink-0"
                          style={{ background: '#fef3c7', color: '#92400e' }}
                        >
                          {tr('activities.notPaid')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 slide-up">
        {/* DETAIL */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <button
            onClick={() => {
              setSelectedId(null);
              setEditing(false);
              setConfirmDel(false);
            }}
            className="btn-outline text-sm"
          >
            <ArrowLeft size={14} /> {tr('activities.allActivities')}
          </button>
          <div className="flex items-center gap-2">
            {/* Shortcut: preview the tournament straight from the header (same action
                as the button at the bottom) — so admin doesn't have to scroll down. */}
            {adminMode && isPadel24 && (
              <button
                onClick={startActivity}
                disabled={c.confirmed < 4 || busy}
                className="btn-lime text-sm disabled:opacity-40"
                title={
                  c.confirmed < 4
                    ? tr('activities.previewNeeds4')
                    : tr('activities.previewTournament')
                }
              >
                <Play size={14} strokeWidth={2.5} />{' '}
                {tr(busy ? 'activities.starting' : 'activities.preview')}
              </button>
            )}
            <button
              onClick={() => setAdminMode(!adminMode)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${adminMode ? 'bg-amber-300 text-amber-900 border-amber-300 shadow' : 'bg-white text-ink-3 border-line hover:border-line-soft'}`}
              title={tr('activities.toggleAdminTitle')}
            >
              {tr(adminMode ? 'activities.adminOn' : 'activities.adminOff')}
            </button>
          </div>
        </div>
        <div className="card-elev p-5 mb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {type && (
                  <span
                    className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ background: type.color }}
                  >
                    {type.abbrev}
                  </span>
                )}
                {(() => {
                  const dHasLvl =
                    selected.config?.levelMin != null || selected.config?.levelMax != null;
                  const lc = dHasLvl
                    ? ELO_LEVELS.find(
                        (l) =>
                          l.level ===
                          (selected.config.levelMin != null ? selected.config.levelMin : 1),
                      )
                    : null;
                  const lv = dHasLvl
                    ? { label: levelLabel, bg: lc?.color, color: lc?.text }
                    : { label: tr('activities.allLevels'), bg: '#eef2f7', color: '#64748b' };
                  return (
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: lv.bg, color: lv.color }}
                    >
                      {lv.label}
                    </span>
                  );
                })()}
                {(() => {
                  const dFull = c.max != null && c.confirmed >= c.max;
                  const st =
                    selected.status === 'cancelled'
                      ? { label: tr('activities.badge.cancelled'), bg: '#dc2626', color: '#fff' }
                      : selected.status === 'confirmed'
                        ? { label: tr('activities.badge.locked'), bg: '#475569', color: '#fff' }
                        : signupClosed
                          ? {
                              label: tr(
                                autoClosed && !selected.signupClosed
                                  ? 'activities.badge.closedAuto'
                                  : 'activities.badge.closed',
                              ),
                              bg: '#9ca3af',
                              color: '#fff',
                            }
                          : dFull
                            ? {
                                label: tr('activities.badge.waitlistOpen'),
                                bg: '#d97706',
                                color: '#fff',
                              }
                            : { label: tr('activities.badge.open'), bg: '#16a34a', color: '#fff' };
                  return (
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: st.bg, color: st.color }}
                    >
                      {st.label}
                    </span>
                  );
                })()}
              </div>
              <h1 className="font-display text-xl font-semibold text-ink-1">
                {selected.config?.name || tr('activities.activityFallback')}
              </h1>
              {selected.recurringId && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium"
                  style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
                >
                  <span>↻</span>
                  <span>
                    {tr('activities.partOfSeries')}
                    {selected.seriesName
                      ? tr('activities.seriesNameSuffix', { name: selected.seriesName })
                      : ''}
                    {selected.seriesCode ? ` · #${selected.seriesCode}` : ''}
                    {selected.recurringCount
                      ? tr('activities.occurrenceOf', {
                          index: String((selected.recurringIndex ?? 0) + 1),
                          total: String(selected.recurringCount),
                        })
                      : selected.recurringIndex != null
                        ? tr('activities.occurrenceRolling', {
                            index: String(selected.recurringIndex + 1),
                          })
                        : ''}
                  </span>
                </div>
              )}
              {/* Clear WHEN and WHERE */}
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2 text-sm text-ink-1">
                  <Clock size={15} className="text-ink-3 flex-shrink-0" />
                  {whenWhere ? (
                    <span>
                      <strong>{whenWhere.dateLabel}</strong> · {whenWhere.start}–{whenWhere.end}
                    </span>
                  ) : (
                    <span className="text-ink-3">{tr('activities.when.notSetYet')}</span>
                  )}
                </div>
                <div className="flex items-start gap-2 text-sm text-ink-1">
                  <MapPin size={15} className="text-ink-3 flex-shrink-0 mt-0.5" />
                  <span>
                    {venueName}
                    {courtNums.length
                      ? tr('activities.courtSuffix', { courts: courtNums.join(', ') })
                      : ''}
                    {venueLine2 ? (
                      <span className="block text-[12px] text-ink-3">{venueLine2}</span>
                    ) : null}
                  </span>
                </div>
              </div>
              <div className="text-[13px] text-ink-3 mt-1.5 flex items-center gap-1.5 flex-wrap">
                {isKmActivity(selected) ? (
                  (() => {
                    const prices = kmBucketsOf(selected)
                      .map((b) => kmBucketPrice(selected, b.bucketKey))
                      .filter((p) => p != null);
                    if (!prices.length || prices.every((p) => p === 0))
                      return <span>{tr('activities.free')}</span>;
                    const min = Math.min(...prices),
                      max = Math.max(...prices);
                    return (
                      <span>
                        {min === max
                          ? tr('activities.pricePerTeam', { amount: String(min) })
                          : tr('activities.pricePerTeamRange', {
                              min: String(min),
                              max: String(max),
                            })}
                      </span>
                    );
                  })()
                ) : selected.config?.pricePerPlayer ? (
                  <span>
                    {tr('activities.pricePerPlayer', {
                      amount: String(selected.config.pricePerPlayer),
                    })}
                  </span>
                ) : (
                  <span>{tr('activities.free')}</span>
                )}
                {selected.config?.deregisterCutoffHours != null && (
                  <span>
                    {tr('activities.deregisterCutoff', {
                      hours: String(selected.config.deregisterCutoffHours),
                    })}
                  </span>
                )}
              </div>
              {!signupClosed && fmtCloseAt && (
                <div className="text-[12px] text-ink-3 mt-0.5">
                  {tr('activities.signupAutoCloses', {
                    at: fmtCloseAt,
                    when:
                      signupCloseHours > 0
                        ? tr('activities.hoursBeforeStart', { hours: String(signupCloseHours) })
                        : tr('activities.atStart'),
                  })}
                </div>
              )}
              {!signupClosed && isKmActivity(selected) && kmDeadlineLabel && (
                <div className="text-[12px] text-ink-3 mt-0.5">
                  {tr('activities.signupClosesAt', { at: kmDeadlineLabel })}
                </div>
              )}
              {signupClosed &&
                isKmActivity(selected) &&
                kmDeadlinePassed &&
                !selected.signupClosed && (
                  <div className="text-[12px] mt-0.5" style={{ color: 'var(--danger)' }}>
                    {tr('activities.signupClosedDeadlinePassed', {
                      at: kmDeadlineLabel ? ` ${kmDeadlineLabel}` : '',
                    })}
                  </div>
                )}
              {signupClosed &&
                !(isKmActivity(selected) && kmDeadlinePassed && !selected.signupClosed) && (
                  <div className="text-[12px] mt-0.5" style={{ color: 'var(--danger)' }}>
                    {tr('activities.signupClosedHow', {
                      how:
                        autoClosed && !selected.signupClosed
                          ? signupCloseHours > 0
                            ? tr('activities.closedAutoHours', { hours: String(signupCloseHours) })
                            : tr('activities.closedAutoAtStart')
                          : tr('activities.closedManually'),
                    })}
                  </div>
                )}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-semibold text-ink-1">
                {c.confirmed}
                {c.max != null ? `/${c.max}` : ''}
              </div>
              {c.mix && (
                <div className="text-[11px] text-ink-3">
                  {tr('activities.mixCounts', {
                    men: String(c.men.confirmed),
                    menMax: String(c.men.max),
                    women: String(c.women.confirmed),
                    womenMax: String(c.women.max),
                  })}
                </div>
              )}
              {c.club && (
                <div className="text-[11px] text-ink-3">
                  {c.clubs.map((cl) => `${cl.confirmed}/${cl.max} ${cl.name}`).join(' · ')}
                </div>
              )}
              <div className="text-[11px] text-ink-3">
                {tr('activities.signedUpLower')}
                {c.waitlist > 0
                  ? tr('activities.waitlistSuffix', { count: String(c.waitlist) })
                  : ''}
              </div>
            </div>
          </div>
        </div>

        {/* About the tournament (free text) */}
        {desc && desc.trim() && (
          <div className="card-elev p-5 mb-4">
            <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
              {tr('activities.aboutTournament')}
            </h3>
            <RichText text={desc} />
          </div>
        )}

        {/* SIGN-UP DEADLINE & MINIMUM — status + Decide now */}
        {(deadlineMs != null ||
          minPlayers != null ||
          isFinalized ||
          (adminMode && bookedCourtsSorted.length > 0)) && (
          <div
            className="card-elev p-5 mb-4"
            style={
              isCancelled
                ? { borderColor: 'var(--rose)', borderWidth: 1 }
                : isConfirmedLocked
                  ? { borderColor: 'var(--lime-deep)', borderWidth: 1 }
                  : {}
            }
          >
            {isCancelled ? (
              <div>
                <h3
                  className="font-display text-base font-semibold mb-1"
                  style={{ color: 'var(--danger)' }}
                >
                  {tr('activities.cancelledTooFew')}
                </h3>
                <p className="text-sm text-ink-2">
                  {tr('activities.cancelledLead', {
                    count: String(confirmedCount),
                    min:
                      minPlayers != null
                        ? tr('activities.butMinRequired', { min: String(minPlayers) })
                        : '',
                  })}
                </p>
                {adminMode && (
                  <button onClick={reopenActivity} className="btn-outline text-sm mt-3">
                    {tr('activities.reopenSignup')}
                  </button>
                )}
              </div>
            ) : isConfirmedLocked ? (
              <div>
                <h3
                  className="font-display text-base font-semibold mb-1"
                  style={{ color: 'var(--lime-text)' }}
                >
                  {tr('activities.confirmedGoesAhead')}
                </h3>
                <p className="text-sm text-ink-2">
                  {tr('activities.confirmedLead', { count: String(confirmedCount) })}
                  {!isPadel24 ? tr('activities.adjustCourtsHint') : ''}
                </p>
                {adminMode && (
                  <button onClick={reopenActivity} className="btn-outline text-sm mt-3">
                    {tr('activities.reopenSignup')}
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="font-display text-base font-semibold text-ink-1">
                    {tr('activities.deadlineAndMinimum')}
                  </h3>
                  {deadlineLabel && (
                    <span
                      className="font-mono text-[10px] font-bold px-2 py-0.5 rounded"
                      style={
                        deadlinePassed
                          ? { background: 'var(--rose)', color: '#fff' }
                          : { background: 'var(--lime-bg)', color: 'var(--lime-text)' }
                      }
                    >
                      {deadlinePassed ? tr('activities.deadlinePassed') : deadlineLabel}
                    </span>
                  )}
                </div>
                {(minPlayers != null || c.max != null) &&
                  (() => {
                    const maxTarget =
                      c.max != null
                        ? c.max
                        : bookedCourtsSorted.length
                          ? bookedCourtsSorted.length * 4
                          : null;
                    const tier =
                      maxTarget != null && confirmedCount >= maxTarget
                        ? 'full'
                        : minPlayers == null || confirmedCount >= minPlayers
                          ? 'enough'
                          : 'under';
                    const barColor =
                      tier === 'full'
                        ? 'var(--lime-deep)'
                        : tier === 'enough'
                          ? '#eab308'
                          : '#ef4444';
                    const labelColor =
                      tier === 'full'
                        ? 'var(--lime-deep)'
                        : tier === 'enough'
                          ? '#a16207'
                          : '#dc2626';
                    const label =
                      tier === 'full'
                        ? tr('activities.tier.full')
                        : tier === 'enough'
                          ? maxTarget != null
                            ? tr('activities.tier.enoughCanFill', { max: String(maxTarget) })
                            : tr('activities.tier.enough')
                          : minPlayers != null
                            ? tr('activities.tier.missing', {
                                count: String(minPlayers - confirmedCount),
                              })
                            : '';
                    const denom = maxTarget != null ? maxTarget : minPlayers || 1;
                    const minPct = maxTarget && minPlayers ? (minPlayers / maxTarget) * 100 : null;
                    return (
                      <div className="mb-2">
                        <div className="flex justify-between text-[12px] mb-1">
                          <span className="text-ink-2">
                            {tr('activities.progressCount', {
                              count: String(confirmedCount),
                              of:
                                maxTarget != null
                                  ? tr('activities.ofMax', { max: String(maxTarget) })
                                  : '',
                              min:
                                minPlayers != null
                                  ? tr('activities.minSuffix', { min: String(minPlayers) })
                                  : '',
                            })}
                          </span>
                          <span style={{ color: labelColor }}>{label}</span>
                        </div>
                        <div className="relative">
                          <div
                            className="h-2 rounded-full overflow-hidden"
                            style={{ background: 'var(--line)' }}
                          >
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{
                                width: `${Math.min(100, (confirmedCount / denom) * 100)}%`,
                                background: barColor,
                              }}
                            />
                          </div>
                          {minPct != null && minPct > 1 && minPct < 99 && (
                            <div
                              className="absolute top-[-2px] bottom-[-2px]"
                              style={{
                                left: `${minPct}%`,
                                width: '2px',
                                background: 'rgba(15,22,17,0.45)',
                              }}
                              title={tr('activities.minimumMarker', { count: minPlayers })}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()}
                {deadlinePassed ? (
                  <div
                    className="mt-3 p-3 rounded-lg"
                    style={{
                      background: meetsMin ? 'var(--lime-bg)' : '#fef2f2',
                      border: `1px solid ${meetsMin ? 'var(--lime-deep)' : 'var(--rose)'}`,
                    }}
                  >
                    <p className="text-sm text-ink-1 mb-2">
                      {tr(meetsMin ? 'activities.closedEnough' : 'activities.closedTooFew', {
                        count: String(confirmedCount),
                        min: minPlayers != null ? `/${minPlayers}` : '',
                      })}
                    </p>
                    {adminMode ? (
                      meetsMin ? (
                        <button
                          onClick={finalizeNow}
                          disabled={busy}
                          className="btn-lime text-sm disabled:opacity-40"
                        >
                          {busy ? '…' : tr('activities.confirmGoesAhead')}
                        </button>
                      ) : (
                        <button
                          onClick={finalizeNow}
                          disabled={busy}
                          className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
                          style={{ background: 'var(--rose)' }}
                        >
                          {busy ? '…' : tr('activities.cancelAndRelease')}
                        </button>
                      )
                    ) : (
                      <span className="text-[12px] text-ink-3">
                        {tr('activities.awaitingClubDecision')}
                      </span>
                    )}
                  </div>
                ) : (
                  deadlineLabel && (
                    <p className="text-[12px] text-ink-3 mt-1.5">
                      {tr('activities.autoDecidePre', {
                        when: tr(
                          usesCustomDeadline
                            ? 'activities.atTheDeadline'
                            : 'activities.whenSignupCloses',
                        ),
                        deadline: deadlineLabel,
                      })}{' '}
                      <strong>{minPlayers}</strong> {tr('activities.autoDecidePost')}
                      {!isPadel24 ? tr('activities.adjustCourtsAnytimeHint') : ''}
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* COURTS & SLOTS — direct adjustment (admin), works even when locked. Not for KM/Padel24 (courts are managed in the setup). */}
        {adminMode && !isKmActivity(selected) && !isPadel24 && (
          <div className="card-elev p-4 mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-display text-base font-semibold text-ink-1">
                  {tr('activities.courtsAndSlots')}
                </h3>
                <p className="text-[12px] text-ink-3">
                  {tr(
                    bookedCourtsSorted.length === 1
                      ? 'activities.courtsBookedOne'
                      : 'activities.courtsBookedMany',
                    { count: String(bookedCourtsSorted.length) },
                  )}
                  {bookedCourtsSorted.length
                    ? tr('activities.courtListSuffix', {
                        courts: bookedCourtsSorted.join(', '),
                      })
                    : ''}{' '}
                  ·{' '}
                  {c.max != null
                    ? tr('activities.slotsCount', { count: String(c.max) })
                    : tr('activities.capacityFollowsCourts')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={releaseOneCourt}
                  disabled={busy || bookedCourtsSorted.length <= 1}
                  className="btn-outline text-sm disabled:opacity-40"
                >
                  {tr('activities.releaseOneCourt')}
                </button>
                <button
                  onClick={addCourt}
                  disabled={busy}
                  className="btn-lime text-sm disabled:opacity-40"
                >
                  {busy ? '…' : tr('activities.openOneMoreCourt')}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-ink-3 mt-2">{tr('activities.courtButtonsHelp')}</p>
            {c.waitlist >= 4 && (
              <p
                className="text-[11px] mt-2 px-2.5 py-1.5 rounded-lg"
                style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
              >
                {tr(
                  Math.floor(c.waitlist / 4) === 1
                    ? 'activities.waitlistEnoughForOneCourt'
                    : 'activities.waitlistEnoughForCourts',
                  {
                    count: String(c.waitlist),
                    courts: String(Math.floor(c.waitlist / 4)),
                  },
                )}
              </p>
            )}
          </div>
        )}

        {/* SIGN UP — user view (admin off). Simulated logged-in profile (DEMO). */}
        {!adminMode && (
          <div
            className="card-elev p-5 mb-4"
            style={{ borderColor: 'var(--lime-deep)', borderWidth: 1 }}
          >
            {/* Demo logged-in banner */}
            <div
              className="flex items-center justify-between gap-2 mb-3 p-2 rounded-lg"
              style={{ background: '#fef9c3', border: '1px dashed #ca8a04' }}
            >
              <div className="text-[12px] text-ink-2 min-w-0">
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5"
                  style={{ background: '#a16207', color: '#fff' }}
                >
                  DEMO
                </span>
                {tr('activities.signedInAs')}{' '}
                <strong className="text-ink-1">
                  {demoUser ? demoUser.name : tr('activities.noProfile')}
                </strong>
                {demoLvl && (
                  <span
                    className="font-mono text-[10px] font-bold px-1 rounded ml-1.5"
                    style={{ background: demoLvl.color, color: demoLvl.text }}
                    title={tr('activities.levelBadge', {
                      level: String(demoLvl.level),
                      name: demoLvl.name,
                    })}
                  >
                    N{demoLvl.level}
                  </span>
                )}
              </div>
              <button
                onClick={() => setProfileSwitch((v) => !v)}
                className="text-[11px] font-semibold underline text-ink-2 hover:text-ink-1 flex-shrink-0"
              >
                {tr('activities.switchProfile')}
              </button>
            </div>
            {profileSwitch && (
              <div className="mb-3 p-2 rounded-lg border border-line bg-bg-soft/40">
                <p className="text-[10px] text-ink-3 mb-1.5">{tr('activities.pickDemoProfile')}</p>
                <input
                  type="text"
                  className="input w-full text-sm mb-2"
                  placeholder={tr('activities.searchProfile')}
                  aria-label={tr('activities.searchProfile')}
                  value={profileSearch}
                  onChange={(e) => setProfileSearch(e.target.value)}
                />
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {profileResults.map((p) => {
                    const lvl = p.elo != null ? getEloLevel(p.elo) : null;
                    return (
                      <button
                        key={p.id}
                        onClick={() => pickDemoUser(p.id)}
                        className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-lime-bg/30 text-left"
                      >
                        {lvl && (
                          <span
                            className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
                            style={{ background: lvl.color, color: lvl.text }}
                          >
                            {lvl.level}
                          </span>
                        )}
                        <span className="text-sm text-ink-1 truncate">{p.name}</span>
                        {p.id === demoUser?.id && (
                          <Check size={14} className="text-lime-deep ml-auto flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* The actual sign-up for the logged-in profile */}
            {signupClosed ? (
              <div className="text-sm text-ink-2">{tr('activities.signupClosedForThis')}</div>
            ) : demoReg && !isKmActivity(selected) ? (
              selfSwap && selfPairPicking ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm text-ink-1 font-semibold">
                      {tr('activities.swapPartner')}
                    </p>
                    <button
                      onClick={() => {
                        setSelfSwap(false);
                        setSelfPairPicking(false);
                        setSelfPartnerSearch('');
                      }}
                      className="text-xs text-ink-3"
                    >
                      {tr('home.common.cancel')}
                    </button>
                  </div>
                  {renderPartnerPicker(demoUser.id, (pid) => {
                    swapPartner(demoReg, demoUser.id, pid);
                    setSelfSwap(false);
                    setSelfPairPicking(false);
                    setSelfPartnerSearch('');
                  })}
                </div>
              ) : (
                (() => {
                  const myPartnerId = demoReg.partnerId
                    ? demoReg.playerId === demoUser.id
                      ? demoReg.partnerId
                      : demoReg.playerId
                    : null;
                  const isMe = (r) => r.playerId === demoUser.id || r.partnerId === demoUser.id;
                  // Actual days from the Padel24 setup (for the day choice on the standby reserve)
                  const p24Days = (selected.config?.p24Snapshot?.days || []).filter(
                    (d) => d.date || d.id,
                  );
                  const dayKey = (d) => d.date || d.id;
                  const dayLabel = (d) => {
                    if (d.label) return d.label;
                    if (d.date) {
                      const [y, m, dd] = d.date.split('-').map(Number);
                      const wd = wdShort[new Date(y, m - 1, dd).getDay()];
                      return tr('activities.date.dayNumMonth', {
                        wd,
                        day: String(dd),
                        month: String(m),
                      });
                    }
                    return tr('activities.day');
                  };
                  const allDayKeys = p24Days.map(dayKey);
                  // Saved value vs draft (saved with a dedicated button)
                  const savedOn = !!demoReg.standbyReserve;
                  const savedDays =
                    demoReg.standbyDays && demoReg.standbyDays.length
                      ? demoReg.standbyDays
                      : allDayKeys;
                  const draftOn = standbyDraftOn != null ? standbyDraftOn : savedOn;
                  const draftDays = standbyDraftDays != null ? standbyDraftDays : savedDays;
                  const sameDays = (a, b) => a.length === b.length && a.every((k) => b.includes(k));
                  const dirty = draftOn !== savedOn || (draftOn && !sameDays(draftDays, savedDays));
                  const setDraftOn = (on) => setStandbyDraftOn(on);
                  const toggleDraftDay = (key) => {
                    const cur = draftDays;
                    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
                    setStandbyDraftDays(next.length ? next : [key]); // at least one day
                  };
                  const saveStandby = () => {
                    mutate(selected.id, (a) => ({
                      ...a,
                      registrations: (a.registrations || []).map((r) =>
                        isMe(r)
                          ? {
                              ...r,
                              standbyReserve: draftOn,
                              standbyAt: draftOn ? r.standbyAt || Date.now() : null,
                              standbyDays: draftOn ? draftDays : null,
                            }
                          : r,
                      ),
                    }));
                    setStandbyDraftOn(null);
                    setStandbyDraftDays(null);
                  };
                  const cancelStandby = () => {
                    setStandbyDraftOn(null);
                    setStandbyDraftDays(null);
                  };
                  // Standby list in order (oldest standbyAt first) + max enforcement (based on the saved value)
                  const standbyList = (selected.registrations || [])
                    .filter((r) => r.standbyReserve)
                    .sort((x, y) => (x.standbyAt || 0) - (y.standbyAt || 0));
                  const maxStandby = selected.config?.maxStandbyReserves;
                  const myStandbyIdx = standbyList.findIndex(isMe);
                  const standbyFull =
                    maxStandby != null &&
                    maxStandby > 0 &&
                    myStandbyIdx === -1 &&
                    standbyList.length >= maxStandby;
                  const priceTxt = selected.config?.pricePerPlayer
                    ? ` · ${selected.config.pricePerPlayer} kr`
                    : '';
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm text-ink-1">
                          <span style={{ color: 'var(--lime-text)' }}>✓</span>{' '}
                          {tr('activities.youAre')}{' '}
                          {demoReg.standbyOnly ? (
                            <>
                              <strong style={{ color: '#3f6212' }}>
                                {tr('activities.standbyFree')}
                                {myStandbyIdx >= 0
                                  ? tr('activities.queueNumberSuffix', {
                                      n: String(myStandbyIdx + 1),
                                    })
                                  : ''}
                              </strong>
                            </>
                          ) : demoReg.status === 'waitlist' ? (
                            tr('activities.onWaitlistReserve')
                          ) : (
                            tr('activities.signedUpLower')
                          )}
                          {!demoReg.standbyOnly && savedOn ? (
                            <>
                              {' '}
                              ·{' '}
                              <strong style={{ color: '#3f6212' }}>
                                {tr('activities.standby')}
                                {myStandbyIdx >= 0
                                  ? tr('activities.queueNumberParen', {
                                      n: String(myStandbyIdx + 1),
                                    })
                                  : ''}
                              </strong>
                            </>
                          ) : (
                            ''
                          )}
                          {myPartnerId ? (
                            <>
                              {' '}
                              · {tr('activities.partnerLabel')}{' '}
                              <strong>{playerName(myPartnerId)}</strong>
                            </>
                          ) : (
                            ''
                          )}
                          {demoReg.status === 'waitlist' && !demoReg.standbyOnly ? priceTxt : ''}.
                        </div>
                        <div className="flex items-center gap-2">
                          {demoReg.standbyOnly && (
                            <button
                              onClick={() => {
                                const willWait = indWillWait;
                                const price = selected.config?.pricePerPlayer || 0;
                                if (price > 0)
                                  setPayIntent({
                                    kind: 'individual',
                                    amount: price,
                                    label: demoUser.name,
                                    reserve: willWait,
                                  });
                                else addPlayer(demoUser.id);
                              }}
                              className="text-sm px-3 py-2 rounded-lg border border-lime-deep text-lime-deep hover:bg-lime-bg/30 flex items-center gap-1.5"
                            >
                              <Plus size={13} />{' '}
                              {tr(
                                indWillWait ? 'activities.putMeOnWaitlist' : 'activities.signMeUp',
                              )}
                            </button>
                          )}
                          {myPartnerId && (
                            <button
                              onClick={() => {
                                setSelfSwap(true);
                                setSelfPairPicking(true);
                                setSelfPartnerSearch('');
                              }}
                              className="text-sm px-3 py-2 rounded-lg border border-line text-ink-2 hover:bg-bg-soft flex items-center gap-1.5"
                            >
                              <Shuffle size={13} /> {tr('activities.swapPartner')}
                            </button>
                          )}
                          <button
                            onClick={() => removePlayer(demoUser.id)}
                            className="text-sm px-3 py-2 rounded-lg border border-rose text-rose hover:bg-rose-soft/30"
                          >
                            {tr('activities.signYourselfOff')}
                          </button>
                        </div>
                      </div>
                      {demoReg.status === 'waitlist' && isPadel24 && (
                        <div
                          className="rounded-lg border p-3"
                          style={{
                            borderColor: draftOn ? '#84cc16' : 'var(--line-soft, #e5e7eb)',
                            background: draftOn ? '#f7fee7' : 'var(--bg-soft, #f8f9fa)',
                          }}
                        >
                          <label
                            className={`flex items-start gap-2 ${standbyFull ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              disabled={standbyFull}
                              checked={draftOn}
                              onChange={(e) => setDraftOn(e.target.checked)}
                              className="w-4 h-4 mt-0.5 flex-shrink-0"
                            />
                            <span className="text-sm text-ink-1 font-semibold">
                              {tr('activities.alsoStandby')}
                              {savedOn && myStandbyIdx >= 0
                                ? tr('activities.youAreNumberInQueue', {
                                    n: String(myStandbyIdx + 1),
                                  })
                                : ''}
                            </span>
                          </label>
                          <p className="text-[11px] text-ink-3 mt-1.5 ml-6">
                            {tr('activities.standbyExplain1')}
                            {selected.config?.pricePerPlayer
                              ? tr('activities.amountParen', {
                                  amount: String(selected.config.pricePerPlayer),
                                })
                              : ''}
                            {tr('activities.standbyExplain2')}{' '}
                            <strong>{tr('activities.not')}</strong>{' '}
                            {tr('activities.standbyExplain3')}{' '}
                            <strong>{tr('activities.refundedWord')}</strong>{' '}
                            {tr('activities.standbyExplain4')}{' '}
                            <strong>{tr('activities.standbyNearby')}</strong>
                            {tr('activities.standbyExplain5')}
                          </p>
                          {standbyFull && (
                            <p className="text-[11px] mt-1 ml-6" style={{ color: 'var(--danger)' }}>
                              {tr('activities.standbyListFull', {
                                count: String(standbyList.length),
                                max: String(maxStandby),
                              })}
                            </p>
                          )}
                          {draftOn && p24Days.length >= 2 && (
                            <div className="mt-2 ml-6">
                              <p className="text-[11px] font-semibold text-ink-2 mb-1">
                                {tr('activities.whichDays')}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {p24Days.map((d) => {
                                  const k = dayKey(d);
                                  const on = draftDays.includes(k);
                                  return (
                                    <label
                                      key={k}
                                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 cursor-pointer text-[12px] ${on ? 'border-lime-deep bg-lime-bg/30' : 'border-line hover:border-line-soft'}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={() => toggleDraftDay(k)}
                                        className="w-3.5 h-3.5"
                                      />
                                      {dayLabel(d)}
                                    </label>
                                  );
                                })}
                              </div>
                              <p className="text-[10px] text-ink-3 mt-1">
                                {tr('activities.standbyDaysNote')}
                              </p>
                            </div>
                          )}
                          {dirty && (
                            <div className="flex items-center gap-2 mt-2.5 ml-6">
                              <button
                                type="button"
                                onClick={saveStandby}
                                className="btn-lime text-xs"
                              >
                                <Check size={13} /> {tr('activities.saveChoice')}
                              </button>
                              <button
                                type="button"
                                onClick={cancelStandby}
                                className="text-xs px-3 py-1.5 rounded-lg border border-line text-ink-2 hover:bg-bg-soft"
                              >
                                {tr('activities.undoCapital')}
                              </button>
                              <span className="text-[10px] text-ink-3">
                                {tr('activities.unsavedChanges')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()
              )
            ) : !demoLevelOk ? (
              <div className="text-sm text-ink-2">
                {levelLabel && (
                  <div
                    className="mb-2 inline-block font-mono text-[10px] px-2 py-0.5 rounded"
                    style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
                  >
                    {tr('activities.levelRequirement', { label: levelLabel })}
                  </div>
                )}
                <p>
                  {tr('activities.yourLevelIs', {
                    level: demoLvl ? String(demoLvl.level) : '?',
                    name: demoLvl ? tr('activities.nameParen', { name: demoLvl.name }) : '',
                    relation: tr(
                      demoRelation === 'under'
                        ? 'activities.relationUnder'
                        : demoRelation === 'over'
                          ? 'activities.relationOver'
                          : 'activities.relationOutside',
                    ),
                  })}
                </p>
              </div>
            ) : (
              <div>
                {levelLabel && (
                  <div
                    className="mb-2 inline-block font-mono text-[10px] px-2 py-0.5 rounded"
                    style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
                  >
                    {tr('activities.levelRequirement', { label: levelLabel })}
                  </div>
                )}
                <p className="text-[12px] text-ink-3 mb-3">
                  {c.mix
                    ? mixSlotFull
                      ? tr('activities.genderSlotsFull', {
                          slots: tr(
                            demoGender === 'F'
                              ? 'activities.womensPlaces'
                              : 'activities.mensPlaces',
                          ),
                          gender: tr(demoGender === 'F' ? 'activities.aWoman' : 'activities.aMan'),
                        })
                      : tr('activities.readyMix', {
                          men: String(c.men.max - c.men.confirmed),
                          women: String(c.women.max - c.women.confirmed),
                        })
                    : c.max != null && c.confirmed >= c.max
                      ? tr('activities.fullGoesToWaitlist', {
                          waitlist:
                            c.waitlist > 0
                              ? tr('activities.waitlistNowSuffix', {
                                  count: String(c.waitlist),
                                })
                              : '',
                        })
                      : tr('activities.readyForSignup', {
                          free:
                            c.max != null
                              ? tr('activities.freePlacesSuffix', {
                                  count: String(Math.max(0, c.max - c.confirmed)),
                                })
                              : '',
                        })}
                  {selected.config?.pricePerPlayer
                    ? tr('activities.priceSentence', {
                        amount: String(selected.config.pricePerPlayer),
                      })
                    : ''}
                </p>
                {c.max != null && c.confirmed >= c.max && (
                  <p
                    className="text-[11px] mb-3 px-2.5 py-1.5 rounded-lg"
                    style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
                  >
                    {tr('activities.waitlistTip')}
                    {c.waitlist >= 4
                      ? tr(
                          Math.floor(c.waitlist / 4) === 1
                            ? 'activities.waitlistTipCountOne'
                            : 'activities.waitlistTipCountMany',
                          {
                            count: String(c.waitlist),
                            courts: String(Math.floor(c.waitlist / 4)),
                          },
                        )
                      : ''}
                  </p>
                )}
                {isKmActivity(selected) ? (
                  (() => {
                    const buckets = kmBucketsOf(selected);
                    const kmPairsIn = (bk) =>
                      (selected.registrations || []).filter(
                        (r) => r.status === 'confirmed' && r.bucketKey === bk,
                      ).length;
                    const kmFull = (bk) => {
                      const mx = kmBucketMax(selected, bk);
                      return mx != null && kmPairsIn(bk) >= mx;
                    };
                    // deviation (lint): `price` is unused in the KM branch (KM uses
                    // per-class `kmBucketPrice`); `_`-prefixed to satisfy no-unused-vars.
                    const _price = selected.config?.pricePerPlayer || 0;
                    if (!selfClub) {
                      const myG = genderOf(demoUser.id);
                      const canJoin = (b) => (b.catId === 'women' ? myG === 'F' : true); // Men open (women can play up), Mix open
                      return (
                        <div>
                          {(() => {
                            const myRegs = (selected.registrations || []).filter(
                              (r) => r.playerId === demoUser.id || r.partnerId === demoUser.id,
                            );
                            if (!myRegs.length) return null;
                            return (
                              <div className="mb-3 space-y-1.5">
                                <p className="text-sm text-ink-1 font-semibold">
                                  {tr('activities.yourSignups', {
                                    count: String(myRegs.length),
                                  })}
                                </p>
                                {myRegs.map((reg, i) => {
                                  const bk = kmBucketsOf(selected).find(
                                    (x) => x.bucketKey === reg.bucketKey,
                                  );
                                  const mate = reg.partnerId
                                    ? reg.playerId === demoUser.id
                                      ? reg.partnerId
                                      : reg.playerId
                                    : null;
                                  return (
                                    <div
                                      key={reg.bucketKey || i}
                                      className="flex items-center justify-between gap-2 p-2 rounded-lg border border-line-soft flex-wrap"
                                    >
                                      <div className="text-sm text-ink-1">
                                        <span style={{ color: 'var(--lime-text)' }}>✓</span>{' '}
                                        <strong>
                                          {bk?.label || tr('activities.classFallback')}
                                        </strong>
                                        {mate ? (
                                          <>
                                            {' '}
                                            · {tr('activities.partnerLabel')} {playerName(mate)}
                                          </>
                                        ) : (
                                          ''
                                        )}
                                        {reg.status === 'waitlist'
                                          ? tr('activities.waitlistDotSuffix')
                                          : ''}
                                      </div>
                                      <button
                                        onClick={() =>
                                          mutate(selected.id, (a) => ({
                                            ...a,
                                            registrations: (a.registrations || []).filter(
                                              (r) =>
                                                !(
                                                  r.bucketKey === reg.bucketKey &&
                                                  (r.playerId === demoUser.id ||
                                                    r.partnerId === demoUser.id)
                                                ),
                                            ),
                                          }))
                                        }
                                        className="text-xs px-2.5 py-1.5 rounded-lg border border-rose text-rose hover:bg-rose-soft/30"
                                      >
                                        {tr('activities.signYourselfOff')}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          <p className="text-sm text-ink-1 font-semibold mb-2">
                            {tr('activities.km.signUpInAClass')}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {buckets.map((b) => {
                              const isFull = kmFull(b.bucketKey);
                              const inCat = kmPlayerInCat(
                                selected,
                                demoUser.id,
                                b.catId,
                                b.bucketKey,
                              );
                              // Level gate per class — the player's level must fall within [levelMin, levelMax].
                              const bLvMin = b.levelMin ?? null;
                              const bLvMax = b.levelMax ?? null;
                              const bHasLvl = bLvMin != null || bLvMax != null;
                              const bLevelOk = !bHasLvl
                                ? true
                                : demoLvl == null
                                  ? false
                                  : bLvMin != null && demoLvl.level < bLvMin
                                    ? false
                                    : bLvMax != null && demoLvl.level > bLvMax
                                      ? false
                                      : true;
                              const bLvlLabel = bHasLvl
                                ? bLvMin != null && bLvMin === bLvMax
                                  ? tr('activities.km.levelExact', { level: String(bLvMin) })
                                  : tr('activities.km.levelRange', {
                                      min: String(bLvMin != null ? bLvMin : 1),
                                      max: String(bLvMax != null ? bLvMax : 9),
                                    })
                                : null;
                              const ok = canJoin(b) && !isFull && !inCat && bLevelOk;
                              const grayUp = b.catId === 'men' && myG === 'F'; // woman who can play up: gray, but clickable
                              const col =
                                isFull || grayUp || inCat || !bLevelOk
                                  ? '#9ca3af'
                                  : b.color || '#888';
                              const txt =
                                isFull || grayUp || inCat || !bLevelOk
                                  ? '#6b7280'
                                  : b.color || '#444';
                              const bMax = kmBucketMax(selected, b.bucketKey);
                              const bPrice = kmBucketPrice(selected, b.bucketKey);
                              return (
                                <span
                                  key={b.bucketKey}
                                  className="relative inline-flex"
                                  onMouseEnter={() => {
                                    if (grayUp && !isFull && !inCat) setKmTipKey(b.bucketKey);
                                  }}
                                  onMouseLeave={() =>
                                    setKmTipKey((k) => (k === b.bucketKey ? null : k))
                                  }
                                >
                                  <button
                                    disabled={!ok}
                                    onClick={() => {
                                      if (ok) setSelfClub(b.bucketKey);
                                    }}
                                    title={
                                      inCat
                                        ? tr('activities.km.alreadyInCategory')
                                        : isFull
                                          ? tr('activities.km.classFull')
                                          : !bLevelOk
                                            ? tr('activities.km.requiresLevel', {
                                                label: bLvlLabel,
                                              })
                                            : !canJoin(b)
                                              ? tr('activities.km.womenOnly')
                                              : ''
                                    }
                                    className={`text-sm px-3 py-2 rounded-lg border-2 ${ok ? 'hover:bg-bg-soft' : 'opacity-40 cursor-not-allowed'}`}
                                    style={{ borderColor: col, color: txt }}
                                  >
                                    {b.label}
                                    {bLvlLabel ? ` · ${bLvlLabel}` : ''}
                                    {bPrice > 0
                                      ? tr('activities.km.teamPriceSuffix', {
                                          amount: String(bPrice),
                                        })
                                      : ''}
                                    {inCat
                                      ? tr('activities.km.signedUpSuffix')
                                      : isFull
                                        ? tr('activities.km.fullSuffix')
                                        : bMax != null
                                          ? ` ${kmPairsIn(b.bucketKey)}/${bMax}`
                                          : ''}
                                  </button>
                                  {grayUp && kmTipKey === b.bucketKey && (
                                    <span
                                      className="absolute z-20 left-0 top-full mt-1.5 w-60 p-2.5 rounded-lg shadow-lg text-[11px] leading-snug"
                                      style={{ background: '#1f2937', color: '#f9fafb' }}
                                    >
                                      {tr('activities.km.playUpTipPre')}{' '}
                                      <strong>{tr('activities.km.openToAll')}</strong>
                                      {tr('activities.km.playUpTipMid')}{' '}
                                      <strong>{tr('activities.km.womenClass')}</strong>.
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                          <p className="text-[11px] text-ink-3 mt-2">
                            {tr('activities.km.pairsNote')}
                          </p>
                        </div>
                      );
                    }
                    const bucket = buckets.find((b) => b.bucketKey === selfClub);
                    const womanUpWarn = bucket?.catId === 'men' && genderOf(demoUser.id) === 'F';
                    return (
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-sm text-ink-1 font-semibold">
                            {tr('activities.km.pickPartnerFor', {
                              label: bucket?.label,
                            })}{' '}
                            <span className="font-normal text-ink-3">({kmRuleText(bucket)})</span>
                          </p>
                          <button
                            onClick={() => {
                              setSelfClub(null);
                              setSelfPartnerSearch('');
                            }}
                            className="text-xs text-ink-3"
                          >
                            {tr('activities.km.switchClass')}
                          </button>
                        </div>
                        {womanUpWarn && (
                          <div
                            className="mb-2 p-2 rounded-lg border text-[12px]"
                            style={{
                              borderColor: '#f59e0b',
                              background: '#fffbeb',
                              color: '#92400e',
                            }}
                          >
                            {tr('activities.km.womanUpWarnPre')}{' '}
                            <strong>{tr('activities.km.mensClass')}</strong>
                            {tr('activities.km.womanUpWarnPost')}
                          </div>
                        )}
                        {renderPartnerPicker(
                          demoUser.id,
                          (pid) => {
                            const teamPrice = kmBucketPrice(selected, selfClub);
                            if (teamPrice > 0)
                              setPayIntent({
                                kind: 'pair',
                                partnerId: pid,
                                amount: teamPrice,
                                label: `${demoUser.name} & ${playerName(pid)}`,
                                bucketKey: selfClub,
                              });
                            else {
                              addTeam(demoUser.id, pid, null, null, selfClub);
                              setSelfClub(null);
                              setSelfPartnerSearch('');
                            }
                          },
                          (p) =>
                            kmGenderOk(demoUser.id, p.id, bucket) &&
                            !kmPlayerInCat(selected, p.id, bucket?.catId, selfClub),
                          bucket?.catId === 'women'
                            ? 'F'
                            : bucket?.catId === 'mix'
                              ? genderOf(demoUser.id) === 'F'
                                ? 'M'
                                : 'F'
                              : null,
                        )}
                      </div>
                    );
                  })()
                ) : isClubTeamActivity(selected) ? (
                  (() => {
                    const tMode = selected.config?.teamSignupMode || 'both';
                    const price = selected.config?.pricePerPlayer || 0;
                    if (!selfClub) {
                      return (
                        <div>
                          <p className="text-sm text-ink-1 font-semibold mb-2">
                            {tr('activities.clubTeam.pickYourClub')}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {c.clubs.map((cl) => (
                              <button
                                key={cl.id}
                                onClick={() => setSelfClub(cl.id)}
                                className="text-sm px-3 py-2 rounded-lg border-2 hover:bg-bg-soft"
                                style={{
                                  borderColor: cl.id === 'clubA' ? '#65a30d' : '#4f46e5',
                                  color: cl.id === 'clubA' ? '#3f6212' : '#3730a3',
                                }}
                              >
                                {cl.name} ·{' '}
                                {tr('activities.clubTeam.availableCount', {
                                  count: String(Math.max(0, cl.max - cl.confirmed)),
                                })}
                              </button>
                            ))}
                          </div>
                          <p className="text-[11px] text-ink-3 mt-2">
                            {tr('activities.clubTeam.signupNote')}
                          </p>
                        </div>
                      );
                    }
                    const myClub = c.clubs.find((x) => x.id === selfClub);
                    if (selfPairPicking) {
                      return (
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="text-sm text-ink-1 font-semibold">
                              {tr('activities.clubTeam.pickPartnerIn', {
                                club: myClub?.name,
                              })}
                            </p>
                            <button
                              onClick={() => {
                                setSelfPairPicking(false);
                                setSelfPartnerSearch('');
                              }}
                              className="text-xs text-ink-3"
                            >
                              {tr('home.common.cancel')}
                            </button>
                          </div>
                          {renderPartnerPicker(demoUser.id, (pid) => {
                            if (price > 0)
                              setPayIntent({
                                kind: 'pair',
                                partnerId: pid,
                                amount: price * 2,
                                label: `${demoUser.name} & ${playerName(pid)}`,
                                clubId: selfClub,
                              });
                            else {
                              addTeam(demoUser.id, pid, null, selfClub);
                              setSelfPairPicking(false);
                              setSelfPartnerSearch('');
                              setSelfClub(null);
                            }
                          })}
                        </div>
                      );
                    }
                    return (
                      <>
                        <p className="text-[12px] text-ink-2 mb-2">
                          {tr('activities.clubTeam.youRepresent')} <strong>{myClub?.name}</strong>.{' '}
                          <button
                            onClick={() => setSelfClub(null)}
                            className="underline text-ink-3"
                          >
                            {tr('activities.clubTeam.switchClub')}
                          </button>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(tMode === 'pairs' || tMode === 'both') && (
                            <button
                              onClick={() => setSelfPairPicking(true)}
                              className="btn-lime text-sm"
                            >
                              <Plus size={14} /> {tr('activities.signUpWithPartner')}
                              {price
                                ? tr('activities.priceSuffix', { amount: String(price * 2) })
                                : ''}
                            </button>
                          )}
                          {(tMode === 'singles' || tMode === 'both') && (
                            <button
                              onClick={() => {
                                if (price > 0)
                                  setPayIntent({
                                    kind: 'single',
                                    amount: price,
                                    label: demoUser.name,
                                    clubId: selfClub,
                                  });
                                else {
                                  addSingle(demoUser.id, null, selfClub);
                                  setSelfClub(null);
                                }
                              }}
                              className={
                                tMode === 'both'
                                  ? 'text-sm px-3 py-2 rounded-lg border border-lime-deep text-lime-deep hover:bg-lime-bg/30'
                                  : 'btn-lime text-sm'
                              }
                            >
                              <Plus size={14} /> {tr('activities.signUpAlone')}
                            </button>
                          )}
                        </div>
                        {tMode !== 'pairs' && (
                          <p className="text-[11px] text-ink-3 mt-2">
                            {tr('activities.clubTeam.aloneNote', { club: myClub?.name })}
                          </p>
                        )}
                      </>
                    );
                  })()
                ) : isTeamActivity(selected) ? (
                  (() => {
                    const tMode = selected.config?.teamSignupMode || 'both';
                    if (selfPairPicking) {
                      return (
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="text-sm text-ink-1 font-semibold">
                              {tr('activities.pickYourPartner')}
                            </p>
                            <button
                              onClick={() => {
                                setSelfPairPicking(false);
                                setSelfPartnerSearch('');
                              }}
                              className="text-xs text-ink-3"
                            >
                              {tr('home.common.cancel')}
                            </button>
                          </div>
                          {renderPartnerPicker(demoUser.id, (pid) => {
                            const price = selected.config?.pricePerPlayer || 0;
                            const willWait = c.max != null && c.confirmed >= c.max;
                            if (price > 0) {
                              setPayIntent({
                                kind: 'pair',
                                partnerId: pid,
                                amount: pairPriceOf(selected),
                                label: `${demoUser.name} & ${playerName(pid)}`,
                                reserve: willWait,
                              });
                            } else {
                              addTeam(demoUser.id, pid);
                              setSelfPairPicking(false);
                              setSelfPartnerSearch('');
                            }
                          })}
                        </div>
                      );
                    }
                    return (() => {
                      const teamWait = c.max != null && c.confirmed >= c.max;
                      return (
                        <>
                          <div className="flex flex-wrap gap-2">
                            {(tMode === 'pairs' || tMode === 'both') && (
                              <button
                                disabled={waitlistFull}
                                onClick={() => {
                                  if (!waitlistFull) setSelfPairPicking(true);
                                }}
                                className="btn-lime text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <Plus size={14} />{' '}
                                {tr(
                                  waitlistFull
                                    ? 'activities.waitlistFull'
                                    : teamWait
                                      ? 'activities.waitlistWithPartner'
                                      : 'activities.signUpWithPartnerSelf',
                                )}
                                {!waitlistFull && selected.config?.pricePerPlayer
                                  ? tr('activities.priceSuffix', {
                                      amount: String(pairPriceOf(selected)),
                                    })
                                  : ''}
                              </button>
                            )}
                            {(tMode === 'singles' || tMode === 'both') && (
                              <button
                                disabled={waitlistFull}
                                onClick={() => {
                                  if (waitlistFull) return;
                                  const price = selected.config?.pricePerPlayer || 0;
                                  const willWait = c.max != null && c.confirmed >= c.max;
                                  if (price > 0)
                                    setPayIntent({
                                      kind: 'single',
                                      amount: price,
                                      label: demoUser.name,
                                      reserve: willWait,
                                    });
                                  else addSingle(demoUser.id);
                                }}
                                className={
                                  tMode === 'both'
                                    ? 'text-sm px-3 py-2 rounded-lg border border-lime-deep text-lime-deep hover:bg-lime-bg/30 disabled:opacity-40 disabled:cursor-not-allowed'
                                    : 'btn-lime text-sm disabled:opacity-40 disabled:cursor-not-allowed'
                                }
                              >
                                <Plus size={14} />{' '}
                                {tr(
                                  waitlistFull
                                    ? 'activities.waitlistFull'
                                    : teamWait
                                      ? 'activities.waitlistAlone'
                                      : 'activities.signUpAloneSelf',
                                )}
                              </button>
                            )}
                          </div>
                          {waitlistFull && (
                            <p className="text-[11px] text-ink-3 mt-2">
                              {tr('activities.bothFull')}
                            </p>
                          )}
                          {!waitlistFull && teamWait && (
                            <p className="text-[11px] text-ink-3 mt-2">
                              {tr('activities.fullWaitlisted')}
                            </p>
                          )}
                          {tMode !== 'pairs' && (
                            <p className="text-[11px] text-ink-3 mt-2">
                              {tr('activities.aloneNote')}
                            </p>
                          )}
                        </>
                      );
                    })();
                  })()
                ) : isClubActivity(selected) ? (
                  <div>
                    <p className="text-sm text-ink-1 font-semibold mb-2">
                      {tr('activities.clubTeam.pickYourClub')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {c.clubs.map((cl) => {
                        const free = Math.max(0, cl.max - cl.confirmed);
                        const willWait = free <= 0;
                        const price = selected.config?.pricePerPlayer || 0;
                        return (
                          <button
                            key={cl.id}
                            onClick={() => {
                              if (price > 0)
                                setPayIntent({
                                  kind: 'individual',
                                  amount: price,
                                  label: `${demoUser.name} · ${cl.name}`,
                                  clubId: cl.id,
                                  reserve: willWait,
                                });
                              else addPlayer(demoUser.id, null, cl.id);
                            }}
                            className="text-sm px-3 py-2 rounded-lg border-2 hover:bg-bg-soft"
                            style={{
                              borderColor: cl.id === 'clubA' ? '#65a30d' : '#4f46e5',
                              color: cl.id === 'clubA' ? '#3f6212' : '#3730a3',
                            }}
                          >
                            <Plus size={14} className="inline" /> {cl.name}
                            {willWait
                              ? tr('activities.waitlistParen')
                              : tr(
                                  free === 1
                                    ? 'activities.availableSuffixOne'
                                    : 'activities.availableSuffixMany',
                                  { count: String(free) },
                                )}
                            {price > 0
                              ? tr('activities.priceSuffix', { amount: String(price) })
                              : ''}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-ink-3 mt-2">{tr('activities.club.evenNote')}</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      disabled={waitlistFull}
                      onClick={() => {
                        if (waitlistFull) return;
                        const willWait = indWillWait;
                        const price = selected.config?.pricePerPlayer || 0;
                        if (isPadel24 && willWait) {
                          const days = (selected.config?.p24Snapshot?.days || [])
                            .filter((d) => d.date || d.id)
                            .map((d) => d.date || d.id);
                          setStandbyPrompt({
                            amount: price,
                            label: demoUser.name,
                            days,
                            on: false,
                          });
                        } else if (price > 0 && !matchBookerPaid)
                          setPayIntent({
                            kind: 'individual',
                            amount: price,
                            label: demoUser.name,
                            reserve: willWait,
                          });
                        else addPlayer(demoUser.id);
                      }}
                      className="btn-lime text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {waitlistFull ? (
                        reservesAllowed ? (
                          <>{tr('activities.waitlistFull')}</>
                        ) : (
                          <>{tr('activities.signupFull')}</>
                        )
                      ) : indWillWait ? (
                        selected.config?.pricePerPlayer ? (
                          <>
                            <Plus size={14} />{' '}
                            {tr('activities.waitlistAndPay', {
                              amount: String(selected.config.pricePerPlayer),
                            })}
                          </>
                        ) : (
                          <>
                            <Plus size={14} /> {tr('activities.putMeOnWaitlist')}
                          </>
                        )
                      ) : selected.config?.pricePerPlayer ? (
                        <>
                          {tr('activities.signUpAndPay', {
                            amount: String(selected.config.pricePerPlayer),
                          })}
                        </>
                      ) : (
                        <>
                          <Plus size={14} /> {tr('activities.signMeUp')}
                        </>
                      )}
                    </button>
                    {isPadel24 &&
                      (() => {
                        const standbyCount = (selected.registrations || []).filter(
                          (r) => r.standbyReserve,
                        ).length;
                        const maxStandby = selected.config?.maxStandbyReserves;
                        const standbyFull =
                          maxStandby != null && maxStandby > 0 && standbyCount >= maxStandby;
                        return (
                          <button
                            disabled={standbyFull}
                            onClick={() => {
                              const days = (selected.config?.p24Snapshot?.days || [])
                                .filter((d) => d.date || d.id)
                                .map((d) => d.date || d.id);
                              setStandbyPrompt({
                                standbyOnly: true,
                                label: demoUser.name,
                                days,
                                on: true,
                              });
                            }}
                            title={
                              standbyFull
                                ? tr('activities.standbyListIsFull')
                                : tr('activities.standbyOnlyTitle')
                            }
                            className="text-sm px-3 py-2 rounded-lg border border-line text-ink-2 hover:bg-bg-soft disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {tr('activities.standbyOnlyButton')}
                          </button>
                        );
                      })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Fake Vipps/card payment at sign-up (same as the court booking) */}
        {payIntent && demoUser && (
          <BookingPaymentModal
            amount={payIntent.amount}
            bookingSummary={tr('activities.paymentSummary', {
              name: selected.config?.name || tr('activities.activityFallback'),
              label: payIntent.label,
              reserve: payIntent.reserve ? tr('activities.reservePlaceSuffix') : '',
            })}
            onCancel={() => setPayIntent(null)}
            onSuccess={(paymentInfo) => {
              if (payIntent.kind === 'pair') {
                addTeam(
                  demoUser.id,
                  payIntent.partnerId,
                  paymentInfo,
                  payIntent.clubId || null,
                  payIntent.bucketKey || null,
                );
                setSelfPairPicking(false);
                setSelfPartnerSearch('');
                setSelfClub(null);
              } else if (payIntent.kind === 'single') {
                addSingle(demoUser.id, paymentInfo, payIntent.clubId || null);
                setSelfClub(null);
              } else
                addPlayer(
                  demoUser.id,
                  paymentInfo,
                  payIntent.clubId || null,
                  false,
                  payIntent.standby || null,
                );
              setPayIntent(null);
            }}
          />
        )}

        {/* Single-match activity: result entry → ELO via the single-match pipeline.
            Pre-seeded with the four confirmed players (1&2 vs 3&4) + the chosen
            ELO weight; the user just enters the best-of-3 score. */}
        {isSingleMatch &&
          singleMatchResultOpen &&
          onCompleteSingleMatchActivity &&
          confirmed.length >= 4 && (
            <AddSingleMatchModal
              allPlayers={effectivePlayerDB}
              initialMatch={{
                team1: { p1Id: confirmed[0].playerId, p2Id: confirmed[1].playerId },
                team2: { p1Id: confirmed[2].playerId, p2Id: confirmed[3].playerId },
                eloWeightPct:
                  selected.config?.eloWeightPct ??
                  Math.round((selected.config?.kMultiplier ?? 2) * 100),
                sets: [],
                playedAt: Date.now(),
              }}
              title={tr('activities.recordResult')}
              submitLabel={tr('activities.recordResult')}
              onPersistNewPlayer={onPersistNewPlayer}
              onClose={() => setSingleMatchResultOpen(false)}
              onSubmit={async (data) => {
                const r = await onCompleteSingleMatchActivity(selected, data);
                if (r?.success) setSingleMatchResultOpen(false);
                else
                  alert(
                    tr('activities.errorPrefix', {
                      message: r?.error || tr('activities.unknownError'),
                    }),
                  );
              }}
            />
          )}

        {/* Padel24 reserve: standby choice BEFORE payment */}
        {standbyPrompt &&
          demoUser &&
          (() => {
            const p24Days = (selected.config?.p24Snapshot?.days || []).filter(
              (d) => d.date || d.id,
            );
            const dayKey = (d) => d.date || d.id;
            const dayLabel = (d) => {
              if (d.label) return d.label;
              if (d.date) {
                const [y, m, dd] = d.date.split('-').map(Number);
                const wd = wdShort[new Date(y, m - 1, dd).getDay()];
                return tr('activities.date.dayNumMonth', { wd, day: String(dd), month: String(m) });
              }
              return tr('activities.day');
            };
            const allDayKeys = p24Days.map(dayKey);
            const days =
              standbyPrompt.days && standbyPrompt.days.length ? standbyPrompt.days : allDayKeys;
            const standbyCount = (selected.registrations || []).filter(
              (r) => r.standbyReserve,
            ).length;
            const maxStandby = selected.config?.maxStandbyReserves;
            const standbyFull = maxStandby != null && maxStandby > 0 && standbyCount >= maxStandby;
            const proceed = () => {
              if (standbyPrompt.standbyOnly) {
                addStandbyOnly(demoUser.id, days);
                setStandbyPrompt(null);
                return;
              }
              const standby = standbyPrompt.on ? { on: true, days } : null;
              if (standbyPrompt.amount > 0)
                setPayIntent({
                  kind: 'individual',
                  amount: standbyPrompt.amount,
                  label: standbyPrompt.label,
                  reserve: true,
                  standby,
                });
              else addPlayer(demoUser.id, null, null, false, standby);
              setStandbyPrompt(null);
            };
            const showDays = (standbyPrompt.on || standbyPrompt.standbyOnly) && p24Days.length >= 2;
            return (
              // A `Dialog` (#900): named by its title, described by its lead,
              // Tab kept inside, Escape cancels as the backdrop did, and focus
              // goes back to the sign-up button that opened it. Classes and the
              // inline backdrop colour moved across unchanged.
              <Dialog
                labelledBy={dlg.standbyTitle}
                describedBy={dlg.standbyBody}
                onClose={() => setStandbyPrompt(null)}
                showClose={false}
                backdropClassName="fixed inset-0 z-[60] flex items-center justify-center p-4"
                backdropStyle={{ background: 'rgba(0,0,0,0.4)' }}
                className="bg-white rounded-2xl p-5 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
                style={{ maxHeight: 'calc(100dvh - 2rem)' }}
              >
                <h3 id={dlg.standbyTitle} className="font-bold text-ink text-lg mb-1">
                  {tr(
                    standbyPrompt.standbyOnly
                      ? 'activities.becomeStandbyFree'
                      : 'activities.youGoOnWaitlist',
                  )}
                </h3>
                <p id={dlg.standbyBody} className="text-sm text-ink-2 mb-3">
                  {standbyPrompt.standbyOnly ? (
                    tr('activities.standbyOnlyExplain')
                  ) : (
                    <>
                      {tr('activities.fullSoReserve')}
                      {standbyPrompt.amount > 0
                        ? tr('activities.reservePaymentNote', {
                            amount: String(standbyPrompt.amount),
                          })
                        : ''}
                    </>
                  )}
                </p>
                <div
                  className="rounded-lg border p-3"
                  style={{
                    borderColor:
                      standbyPrompt.on || standbyPrompt.standbyOnly
                        ? '#84cc16'
                        : 'var(--line-soft, #e5e7eb)',
                    background:
                      standbyPrompt.on || standbyPrompt.standbyOnly
                        ? '#f7fee7'
                        : 'var(--bg-soft, #f8f9fa)',
                  }}
                >
                  {standbyPrompt.standbyOnly ? (
                    <p className="text-[12px] text-ink-1 font-semibold flex items-center gap-1.5">
                      <Check size={14} style={{ color: '#3f6212' }} />{' '}
                      {tr('activities.standbyOnlyButton')}
                    </p>
                  ) : (
                    <label
                      className={`flex items-start gap-2 ${standbyFull ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        disabled={standbyFull}
                        checked={standbyPrompt.on}
                        onChange={(e) => setStandbyPrompt((s) => ({ ...s, on: e.target.checked }))}
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-ink-1 font-semibold">
                        {tr('activities.alsoStandby')}
                      </span>
                    </label>
                  )}
                  <p className="text-[11px] text-ink-3 mt-1.5 ml-6">
                    {tr(
                      standbyPrompt.standbyOnly
                        ? 'activities.standbyReservesWord'
                        : 'activities.standbyPromptExplain',
                    )}{' '}
                    {tr('activities.mustBe')} <strong>{tr('activities.standbyNearby')}</strong>.
                  </p>
                  <p className="text-[11px] text-ink-3 mt-1.5 ml-6">
                    {tr('activities.standbyCallOrderPre')}{' '}
                    <strong>{tr('activities.closestInLevel')}</strong>{' '}
                    {tr('activities.standbyCallOrderPost')}
                  </p>
                  {standbyFull && (
                    <p className="text-[11px] mt-1 ml-6" style={{ color: 'var(--danger)' }}>
                      {tr('activities.standbyListFull', {
                        count: String(standbyCount),
                        max: String(maxStandby),
                      })}
                    </p>
                  )}
                  {showDays && (
                    <div className="mt-2 ml-6" role="group" aria-labelledby={dlg.standbyDays}>
                      <p id={dlg.standbyDays} className="text-[11px] font-semibold text-ink-2 mb-1">
                        {tr('activities.whichDays')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {p24Days.map((d) => {
                          const k = dayKey(d);
                          const on = days.includes(k);
                          return (
                            <label
                              key={k}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 cursor-pointer text-[12px] ${on ? 'border-lime-deep bg-lime-bg/30' : 'border-line hover:border-line-soft'}`}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() =>
                                  setStandbyPrompt((s) => {
                                    const cur = s.days && s.days.length ? s.days : allDayKeys;
                                    const next = cur.includes(k)
                                      ? cur.filter((x) => x !== k)
                                      : [...cur, k];
                                    return { ...s, days: next.length ? next : [k] };
                                  })
                                }
                                className="w-3.5 h-3.5"
                              />
                              {dayLabel(d)}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setStandbyPrompt(null)}
                    className="text-sm px-4 py-2 rounded-lg border border-line text-ink-2 hover:bg-bg-soft"
                  >
                    {tr('home.common.cancel')}
                  </button>
                  <button type="button" onClick={proceed} className="btn-lime text-sm">
                    {standbyPrompt.standbyOnly ? (
                      <>{tr('activities.signMeUpAsStandby')}</>
                    ) : standbyPrompt.amount > 0 ? (
                      <>{tr('activities.continueToPayment')}</>
                    ) : (
                      <>{tr('activities.putMeOnWaitlistArrow')}</>
                    )}
                  </button>
                </div>
              </Dialog>
            );
          })()}

        {npModalCtx && (
          <PlayerEditModal
            mode="create"
            initialName={npModalCtx.initialName || ''}
            existingPlayers={effectivePlayerDB}
            lockGender={npModalCtx.lockGender || null}
            onUseExisting={(p) => {
              npModalCtx.onPick(p.id);
              setNpModalCtx(null);
            }}
            onClose={() => setNpModalCtx(null)}
            onSave={async ({ name, firstName, lastName, email, gender, elo }) => {
              const id = `c_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
              const eloN = Number(elo) || 1500;
              const player = {
                id,
                name,
                firstName,
                lastName,
                email: email || undefined,
                isGhost: !email,
                gender,
                elo: eloN,
                level: Math.min(9, Math.max(1, Math.floor((eloN - 500) / 300) + 1)),
                isSystem: false,
                isFavorite: false,
                createdAt: Date.now(),
                matchesPlayed: 0,
                tournamentsPlayed: 0,
                lastPlayed: 0,
                lastMatchAt: null,
              };
              if (onPersistNewPlayer) await onPersistNewPlayer(player);
              npModalCtx.onPick(id);
              setNpModalCtx(null);
            }}
          />
        )}

        {/* ADMIN: Setup + Court time */}
        {adminMode && (
          <>
            <div className="card-elev p-4 mb-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-semibold text-ink-1">
                  {tr('activities.setup')}
                </h3>
                {!editing ? (
                  <button
                    onClick={() => onEditFullSetup?.(selected)}
                    className="btn-outline text-xs"
                  >
                    <Edit2 size={12} /> {tr('activities.tuneSetup')}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(false)} className="btn-outline text-xs">
                      {tr('home.common.cancel')}
                    </button>
                    <button onClick={saveEdit} className="btn-lime text-xs">
                      <Save size={12} /> {tr('activities.save')}
                    </button>
                  </div>
                )}
              </div>
              {editing && editDraft && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                      {tr('activities.nameLabel')}
                    </label>
                    <input
                      type="text"
                      className="input w-full text-sm"
                      value={editDraft.name}
                      onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                      {tr('activities.maxPlayersLabel')}
                    </label>
                    <input
                      type="number"
                      min="4"
                      className="input w-full text-sm"
                      value={editDraft.maxPlayers}
                      onChange={(e) => setEditDraft((d) => ({ ...d, maxPlayers: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                      {tr('activities.pricePerPlayerLabel')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="input w-full text-sm"
                      value={editDraft.pricePerPlayer}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, pricePerPlayer: e.target.value }))
                      }
                    />
                  </div>
                  {!isPadel24 && (
                    <>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                          {tr('activities.dateLabel')}
                        </label>
                        <input
                          type="date"
                          className="input w-full text-sm"
                          value={editDraft.activityDate}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, activityDate: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                          {tr('activities.fromLabel')}
                        </label>
                        <input
                          type="time"
                          className="input w-full text-sm"
                          value={editDraft.activityTime}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, activityTime: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                          {tr('activities.toLabel')}
                        </label>
                        <input
                          type="time"
                          className="input w-full text-sm"
                          value={editDraft.activityEndTime}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, activityEndTime: e.target.value }))
                          }
                        />
                      </div>
                    </>
                  )}
                  {isPadel24 && (
                    <div className="col-span-2">
                      <p className="text-[11px] text-ink-3">{tr('activities.padel24SetupNote')}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                      {tr('activities.deregisterCutoffLabel')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="input w-full text-sm"
                      value={editDraft.deregisterCutoffHours}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, deregisterCutoffHours: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
                      {tr('activities.signupCloseHoursLabel')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="input w-full text-sm"
                      value={editDraft.signupCloseHours}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, signupCloseHours: e.target.value }))
                      }
                    />
                    <p className="text-[10px] text-ink-3 mt-1">
                      {tr('activities.signupCloseHoursHelp')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Court time (court booking) — not for KM/Padel24 (court time is managed by the setup) */}
            {!isKmActivity(selected) && !isPadel24 && (
              <div className="card-elev p-4 mb-4">
                <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
                  {tr('activities.courtTime')}
                </h3>
                {(() => {
                  const reserved = (bookings || []).filter((b) => b.seriesId === selected.id);
                  if (reserved.length > 0) {
                    return (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm text-ink-2">
                          <span style={{ color: 'var(--lime-text)' }}>✓</span>{' '}
                          {tr(
                            reserved.length === 1
                              ? 'activities.courtsReservedInCalendarOne'
                              : 'activities.courtsReservedInCalendarMany',
                            { count: String(reserved.length), when: fmtWhen(selected) },
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!selected.config?.noCourts && (
                            <button
                              onClick={() => onOpenCourtReservation?.(selected.id)}
                              className="btn-outline text-sm"
                            >
                              <Calendar size={14} /> {tr('activities.editInCalendar')}
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              setBusy(true);
                              await onReleaseCourts?.(selected);
                              setBusy(false);
                            }}
                            disabled={busy}
                            className="text-sm px-3 py-2 rounded-lg border border-rose text-rose hover:bg-rose-soft/30 disabled:opacity-50"
                          >
                            {tr('activities.release')}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return selected.config?.noCourts ? (
                    <div className="text-[12px] text-ink-2">
                      <p className="mb-1">{tr('activities.noCourtsUsed')}</p>
                      {(selected.config?.activityDate || selected.config?.activityTime) && (
                        <p className="font-mono text-ink-1">
                          {selected.config.activityDate || ''}
                          {selected.config.activityTime
                            ? ` · ${selected.config.activityTime}–${selected.config.activityEndTime || ''}`
                            : ''}
                        </p>
                      )}
                      {selected.config?.location && (
                        <p className="text-ink-1 mt-0.5">📍 {selected.config.location}</p>
                      )}
                      {(selected.config?.address || selected.config?.poststed) && (
                        <p className="text-ink-2 mt-0.5">
                          {[
                            selected.config.address,
                            [selected.config.postnr, selected.config.poststed]
                              .filter(Boolean)
                              .join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-[12px] text-ink-3 mb-2">
                        {tr('activities.pickCourtsVisually')}
                      </p>
                      <button
                        onClick={() => onOpenCourtReservation?.(selected.id)}
                        className="btn-lime text-sm"
                      >
                        <Calendar size={14} /> {tr('activities.reserveCourtsInCalendar')}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {/* Add player (left) + Confirmed (right) — two columns on wide screens */}
        <div className={adminMode ? 'grid lg:grid-cols-2 gap-4 items-start mb-4' : 'mb-4'}>
          {/* Add manually — admin only */}
          {adminMode && (
            <div className="card-elev p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="font-display text-base font-semibold text-ink-1">
                  {tr(
                    isTeamActivity(selected)
                      ? teamMode_ === 'singles'
                        ? 'activities.addSingle'
                        : teamMode_ === 'pairs'
                          ? 'activities.addPair'
                          : 'activities.add'
                      : 'activities.addPlayerManually',
                  )}
                </h3>
                {levelLabel && (
                  <span
                    className="font-mono text-[10px] px-2 py-0.5 rounded"
                    style={{ background: 'var(--lime-bg)', color: 'var(--lime-text)' }}
                  >
                    {levelLabel}
                  </span>
                )}
              </div>
              {!isMixActivity(selected) &&
                !isClubActivity(selected) &&
                !isClubTeamActivity(selected) &&
                !isKmActivity(selected) && (
                  <input
                    type="text"
                    className="input w-full text-sm mb-2"
                    placeholder={tr('activities.searchNameOrBrowse')}
                    aria-label={tr('activities.searchNameOrBrowse')}
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                  />
                )}
              {(() => {
                const tTeam = isTeamActivity(selected);
                const tMode = selected.config?.teamSignupMode || 'both';
                const fillPairs = (tTeam && tMode !== 'singles') || isKmActivity(selected);
                const noun = tr(
                  fillPairs
                    ? 'activities.nounPairs'
                    : tTeam
                      ? 'activities.nounSingles'
                      : 'activities.nounPlayers',
                );
                return (
                  <div
                    className="flex items-center gap-2 mb-3 p-2 rounded-lg flex-wrap"
                    style={{ background: '#fef9c3', border: '1px dashed #ca8a04' }}
                  >
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: '#a16207', color: '#fff' }}
                    >
                      DEMO
                    </span>
                    <span className="text-[12px] text-ink-2">
                      {tr('activities.fillWithRandom', { noun })}
                      {hasLevelReq ? tr('activities.withinLevelSuffix') : ''}:
                    </span>
                    {[5, 10, 20].map((n) => (
                      <button
                        key={n}
                        onClick={() => demoFill(fillPairs ? n * 2 : n)}
                        disabled={demoPoolCount === 0 || (fillPairs && demoPoolCount < 2)}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg border disabled:opacity-40"
                        style={{ borderColor: '#ca8a04', color: '#854d0e', background: '#fff' }}
                      >
                        +{n}
                      </button>
                    ))}
                    <span className="text-[10px] text-ink-3">
                      {fillPairs
                        ? tr('activities.pairsCount', {
                            count: String(Math.floor(demoPoolCount / 2)),
                          })
                        : `${demoPoolCount}`}{' '}
                      {tr('activities.available')}
                      {hasLevelReq ? tr('activities.inLevelSuffix') : ''}
                    </span>
                  </div>
                );
              })()}
              {pendingOverride &&
                (() => {
                  const oElo = playerElo(pendingOverride.id);
                  const oLvl = oElo != null ? getEloLevel(oElo) : null;
                  const rel = oLvl
                    ? oLvl.level < (lvMin != null ? lvMin : 1)
                      ? 'under'
                      : 'over'
                    : null;
                  return (
                    <div
                      className="mb-2 p-3 rounded-lg border flex flex-col gap-2"
                      style={{
                        borderColor: 'var(--rose)',
                        background: 'var(--rose-soft, #fff1f2)',
                      }}
                    >
                      <span className="text-[12px] text-ink-1">
                        ⚠ <strong>{pendingOverride.name}</strong>
                        {oElo != null
                          ? tr('activities.levelEloParen', {
                              level: String(oLvl.level),
                              elo: String(oElo),
                            })
                          : tr('activities.unknownLevelParen')}{' '}
                        {tr('activities.outsideLevelAsk', {
                          relation: tr(
                            rel === 'under'
                              ? 'activities.relationUnder'
                              : rel === 'over'
                                ? 'activities.relationOver'
                                : 'activities.relationOutside',
                          ),
                          label: levelLabel ? tr('activities.nameParen', { name: levelLabel }) : '',
                        })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            addPlayer(
                              pendingOverride.id,
                              null,
                              isClubActivity(selected) ? clubAddFor : null,
                            );
                            setPendingOverride(null);
                          }}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                          style={{ background: 'var(--rose)' }}
                        >
                          {tr('activities.addAnyway')}
                        </button>
                        <button
                          onClick={() => setPendingOverride(null)}
                          className="text-xs px-3 py-1.5 rounded-lg text-ink-3"
                        >
                          {tr('home.common.cancel')}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              {pendingQuotaForce && (
                <div
                  className="mb-2 p-3 rounded-lg border flex flex-col gap-2"
                  style={{ borderColor: '#b45309', background: '#fffbeb' }}
                >
                  <span className="text-[12px] text-ink-1">
                    ⚠ <strong>{pendingQuotaForce.player.name}</strong> — {pendingQuotaForce.label}{' '}
                    {tr('activities.quotaFullPre')} <strong>{tr('activities.extra')}</strong>{' '}
                    {tr('activities.quotaFullPost')}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        pendingQuotaForce.onConfirm();
                        setPendingQuotaForce(null);
                      }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                      style={{ background: '#b45309' }}
                    >
                      {tr('activities.addAsExtra')}
                    </button>
                    <button
                      onClick={() => setPendingQuotaForce(null)}
                      className="text-xs px-3 py-1.5 rounded-lg text-ink-3"
                    >
                      {tr('home.common.cancel')}
                    </button>
                  </div>
                </div>
              )}
              {isTeamActivity(selected) &&
                !isClubTeamActivity(selected) &&
                teamMode_ === 'both' &&
                teamChoiceFor &&
                !pendingTeamP1 && (
                  <div
                    className="mb-2 p-3 rounded-lg border flex flex-wrap items-center gap-2"
                    style={{ borderColor: 'var(--lime-deep)', background: 'var(--lime-bg)' }}
                  >
                    <span className="text-[12px] text-ink-1 mr-1">
                      <strong>{playerName(teamChoiceFor)}</strong> — {tr('activities.addAsColon')}
                    </span>
                    <button
                      onClick={() => {
                        addSingle(
                          teamChoiceFor,
                          null,
                          isClubTeamActivity(selected) ? clubAddFor : null,
                        );
                        setTeamChoiceFor(null);
                      }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-lime-deep text-lime-deep bg-white hover:bg-lime-bg/40"
                    >
                      {tr('activities.single')}
                    </button>
                    <button
                      onClick={() => {
                        setPendingTeamP1(teamChoiceFor);
                        setTeamChoiceFor(null);
                      }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                      style={{ background: 'var(--lime-deep)' }}
                    >
                      {tr('activities.pickPartner')}
                    </button>
                    <button
                      onClick={() => setTeamChoiceFor(null)}
                      className="text-xs px-2.5 py-1.5 rounded-lg text-ink-3 ml-auto"
                    >
                      {tr('home.common.cancel')}
                    </button>
                  </div>
                )}
              {isTeamActivity(selected) && !isClubTeamActivity(selected) && pendingTeamP1 && (
                <div
                  className="mb-2 p-3 rounded-lg border flex items-center justify-between gap-2"
                  style={{ borderColor: 'var(--lime-deep)', background: 'var(--lime-bg)' }}
                >
                  <span className="text-[12px] text-ink-1">
                    {tr('activities.partnerFor')} <strong>{playerName(pendingTeamP1)}</strong>
                    {tr('activities.pickPlayer2Suffix')}
                  </span>
                  <button
                    onClick={() => setPendingTeamP1(null)}
                    className="text-xs px-2.5 py-1 rounded-lg text-ink-3 flex-shrink-0"
                  >
                    {tr('home.common.cancel')}
                  </button>
                </div>
              )}
              {candidatesBase.length === 0 ? (
                <p className="text-[12px] text-ink-3">{tr('activities.noAvailablePlayers')}</p>
              ) : (
                (() => {
                  const candBtn = (p, onClick) => {
                    const elo = p.elo != null ? Math.round(p.elo) : null;
                    const ok = levelOk(elo);
                    const lvl = elo != null ? getEloLevel(elo) : null;
                    const relation =
                      !ok && hasLevelReq && lvl
                        ? lvl.level < (lvMin != null ? lvMin : 1)
                          ? 'under'
                          : 'over'
                        : null;
                    return (
                      <button
                        key={p.id}
                        onClick={() => onClick(ok)}
                        className="w-full flex items-center justify-between gap-2 py-2 px-2 rounded hover:bg-lime-bg/30 text-left"
                      >
                        <span className="text-sm text-ink-1 truncate flex items-center gap-2">
                          {lvl ? (
                            <span
                              className="font-mono text-[10px] font-bold px-1.5 rounded flex-shrink-0"
                              style={{ background: lvl.color, color: lvl.text }}
                              title={tr('activities.levelBadge', {
                                level: String(lvl.level),
                                name: lvl.name,
                              })}
                            >
                              {lvl.level}
                            </span>
                          ) : (
                            <span
                              className="font-mono text-[9px] px-1.5 rounded flex-shrink-0"
                              style={{ background: '#f3f4f6', color: '#6b7280' }}
                            >
                              ?
                            </span>
                          )}
                          <span className="truncate">{p.name}</span>
                          {elo != null && (
                            <span className="font-mono text-[11px] text-ink-3 flex-shrink-0">
                              {elo} ELO
                            </span>
                          )}
                          {relation === 'under' && (
                            <span
                              className="font-mono text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: '#dbeafe', color: '#1e40af' }}
                            >
                              {tr('activities.underLevel')}
                            </span>
                          )}
                          {relation === 'over' && (
                            <span
                              className="font-mono text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: '#fef3c7', color: '#b45309' }}
                            >
                              {tr('activities.overLevel')}
                            </span>
                          )}
                        </span>
                        <Plus
                          size={14}
                          className="flex-shrink-0"
                          style={{ color: ok ? 'var(--lime-deep)' : 'var(--rose)' }}
                        />
                      </button>
                    );
                  };
                  // KM: class picker + pair flow (player 1 → partner) with a gender rule per class.
                  if (isKmActivity(selected)) {
                    const buckets = kmBucketsOf(selected);
                    const kmBucket = buckets.find((b) => b.bucketKey === clubAddFor) || buckets[0];
                    const p1Ok = (p) => (kmBucket?.catId === 'women' ? p.gender === 'F' : true); // Men/Mix open for choosing player 1
                    const listBase = pendingTeamP1
                      ? candidatesBase.filter((p) => kmGenderOk(pendingTeamP1, p.id, kmBucket))
                      : candidatesBase.filter(p1Ok);
                    const rows = filtCands(listBase, pickerSearch);
                    return (
                      <div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {buckets.map((b) => (
                            <button
                              key={b.bucketKey}
                              onClick={() => {
                                setClubAddFor(b.bucketKey);
                                setPendingTeamP1(null);
                                setPickerSearch('');
                              }}
                              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border-2"
                              style={{
                                borderColor:
                                  b.bucketKey === kmBucket?.bucketKey
                                    ? b.color || '#888'
                                    : 'var(--line)',
                                background:
                                  b.bucketKey === kmBucket?.bucketKey
                                    ? (b.color || '#888') + '18'
                                    : 'transparent',
                                color: b.color || '#444',
                              }}
                            >
                              {b.label}
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-ink-3 mb-2">
                          {tr('activities.km.pairRule', {
                            label: kmBucket?.label,
                            rule: kmRuleText(kmBucket),
                          })}{' '}
                          {pendingTeamP1
                            ? tr('activities.km.pickPartnerForPlayer', {
                                name: playerName(pendingTeamP1),
                              })
                            : tr('activities.km.pickPlayer1')}
                        </p>
                        {kmBucket?.catId === 'men' &&
                          pendingTeamP1 &&
                          genderOf(pendingTeamP1) === 'F' && (
                            <div
                              className="mb-2 p-2 rounded-lg border text-[11px]"
                              style={{
                                borderColor: '#f59e0b',
                                background: '#fffbeb',
                                color: '#92400e',
                              }}
                            >
                              {tr('activities.km.addingWomanWarn')}
                            </div>
                          )}
                        {pendingTeamP1 && (
                          <div
                            className="mb-2 p-2 rounded-lg border flex items-center justify-between gap-2"
                            style={{
                              borderColor: 'var(--lime-deep)',
                              background: 'var(--lime-bg)',
                            }}
                          >
                            <span className="text-[12px] text-ink-1">
                              {tr('activities.partnerFor')}{' '}
                              <strong>{playerName(pendingTeamP1)}</strong>?
                            </span>
                            <button
                              onClick={() => setPendingTeamP1(null)}
                              className="text-xs px-2.5 py-1 rounded-lg text-ink-3"
                            >
                              {tr('home.common.cancel')}
                            </button>
                          </div>
                        )}
                        <input
                          type="text"
                          className="input w-full text-sm mb-2"
                          placeholder={tr('activities.searchName')}
                          aria-label={tr('activities.searchName')}
                          value={pickerSearch}
                          onChange={(e) => setPickerSearch(e.target.value)}
                        />
                        {rows.length === 0 ? (
                          <p className="text-[12px] text-ink-3">
                            {tr('activities.km.noPlayersForClass')}
                          </p>
                        ) : (
                          <div className="space-y-1 max-h-72 overflow-y-auto">
                            {rows.map((p) =>
                              candBtn(p, () => {
                                if (!pendingTeamP1) setPendingTeamP1(p.id);
                                else addTeam(pendingTeamP1, p.id, null, null, kmBucket.bucketKey);
                              }),
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  // MIX: two columns by gender, each with its own search. A click adds (gender is derived).
                  if (isMixActivity(selected)) {
                    return (
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2"
                        style={{ columnGap: '2rem', rowGap: '1rem' }}
                      >
                        {[
                          {
                            g: 'M',
                            label: tr('activities.mixColMen'),
                            color: '#1d4ed8',
                            term: pickerSearchA,
                            setTerm: setPickerSearchA,
                          },
                          {
                            g: 'F',
                            label: tr('activities.mixColWomen'),
                            color: '#be185d',
                            term: pickerSearchB,
                            setTerm: setPickerSearchB,
                          },
                        ].map((col) => {
                          const rows = filtCands(
                            candidatesBase.filter((p) => (p.gender || 'M') === col.g),
                            col.term,
                          );
                          return (
                            <div key={col.g}>
                              <h4
                                className="font-mono text-[10px] uppercase tracking-wider mb-1"
                                style={{ color: col.color }}
                              >
                                {col.label}
                              </h4>
                              <input
                                type="text"
                                className="input w-full text-sm mb-1.5"
                                placeholder={tr('activities.searchIn', {
                                  what: col.label.toLowerCase(),
                                })}
                                aria-label={tr('activities.searchIn', {
                                  what: col.label.toLowerCase(),
                                })}
                                value={col.term}
                                onChange={(e) => col.setTerm(e.target.value)}
                              />
                              <div className="space-y-1 max-h-72 overflow-y-auto">
                                {rows.length ? (
                                  rows.map((p) =>
                                    candBtn(p, (ok) => {
                                      if (!ok) {
                                        setPendingOverride(p);
                                        return;
                                      }
                                      const full =
                                        col.g === 'F'
                                          ? c.women.confirmed >= c.women.max
                                          : c.men.confirmed >= c.men.max;
                                      if (full)
                                        setPendingQuotaForce({
                                          player: p,
                                          label: col.label,
                                          onConfirm: () => addPlayer(p.id, null, null, true),
                                        });
                                      else addPlayer(p.id);
                                    }),
                                  )
                                ) : (
                                  <p className="text-[11px] text-ink-3">
                                    {tr(col.term ? 'activities.noMatches' : 'activities.none')}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  // CLUB: two columns by club, each with its own search. A click adds to THAT club.
                  if (isClubActivity(selected)) {
                    const terms = [
                      { term: pickerSearchA, setTerm: setPickerSearchA },
                      { term: pickerSearchB, setTerm: setPickerSearchB },
                    ];
                    return (
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2"
                        style={{ columnGap: '2rem', rowGap: '1rem' }}
                      >
                        {c.clubs.map((cl, ci) => {
                          const t = terms[ci] || terms[0];
                          const rows = filtCands(candidatesBase, t.term);
                          return (
                            <div key={cl.id}>
                              {clubColHeaderSlim(cl)}
                              <input
                                type="text"
                                className="input w-full text-sm mb-1.5"
                                placeholder={tr('activities.searchTo', { club: cl.name })}
                                aria-label={tr('activities.searchTo', { club: cl.name })}
                                value={t.term}
                                onChange={(e) => t.setTerm(e.target.value)}
                              />
                              <div className="space-y-1 max-h-72 overflow-y-auto">
                                {rows.length ? (
                                  rows.map((p) =>
                                    candBtn(p, (ok) => {
                                      if (!ok) {
                                        setClubAddFor(cl.id);
                                        setPendingOverride(p);
                                        return;
                                      }
                                      const full = cl.confirmed >= cl.max;
                                      if (full)
                                        setPendingQuotaForce({
                                          player: p,
                                          label: cl.name,
                                          onConfirm: () => addPlayer(p.id, null, cl.id, true),
                                        });
                                      else addPlayer(p.id, null, cl.id);
                                    }),
                                  )
                                ) : (
                                  <p className="text-[11px] text-ink-3">
                                    {tr(t.term ? 'activities.noMatches' : 'activities.none')}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  // CLUB TEAM: two columns by club, team flow (pair/single) per club. Singles are paired only within the club.
                  if (isClubTeamActivity(selected)) {
                    const tMode = teamMode_;
                    const terms = [
                      { term: pickerSearchA, setTerm: setPickerSearchA },
                      { term: pickerSearchB, setTerm: setPickerSearchB },
                    ];
                    return (
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2"
                        style={{ columnGap: '2rem', rowGap: '1rem' }}
                      >
                        {c.clubs.map((cl, ci) => {
                          const t = terms[ci] || terms[0];
                          const rows = filtCands(candidatesBase, t.term);
                          const colPending =
                            pendingTeamP1 && clubAddFor === cl.id ? pendingTeamP1 : null;
                          const colChoice =
                            teamChoiceFor && clubAddFor === cl.id ? teamChoiceFor : null;
                          return (
                            <div key={cl.id}>
                              {clubColHeaderSlim(cl)}
                              <input
                                type="text"
                                className="input w-full text-sm mb-1.5"
                                placeholder={tr('activities.searchInClub', { club: cl.name })}
                                aria-label={tr('activities.searchInClub', { club: cl.name })}
                                value={t.term}
                                onChange={(e) => t.setTerm(e.target.value)}
                              />
                              {colPending && (
                                <div
                                  className="mb-1.5 p-2 rounded-lg border flex items-center justify-between gap-2"
                                  style={{
                                    borderColor: 'var(--lime-deep)',
                                    background: 'var(--lime-bg)',
                                  }}
                                >
                                  <span className="text-[11px] text-ink-1">
                                    {tr('activities.partnerFor')}{' '}
                                    <strong>{playerName(colPending)}</strong>
                                    {tr('activities.pickInListSuffix')}
                                  </span>
                                  <button
                                    onClick={() => setPendingTeamP1(null)}
                                    className="text-[11px] text-ink-3 flex-shrink-0"
                                  >
                                    {tr('home.common.cancel')}
                                  </button>
                                </div>
                              )}
                              {colChoice && (
                                <div
                                  className="mb-1.5 p-2 rounded-lg border flex flex-wrap items-center gap-1.5"
                                  style={{
                                    borderColor: 'var(--lime-deep)',
                                    background: 'var(--lime-bg)',
                                  }}
                                >
                                  <span className="text-[11px] text-ink-1 mr-1">
                                    <strong>{playerName(colChoice)}</strong>:
                                  </span>
                                  <button
                                    onClick={() => {
                                      addSingle(colChoice, null, cl.id);
                                      setTeamChoiceFor(null);
                                    }}
                                    className="text-[11px] font-semibold px-2 py-1 rounded border border-lime-deep text-lime-deep bg-white"
                                  >
                                    {tr('activities.single')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setPendingTeamP1(colChoice);
                                      setTeamChoiceFor(null);
                                    }}
                                    className="text-[11px] font-semibold px-2 py-1 rounded text-white"
                                    style={{ background: 'var(--lime-deep)' }}
                                  >
                                    {tr('activities.pickPartner')}
                                  </button>
                                  <button
                                    onClick={() => setTeamChoiceFor(null)}
                                    className="text-[11px] text-ink-3 ml-auto"
                                  >
                                    {tr('home.common.cancel')}
                                  </button>
                                </div>
                              )}
                              <div className="space-y-1 max-h-72 overflow-y-auto">
                                {rows.length ? (
                                  rows.map((p) =>
                                    candBtn(p, (ok) => {
                                      if (!ok) {
                                        setClubAddFor(cl.id);
                                        setPendingOverride(p);
                                        return;
                                      }
                                      if (tMode === 'singles') addSingle(p.id, null, cl.id);
                                      else if (tMode === 'pairs') {
                                        if (colPending) {
                                          if (p.id !== colPending)
                                            addTeam(colPending, p.id, null, cl.id);
                                        } else {
                                          setClubAddFor(cl.id);
                                          setPendingTeamP1(p.id);
                                        }
                                      } else {
                                        if (colPending) {
                                          if (p.id !== colPending)
                                            addTeam(colPending, p.id, null, cl.id);
                                        } else {
                                          setClubAddFor(cl.id);
                                          setTeamChoiceFor(p.id);
                                        }
                                      }
                                    }),
                                  )
                                ) : (
                                  <p className="text-[11px] text-ink-3">
                                    {tr(t.term ? 'activities.noMatches' : 'activities.none')}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  // STANDARD (incl. team): one list with team/level logic.
                  return (
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {candidates.map((p) =>
                        candBtn(p, (ok) => {
                          if (isTeamActivity(selected)) {
                            const cid = isClubTeamActivity(selected) ? clubAddFor : null;
                            if (teamMode_ === 'singles') addSingle(p.id, null, cid);
                            else if (teamMode_ === 'pairs') {
                              if (!pendingTeamP1) setPendingTeamP1(p.id);
                              else if (p.id !== pendingTeamP1)
                                addTeam(pendingTeamP1, p.id, null, cid);
                            } else {
                              if (pendingTeamP1) {
                                if (p.id !== pendingTeamP1) addTeam(pendingTeamP1, p.id, null, cid);
                              } else setTeamChoiceFor(p.id);
                            }
                          } else {
                            if (ok) addPlayer(p.id);
                            else setPendingOverride(p);
                          }
                        }),
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* Confirmed */}
          <div className="card-elev p-4">
            <h3 className="font-display text-base font-semibold text-ink-1 mb-2">
              {tr('activities.signedUpHeading')}{' '}
              {c.km
                ? tr('activities.pairsParen', { count: String(c.pairs) })
                : c.club
                  ? `(${c.clubs.map((cl) => `${cl.confirmed}/${cl.max} ${cl.name}`).join(' · ')})`
                  : c.mix
                    ? tr('activities.mixCountsParen', {
                        men: String(c.men.confirmed),
                        menMax: String(c.men.max),
                        women: String(c.women.confirmed),
                        womenMax: String(c.women.max),
                      })
                    : `(${c.confirmed}${c.max != null ? `/${c.max}` : ''}${c.team ? tr('activities.playersWordSuffix') : ''})`}
            </h3>
            {confirmed.length === 0 && !c.club && !c.km ? (
              <p className="text-sm text-ink-3">{tr('activities.noSignupsYet')}</p>
            ) : c.km ? (
              <div className="space-y-4">
                {c.kmBuckets.map((b) => {
                  const rows = confirmed.filter((r) => r.bucketKey === b.bucketKey);
                  return (
                    <div key={b.bucketKey}>
                      <div
                        className="flex items-center gap-2 mb-1.5 pb-1"
                        style={{ borderBottom: `2px solid ${b.color || '#888'}` }}
                      >
                        <span
                          className="font-display text-sm font-semibold"
                          style={{ color: b.color || '#444' }}
                        >
                          {b.label}
                        </span>
                        {b.full && (
                          <span
                            className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--rose)', color: '#fff' }}
                          >
                            FULL
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-ink-3 ml-auto">
                          {b.maxPairs != null
                            ? `${rows.length}/${b.maxPairs} lag`
                            : `${rows.length} par`}
                        </span>
                      </div>
                      {rows.length ? (
                        <div className="space-y-1">
                          {rows.map((r, i) => (
                            <div key={r.playerId + '_' + (r.partnerId || '')}>
                              {RegRow(r, `${i + 1}.`, false)}
                              {kmWomanInMen(r.playerId, r.partnerId, b) && (
                                <p className="text-[10px] mt-0.5 ml-6" style={{ color: '#b45309' }}>
                                  {tr('activities.km.womanInMensClass')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-ink-3">{tr('activities.noPairsYet')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : c.clubTeam ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2"
                style={{ columnGap: '2rem', rowGap: '1rem' }}
              >
                {/* deviation (lint): dropped the unused map index arg `ci` (was `(cl, ci)`). */}
                {c.clubs.map((cl) => {
                  const cRegs = confirmed.filter((r) => r.clubId === cl.id);
                  const cPairs = cRegs.filter((r) => r.partnerId);
                  const cSingles = cRegs.filter((r) => !r.partnerId);
                  return (
                    <div key={cl.id}>
                      {clubColHeader(cl)}
                      {cPairs.length > 0 && (
                        <div className="space-y-1">
                          {cPairs.map((r, i) => RegRow(r, `${i + 1}.`, false))}
                        </div>
                      )}
                      {cSingles.length > 0 && (
                        <>
                          <p className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mt-2 mb-1">
                            {tr('activities.singlesPairedAtStart', {
                              count: String(cSingles.length),
                            })}
                          </p>
                          {/* deviation (lint): dropped the unused map index arg `i` (was `(r, i)`). */}
                          <div className="space-y-1">
                            {cSingles.map((r) => RegRow(r, 'S', false))}
                          </div>
                          {cl.pendingSingle === 1 && (
                            <p className="text-[11px] mt-1" style={{ color: '#b45309' }}>
                              {tr('activities.oneSingleMissingPartnerInClub', {
                                club: cl.name,
                              })}
                            </p>
                          )}
                        </>
                      )}
                      {cRegs.length === 0 && (
                        <p className="text-[11px] text-ink-3">{tr('activities.noneYet')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : c.team ? (
              (() => {
                const cPairs = confirmed.filter((r) => r.partnerId);
                const cSingles = confirmed.filter((r) => !r.partnerId);
                return (
                  <>
                    {cPairs.length > 0 && (
                      <div className="space-y-1">
                        {cPairs.map((r, i) => RegRow(r, `${i + 1}.`, false))}
                      </div>
                    )}
                    {cSingles.length > 0 && (
                      <>
                        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-3 mb-1">
                          {tr('activities.singlesPairedAtStart', {
                            count: String(cSingles.length),
                          })}
                        </h4>
                        {/* deviation (lint): dropped the unused map index arg `i` (was `(r, i)`). */}
                        <div className="space-y-1">
                          {cSingles.map((r) => RegRow(r, 'S', false))}
                        </div>
                        {c.pendingSingle === 1 && (
                          <p className="text-[11px] mt-1.5" style={{ color: '#b45309' }}>
                            {tr('activities.oneSingleMissingPartner')}
                          </p>
                        )}
                      </>
                    )}
                  </>
                );
              })()
            ) : c.club ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2"
                style={{ columnGap: '2rem', rowGap: '1rem' }}
              >
                {/* deviation (lint): dropped the unused map index arg `ci` (was `(cl, ci)`). */}
                {c.clubs.map((cl) => {
                  const rows = confirmed.filter((r) => r.clubId === cl.id);
                  return (
                    <div key={cl.id}>
                      {clubColHeader(cl)}
                      {rows.length ? (
                        <div className="space-y-1">
                          {rows.map((r, i) => RegRow(r, `${i + 1}.`, false))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-ink-3">{tr('activities.noneYet')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : c.mix ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2"
                style={{ columnGap: '2rem', rowGap: '1rem' }}
              >
                {[
                  {
                    g: 'M',
                    label: tr('activities.mixColMen'),
                    max: c.men.max,
                    color: '#1d4ed8',
                  },
                  {
                    g: 'F',
                    label: tr('activities.mixColWomen'),
                    max: c.women.max,
                    color: '#be185d',
                  },
                ].map((col) => {
                  const rows = confirmed.filter(
                    (r) => (r.gender || genderOf(r.playerId)) === col.g,
                  );
                  return (
                    <div key={col.g}>
                      <h4
                        className="font-mono text-[10px] uppercase tracking-wider mb-1"
                        style={{ color: col.color }}
                      >
                        {col.label} ({rows.length}/{col.max})
                      </h4>
                      {rows.length ? (
                        <div className="space-y-1">
                          {rows.map((r, i) => RegRow(r, `${i + 1}.`, false))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-ink-3">{tr('activities.noneYet')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-1">
                {confirmed.map((r, i) => RegRow(r, `${i + 1}.`, false))}
              </div>
            )}
            {waitlist.length > 0 && (
              <>
                <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-3 mb-1">
                  {tr('activities.waitlistHeading', {
                    what: c.team
                      ? tr('activities.playersCount', {
                          count: String(waitlist.reduce((s, r) => s + (r.partnerId ? 2 : 1), 0)),
                        })
                      : String(waitlist.length),
                  })}
                </h4>
                <div className="space-y-1">
                  {waitlist.map((r, i) => RegRow(r, `V${i + 1}.`, true))}
                </div>
              </>
            )}
          </div>
        </div>

        {adminSwapReg && (
          // A `Dialog` (#900): named by its title, described by its lead. It
          // keeps its own header, whose ✕ gains a name. Focus starts in the
          // partner search, as its `autoFocus` put it, and goes back to the
          // «Bytt» button on the registration row when the dialog closes.
          <Dialog
            labelledBy={dlg.swapTitle}
            describedBy={dlg.swapBody}
            onClose={() => {
              setAdminSwapReg(null);
              setSelfPartnerSearch('');
            }}
            showClose={false}
            initialFocus={partnerSearchRef}
            backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
            style={{ maxHeight: 'calc(100dvh - 2rem)' }}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 id={dlg.swapTitle} className="font-display text-lg font-semibold text-ink-1">
                {tr('activities.swapPlayer')}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setAdminSwapReg(null);
                  setSelfPartnerSearch('');
                }}
                aria-label={tr('ui.dialog.close')}
                className="text-ink-3 hover:text-ink-1"
              >
                <X size={18} />
              </button>
            </div>
            <p id={dlg.swapBody} className="text-sm text-ink-3 mb-3">
              {tr('activities.swappingOut')} <strong>{playerName(adminSwapReg.replaceId)}</strong>.{' '}
              <strong>{playerName(adminSwapReg.keepId)}</strong> {tr('activities.isKeptPickNew')}
            </p>
            {renderPartnerPicker(adminSwapReg.keepId, (pid) => {
              swapPartner(adminSwapReg.reg, adminSwapReg.keepId, pid);
              setAdminSwapReg(null);
              setSelfPartnerSearch('');
            })}
          </Dialog>
        )}

        {/* Actions — admin only */}
        {adminMode && (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={() =>
                selected.recurringId ? setDelMenu(true) : setDelConfirm({ scope: 'single' })
              }
              className="text-sm font-semibold px-3.5 py-2 rounded-lg text-white flex items-center gap-1.5 hover:opacity-90"
              style={{ background: '#dc2626' }}
            >
              <Trash2 size={14} /> {tr('home.common.delete')}
              {selected.recurringId && (
                <span className="font-normal text-[11px] opacity-75">
                  {tr('activities.moreOptions')}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSignup}
                className="text-sm px-3 py-2 rounded-lg border border-line text-ink-2 hover:bg-bg-soft"
              >
                {tr(selected.signupClosed ? 'activities.openSignup' : 'activities.closeSignup')}
              </button>
              {(() => {
                // CUSTOM SESSION (coaching/arrangement/custom): a simple session without
                // tournament/matches. Start regardless of the number of sign-ups; warning at 0.
                const isCustomSession = isCustomType(selected.config?.tournamentType || '');
                if (isCustomSession) {
                  if (selected.completedAt) {
                    return (
                      <span className="text-[11px] text-ink-3">
                        {tr('activities.sessionCompleted')}
                      </span>
                    );
                  }
                  if (selected.sessionStarted) {
                    return (
                      <>
                        <span className="text-[11px] font-semibold" style={{ color: '#15803d' }}>
                          {tr('activities.sessionRunning')}
                        </span>
                        <button
                          onClick={finishCustomSession}
                          disabled={busy}
                          className="btn-lime text-sm disabled:opacity-40"
                        >
                          <Save size={14} /> {busy ? '…' : tr('activities.endSession')}
                        </button>
                      </>
                    );
                  }
                  return (
                    <>
                      {c.confirmed === 0 && (
                        <span className="text-[11px]" style={{ color: '#b45309' }}>
                          {tr('activities.noSignupsCanStart')}
                        </span>
                      )}
                      <button
                        onClick={startActivity}
                        disabled={busy}
                        className="btn-lime text-sm disabled:opacity-40"
                      >
                        <Play size={14} strokeWidth={2.5} />{' '}
                        {tr(busy ? 'activities.starting' : 'activities.startSession')}
                      </button>
                    </>
                  );
                }
                // CLUB TEAM: teams per club = pairs + floor(singles/2). Requires ≥1 team each, equal numbers, no loose single.
                const teamsPerClub = c.clubTeam
                  ? c.clubs.map((cl) => {
                      const cRegs = confirmed.filter((r) => r.clubId === cl.id);
                      const pairs = cRegs.filter((r) => r.partnerId).length;
                      const singles = cRegs.filter((r) => !r.partnerId).length;
                      return {
                        name: cl.name,
                        teams: pairs + Math.floor(singles / 2),
                        dangling: singles % 2,
                      };
                    })
                  : null;
                const clubTeamBlocked =
                  c.clubTeam &&
                  (teamsPerClub.some((t) => t.teams < 1) ||
                    teamsPerClub[0].teams !== teamsPerClub[1].teams);
                const clubBlocked =
                  !c.clubTeam &&
                  c.club &&
                  (c.clubs.some((cl) => cl.confirmed < 2) ||
                    c.clubs[0].confirmed !== c.clubs[1].confirmed);
                const mixBlocked = c.mix && (c.men.confirmed < 2 || c.women.confirmed < 2);
                const kmBlocked = c.km && !(c.kmBuckets || []).some((b) => b.pairs >= 2);
                const blocked = c.km
                  ? kmBlocked
                  : c.clubTeam
                    ? clubTeamBlocked
                    : c.club
                      ? clubBlocked
                      : c.mix
                        ? mixBlocked
                        : c.confirmed < 4;
                const msg = c.km
                  ? tr('activities.blocked.km')
                  : c.clubTeam
                    ? tr('activities.blocked.clubTeam', {
                        now: teamsPerClub.map((t) => `${t.teams} ${t.name}`).join(' vs '),
                      })
                    : c.club
                      ? tr('activities.blocked.club', {
                          now: c.clubs.map((cl) => `${cl.confirmed} ${cl.name}`).join(' vs '),
                        })
                      : c.mix
                        ? tr('activities.blocked.mix')
                        : tr('activities.blocked.min4');
                return (
                  <>
                    {blocked && <span className="text-[11px] text-ink-3">{msg}</span>}
                    {!blocked && c.clubTeam && teamsPerClub.some((t) => t.dangling) && (
                      <span className="text-[11px]" style={{ color: '#b45309' }}>
                        {tr('activities.danglingSingleInClub')}
                      </span>
                    )}
                    <button
                      onClick={startActivity}
                      disabled={blocked || busy}
                      className="btn-lime text-sm disabled:opacity-40"
                    >
                      <Play size={14} strokeWidth={2.5} />{' '}
                      {tr(
                        busy
                          ? 'activities.starting'
                          : isSingleMatch
                            ? 'activities.recordResult'
                            : c.km || isPadel24
                              ? 'activities.previewTournament'
                              : 'activities.startTournament',
                      )}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {delMenu &&
          selected.recurringId &&
          (() => {
            const dateOf = (a) => (a.bookingDraft?.slotRooms || [])[0]?.singleDate || '';
            const occs = (activities || []).filter((a) => a.recurringId === selected.recurringId);
            const myDate = dateOf(selected);
            const futureOccs = occs.filter((a) => dateOf(a) >= myDate);
            const bkOf = (ids) => (bookings || []).filter((b) => ids.has(b.seriesId)).length;
            const thisBk = (bookings || []).filter((b) => b.seriesId === selected.id).length;
            const futureBk = bkOf(new Set(futureOccs.map((a) => a.id)));
            const allBk = bkOf(new Set(occs.map((a) => a.id)));
            const pick = (scope, count, bk) => {
              setDelMenu(false);
              setDelConfirm({ scope, count, bk });
            };
            const opt =
              'text-left text-sm w-full px-3 py-2.5 rounded-lg border border-line hover:border-rose hover:bg-rose-soft/20 transition-colors';
            return (
              // A `Dialog` (#900): named by its title, described by its lead.
              // Escape is its «Avbryt». Picking a scope hands over to the
              // confirmation below in the same commit; that dialog inherits
              // this one's way back, so focus still returns to «Slett».
              <Dialog
                labelledBy={dlg.delMenuTitle}
                describedBy={dlg.delMenuBody}
                onClose={() => setDelMenu(false)}
                showClose={false}
                backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
                className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
                style={{ maxHeight: 'calc(100dvh - 2rem)' }}
              >
                <h3
                  id={dlg.delMenuTitle}
                  className="font-display text-lg font-semibold text-ink-1 mb-1"
                >
                  {tr('activities.deleteSeriesActivity')}
                </h3>
                <p id={dlg.delMenuBody} className="text-sm text-ink-3 mb-4">
                  {tr('activities.partOfSeriesShort')}
                  {selected.seriesCode ? ` #${selected.seriesCode}` : ''}
                  {tr('activities.whatToDelete')}
                </p>
                <div className="space-y-2">
                  <button type="button" onClick={() => pick('this', 1, thisBk)} className={opt}>
                    <span className="font-semibold text-ink-1">
                      {tr('activities.deleteScope.thisOnly')}
                    </span>{' '}
                    <span className="text-ink-3">
                      {tr(
                        thisBk === 1
                          ? 'activities.deleteScope.oneActivityBookingOne'
                          : 'activities.deleteScope.oneActivityBookingMany',
                        { bookings: String(thisBk) },
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => pick('future', futureOccs.length, futureBk)}
                    className={opt}
                  >
                    <span className="font-semibold text-ink-1">
                      {tr('activities.deleteScope.thisAndFuture')}
                    </span>{' '}
                    <span className="text-ink-3">
                      {tr(
                        futureOccs.length === 1
                          ? 'activities.deleteScope.activitiesOne'
                          : 'activities.deleteScope.activitiesMany',
                        { count: String(futureOccs.length) },
                      )}
                      {tr(
                        futureBk === 1
                          ? 'activities.deleteScope.bookingsOne'
                          : 'activities.deleteScope.bookingsMany',
                        { count: String(futureBk) },
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => pick('all', occs.length, allBk)}
                    className={opt}
                  >
                    <span className="font-semibold text-ink-1">
                      {tr('activities.deleteScope.wholeSeries')}
                    </span>{' '}
                    <span className="text-ink-3">
                      {tr(
                        occs.length === 1
                          ? 'activities.deleteScope.activitiesOne'
                          : 'activities.deleteScope.activitiesMany',
                        { count: String(occs.length) },
                      )}
                      {tr(
                        allBk === 1
                          ? 'activities.deleteScope.bookingsOne'
                          : 'activities.deleteScope.bookingsMany',
                        { count: String(allBk) },
                      )}
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setDelMenu(false)}
                  className="mt-3 text-sm text-ink-3 hover:text-ink-1"
                >
                  {tr('home.common.cancel')}
                </button>
              </Dialog>
            );
          })()}
        {delConfirm &&
          (() => {
            const single = delConfirm.scope === 'single';
            const scopeLabel = tr(
              single || delConfirm.scope === 'this'
                ? 'activities.scope.thisActivity'
                : delConfirm.scope === 'future'
                  ? 'activities.scope.thisAndFuture'
                  : 'activities.scope.wholeSeries',
            );
            const doExec = async () => {
              if (single) {
                await deleteActivity();
              } else {
                // A REFUSED delete (#1380) changed nothing: the activity is
                // still there, so it stays open behind the toast that says why.
                const out = (await onDeleteActivitySeries?.(selected, delConfirm.scope)) as
                  { refused?: boolean } | undefined;
                if (!out?.refused) setSelectedId(null);
              }
              setDelConfirm(null);
              setConfirmDel(false);
              setDelFuture(true);
            };
            return (
              // A `Dialog` (#900): named by its title, described by its lead.
              // Escape is its «Avbryt» — nothing is deleted — and focus starts
              // there. On close focus goes back to «Slett», directly or through
              // the scope menu that handed over to this.
              <Dialog
                labelledBy={dlg.delTitle}
                describedBy={dlg.delBody}
                onClose={() => setDelConfirm(null)}
                showClose={false}
                initialFocus={delCancelRef}
                backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
                className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
                style={{ maxHeight: 'calc(100dvh - 2rem)' }}
              >
                <h3
                  id={dlg.delTitle}
                  className="font-display text-lg font-semibold text-ink-1 mb-2"
                >
                  {tr('activities.confirmDeletion')}
                </h3>
                <p id={dlg.delBody} className="text-sm text-ink-2 mb-1">
                  {tr('activities.aboutToDelete', { scope: scopeLabel })}
                  {!single
                    ? tr(
                        delConfirm.count === 1
                          ? 'activities.deleteCountsOne'
                          : 'activities.deleteCountsMany',
                        {
                          count: String(delConfirm.count),
                          bookings: String(delConfirm.bk),
                          bookingWord: tr(
                            delConfirm.bk === 1
                              ? 'activities.bookingWordOne'
                              : 'activities.bookingWordMany',
                          ),
                        },
                      )
                    : ''}
                  .
                </p>
                <p className="text-[13px] font-medium mb-4" style={{ color: 'var(--danger)' }}>
                  {tr('activities.cannotBeUndone')}
                </p>
                {single && (
                  <label className="flex items-start gap-2 text-[12px] text-ink-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={delFuture}
                      onChange={(e) => setDelFuture(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>{tr('activities.alsoRemoveFutureBookings')}</span>
                  </label>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={doExec}
                    className="text-sm font-semibold px-4 py-2 rounded-lg text-white hover:opacity-90"
                    style={{ background: '#dc2626' }}
                  >
                    {tr('activities.yesDelete')}
                  </button>
                  <button
                    ref={delCancelRef}
                    type="button"
                    onClick={() => setDelConfirm(null)}
                    className="text-sm px-3 py-2 rounded-lg text-ink-3"
                  >
                    {tr('home.common.cancel')}
                  </button>
                </div>
              </Dialog>
            );
          })()}
      </div>
    );
  }

  // ──── List of activities ─────────────────────────────────────────────
  // Sort/filter the activity list. Default: newest first (createdAt desc).
  const actStartMs = (a) => {
    const mine = (bookings || []).filter((b) => b.seriesId === a.id);
    const stamps = mine
      .map((b) => new Date(`${b.date}T${b.startTime || '00:00'}:00`).getTime())
      .filter((n) => !isNaN(n));
    if (stamps.length) return Math.min(...stamps);
    // Standalone: no calendar bookings — use date/time from the time window in the draft,
    // so it sorts chronologically together with the calendar activities.
    if (a.bookingDraft?.slotMode === 'standalone') {
      const r = (a.bookingDraft.slotRooms || [])[0];
      if (r?.singleDate) {
        const ms = new Date(`${r.singleDate}T${r.startTime || '00:00'}:00`).getTime();
        if (!isNaN(ms)) return ms;
      }
    }
    return Infinity;
  };
  const actIsClosed = (a) => {
    if (a.status === 'cancelled' || a.status === 'confirmed') return true;
    if (a.signupClosed) return true;
    const startMs = actStartMs(a);
    const h =
      a.config?.signupCloseHours != null ? Math.max(0, Number(a.config.signupCloseHours) || 0) : 24;
    return startMs !== Infinity && Date.now() >= startMs - h * 3600000;
  };
  const presentTypeIds = [
    ...new Set((activities || []).map((a) => a.config?.tournamentType || 'americano')),
  ];
  const venueOf = (a) =>
    a.bookingDraft?.slotMode === 'standalone' && (a.bookingDraft?.venueName || '').trim()
      ? a.bookingDraft.venueName.trim()
      : clubInfo?.name || tr('activities.venueFallback');
  const presentVenues = [...new Set((activities || []).map(venueOf))];
  const visibleActivities = (activities || [])
    .filter((a) => {
      // Tab split: "Turneringer" = the sign-up formats; "Aktiviteter" =
      // coaching/arrangement/custom.
      const custom = isCustomType(a.config?.tournamentType || '');
      if (actTab === 'tournaments' && custom) return false;
      if (actTab === 'activities' && !custom) return false;
      if (actStatus !== 'all') {
        const cc = cap(a);
        const full = cc.max != null && cc.free === 0;
        const closed = actIsClosed(a);
        const cancelled = a.status === 'cancelled';
        if (actStatus === 'open' && !(!closed && !full)) return false;
        if (actStatus === 'waitlist' && !(full && !closed)) return false;
        if (actStatus === 'closed' && !(closed && !cancelled)) return false;
        if (actStatus === 'cancelled' && !cancelled) return false;
      }
      if (actType !== 'all' && (a.config?.tournamentType || 'americano') !== actType) return false;
      if (actVenue !== 'all' && venueOf(a) !== actVenue) return false;
      return true;
    })
    .slice()
    .sort((x, y) => {
      if (actSort === 'old') return (x.createdAt || 0) - (y.createdAt || 0);
      if (actSort === 'name')
        return (x.config?.name || '').localeCompare(y.config?.name || '', 'nb');
      if (actSort === 'date') return actStartMs(x) - actStartMs(y);
      return (y.createdAt || 0) - (x.createdAt || 0); // 'new' (default)
    });

  // Shared tag definitions (used in both list cards and detail) — logical colors.
  const statusBadgeOf = (a) => {
    const cc = cap(a);
    const isFull = cc.max != null && cc.free === 0;
    const wlOpen = isFull && !actIsClosed(a);
    if (a.completedAt)
      return { label: tr('activities.badge.completed'), bg: '#475569', color: '#fff' }; // slate
    if (a.sessionStarted)
      return { label: tr('activities.badge.inProgress'), bg: '#16a34a', color: '#fff' }; // green
    if (a.status === 'cancelled')
      return { label: tr('activities.badge.cancelled'), bg: '#dc2626', color: '#fff' }; // red
    if (a.status === 'confirmed')
      return { label: tr('activities.badge.locked'), bg: '#475569', color: '#fff' }; // slate
    if (actIsClosed(a))
      return { label: tr('activities.badge.closed'), bg: '#9ca3af', color: '#fff' }; // gray
    if (wlOpen) return { label: tr('activities.badge.waitlistOpen'), bg: '#d97706', color: '#fff' }; // yellow/amber
    return { label: tr('activities.badge.open'), bg: '#16a34a', color: '#fff' }; // green
  };
  const levelBadgeOf = (a) => {
    const lMin = a?.config?.levelMin ?? null;
    const lMax = a?.config?.levelMax ?? null;
    const hasLvl = lMin != null || lMax != null;
    if (!hasLvl)
      return {
        label: tr('activities.allLevels'),
        bg: '#eef2f7',
        color: '#64748b',
        title: tr('activities.openToAllLevels'),
      };
    const label =
      lMin != null && lMin === lMax
        ? tr('activities.levelOnly', { level: String(lMin) })
        : tr('activities.level.range', {
            min: String(lMin != null ? lMin : 1),
            max: String(lMax != null ? lMax : 9),
          });
    const lc = ELO_LEVELS.find((l) => l.level === (lMin != null ? lMin : 1));
    return {
      label,
      bg: lc?.color,
      color: lc?.text,
      title: tr('activities.eloLevelTitle', { label }),
    };
  };

  // Week segments — makes it easy to see when the activities run. Monday = start of week (Norwegian).
  const segNow = new Date();
  const segToday = new Date(segNow.getFullYear(), segNow.getMonth(), segNow.getDate());
  const segMonday = new Date(segToday);
  segMonday.setDate(segToday.getDate() - ((segToday.getDay() + 6) % 7)); // this week's Monday
  const segEndThisWeek = new Date(segMonday);
  segEndThisWeek.setDate(segMonday.getDate() + 7); // next Monday 00:00
  const segEndNextWeek = new Date(segMonday);
  segEndNextWeek.setDate(segMonday.getDate() + 14);
  const bucketOf = (a) => {
    // Completed custom session → always "Tidligere" (regardless of date).
    if (a.completedAt) return 'past';
    const ms = actStartMs(a);
    if (ms === Infinity) return 'none';
    if (ms < segToday.getTime()) return 'past';
    if (ms < segEndThisWeek.getTime()) return 'this';
    if (ms < segEndNextWeek.getTime()) return 'next';
    return 'later';
  };
  const segDefs = [
    { key: 'this', label: tr('activities.seg.thisWeek') },
    { key: 'next', label: tr('activities.seg.nextWeek') },
    { key: 'later', label: tr('activities.seg.later') },
    { key: 'none', label: tr('activities.seg.noFixedTime') },
    { key: 'past', label: tr('activities.seg.past') },
  ];
  const groupedActivities = segDefs
    .map((s) => ({ ...s, items: visibleActivities.filter((a) => bucketOf(a) === s.key) }))
    .filter((s) => s.items.length > 0);

  // ── Create an activity directly from Activities ────────────────────────
  // deviation (lint): `newCustomDraft` is defined in the monolith but unused in this
  // range (custom drafts are created via `onCreateViaActivities`); `_`-prefixed.
  const _newCustomDraft = (type) => ({
    type,
    name: tr(
      type === 'coaching'
        ? 'activities.create.coaching'
        : type === 'arrangement'
          ? 'activities.create.arrangement'
          : 'activities.create.newActivity',
    ),
    date: '',
    startTime: '',
    endTime: '',
    maxParticipants: type === 'coaching' ? '8' : '',
    allowWaitlist: true,
    maxReserves: '',
    reserveCourts: type === 'coaching',
    location: '',
    description: '',
  });
  const createCustomActivity = async () => {
    const d = customDraft;
    if (!d || !d.name.trim()) return;
    const id = `custom_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const activity = {
      id,
      config: {
        name: d.name.trim(),
        tournamentType: d.type,
        isCustomActivity: true,
        activityDate: d.date || null,
        activityTime: d.startTime || null,
        activityEndTime: d.endTime || null,
        maxPlayers: d.maxParticipants === '' ? null : Math.max(1, Number(d.maxParticipants) || 0),
        allowReserves: !!d.allowWaitlist,
        maxReserves: d.maxReserves === '' ? null : Math.max(0, Number(d.maxReserves) || 0),
        reserveCourts: !!d.reserveCourts,
        location: d.location.trim() || null,
        description: d.description.trim() || null,
        pricePerPlayer: 0,
      },
      status: 'awaiting_signup',
      registrations: [],
      matches: [],
      createdAt: Date.now(),
    };
    await onPersist([...(activities || []), activity]);
    setCustomDraft(null);
    setShowCreatePicker(false);
    setSelectedId(id);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 slide-up">
      <button onClick={onBack} className="btn-outline text-sm mb-4">
        <ArrowLeft size={14} /> {tr('activities.toHome')}
      </button>
      {/* Header — generic over the filter */}
      <div className="flex items-center gap-2 mb-1.5">
        <Trophy
          size={16}
          strokeWidth={2.5}
          style={{
            color:
              actTab === 'activities'
                ? '#0891b2'
                : actTab === 'tournaments'
                  ? '#7c3aed'
                  : '#475569',
          }}
        />
        <span className="font-mono text-[10px] font-bold tracking-[0.18em] uppercase text-ink-3">
          {tr(
            actTab === 'activities'
              ? 'activities.eyebrow.activities'
              : actTab === 'tournaments'
                ? 'activities.eyebrow.tournaments'
                : 'activities.eyebrow.signups',
          )}
        </span>
      </div>
      <h1 className="font-display text-2xl font-semibold text-ink-1 mb-1">
        {tr(
          actTab === 'activities'
            ? 'activities.title.activities'
            : actTab === 'tournaments'
              ? 'activities.title.tournaments'
              : 'activities.title.signups',
        )}
      </h1>
      <p className="text-sm text-ink-3 mb-4">
        {tr(
          actTab === 'activities'
            ? 'activities.lead.activities'
            : actTab === 'tournaments'
              ? 'activities.lead.tournaments'
              : 'activities.lead.signups',
          { club: clubInfo?.name || tr('activities.theCentre') },
        )}
      </p>
      <button onClick={() => setShowCreatePicker(true)} className="btn-lime text-sm mb-4">
        <Plus size={15} /> {tr('activities.createActivity')}
      </button>
      {/* Main filter: All (default) | Tournaments | Activities — placed BELOW "Opprett aktivitet" */}
      {(() => {
        const allCount = (activities || []).length;
        const tCount = (activities || []).filter(
          (a) => !isCustomType(a.config?.tournamentType || ''),
        ).length;
        const aCount = (activities || []).filter((a) =>
          isCustomType(a.config?.tournamentType || ''),
        ).length;
        const Tab = ({ id, label, count }) => (
          <button
            onClick={() => {
              setActTab(id);
              setActType('all');
            }}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={
              actTab === id
                ? { background: '#1f2937', color: '#fff' }
                : { background: '#f1f3ee', color: '#5b6358' }
            }
          >
            {label}
            {count > 0 ? ` (${count})` : ''}
          </button>
        );
        return (
          <div className="flex items-center gap-2 mb-6">
            <Tab id="all" label={tr('activities.tab.all')} count={allCount} />
            <Tab id="tournaments" label={tr('activities.tab.tournaments')} count={tCount} />
            <Tab id="activities" label={tr('activities.tab.activities')} count={aCount} />
          </div>
        );
      })()}

      {/* Type picker: custom + tournament formats (not seriespill) */}
      {showCreatePicker && !customDraft && (
        // A `Dialog` (#900): named by its title, described by its lead, Tab
        // kept inside, Escape closes as the backdrop did, focus back to
        // «Opprett aktivitet». The inline styles moved across unchanged; each
        // section title names its row of choices as a group.
        <Dialog
          labelledBy={dlg.createTitle}
          describedBy={dlg.createBody}
          onClose={() => setShowCreatePicker(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15,22,17,0.55)',
          }}
          className=""
          style={{
            background: '#fff',
            maxWidth: 560,
            width: '100%',
            marginTop: 80,
            borderRadius: 16,
            overflow: 'hidden',
            maxHeight: 'calc(100dvh - 100px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e7eae3', flexShrink: 0 }}>
            <h3 id={dlg.createTitle} className="font-display text-lg font-semibold text-ink-1">
              {tr('activities.createActivity')}
            </h3>
            <p id={dlg.createBody} className="text-xs text-ink-3 mt-0.5">
              {tr('activities.createPickerLead')}
            </p>
          </div>
          <div style={{ padding: '14px 20px', overflowY: 'auto' }}>
            <div
              id={dlg.createCustom}
              className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2"
            >
              {tr('activities.customSection')}
            </div>
            <div
              role="group"
              aria-labelledby={dlg.createCustom}
              className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4"
            >
              {['coaching', 'arrangement', 'custom'].map((tid) => {
                const t = TOURNAMENT_TYPES[tid];
                return (
                  <button
                    key={tid}
                    type="button"
                    onClick={() => {
                      setShowCreatePicker(false);
                      onCreateViaActivities?.(tid);
                    }}
                    disabled={!onCreateViaActivities}
                    className="text-left p-3 rounded-xl border-2 border-line hover:border-line-soft transition-all disabled:opacity-40"
                  >
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                      style={{ background: t.color }}
                    >
                      {t.abbrev}
                    </span>
                    <div className="font-semibold text-sm text-ink-1 mt-1.5">{t.label}</div>
                    <div className="text-[11px] text-ink-3">
                      {tr(
                        tid === 'coaching'
                          ? 'activities.create.coachingHint'
                          : tid === 'arrangement'
                            ? 'activities.create.arrangementHint'
                            : 'activities.create.customHint',
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div
              id={dlg.createFormats}
              className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2"
            >
              {tr('activities.tournamentFormatsSection')}
            </div>
            <div
              role="group"
              aria-labelledby={dlg.createFormats}
              className="grid grid-cols-2 sm:grid-cols-3 gap-2"
            >
              {Object.values(TOURNAMENT_TYPES)
                .filter((t) => t.category !== 'custom')
                .map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setShowCreatePicker(false);
                      onCreateViaActivities?.(t.id);
                    }}
                    disabled={!onCreateViaActivities}
                    className="text-left p-2.5 rounded-lg border border-line hover:border-line-soft transition-all disabled:opacity-40 flex items-center gap-2"
                  >
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                      style={{ background: t.color }}
                    >
                      {t.abbrev}
                    </span>
                    <span className="text-[12px] font-medium text-ink-1 truncate">{t.label}</span>
                  </button>
                ))}
            </div>
            <p className="text-[11px] text-ink-3 mt-3 italic">
              {tr('activities.leagueNotFromHere')}
            </p>
          </div>
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e7eae3',
              textAlign: 'right',
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => setShowCreatePicker(false)}
              className="btn-outline text-sm"
            >
              {tr('home.common.cancel')}
            </button>
          </div>
        </Dialog>
      )}

      {/* Custom setup (Coaching/Arrangement/Custom) */}
      {customDraft &&
        (() => {
          const t = TOURNAMENT_TYPES[customDraft.type];
          const set = (patch) => setCustomDraft((d) => ({ ...d, ...patch }));
          return (
            // A `Dialog` (#900), migrated with the rest of the view so no
            // hand-built overlay is left in it. NOTHING OPENS IT TODAY: nothing
            // in this file sets `customDraft` to a draft (the custom types go
            // through `onCreateViaActivities` from the picker above), so it is
            // migrated mechanically and has no test through an opener.
            <Dialog
              labelledBy={dlg.customTitle}
              onClose={() => setCustomDraft(null)}
              showClose={false}
              backdropClassName=""
              backdropStyle={{
                position: 'fixed',
                inset: 0,
                zIndex: 10002,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                background: 'rgba(15,22,17,0.55)',
              }}
              className=""
              style={{
                background: '#fff',
                maxWidth: 480,
                width: '100%',
                marginTop: 80,
                borderRadius: 16,
                overflow: 'hidden',
                maxHeight: 'calc(100dvh - 100px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{ padding: '16px 20px', borderBottom: '1px solid #e7eae3', flexShrink: 0 }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ background: t.color }}
                  >
                    {t.abbrev}
                  </span>
                  <h3
                    id={dlg.customTitle}
                    className="font-display text-lg font-semibold text-ink-1"
                  >
                    {tr('activities.newOfType', { type: t.label.toLowerCase() })}
                  </h3>
                </div>
              </div>
              <div
                style={{
                  padding: '16px 20px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <label className="text-[12px] text-ink-2">
                  {tr('activities.nameLabel')}
                  <input
                    value={customDraft.name}
                    onChange={(e) => set({ name: e.target.value })}
                    className="block mt-1 w-full px-3 py-2 rounded-lg border border-line text-sm"
                    placeholder={tr('activities.namePlaceholder')}
                  />
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-[12px] text-ink-2">
                    {tr('activities.dateLabel')}
                    <input
                      type="date"
                      value={customDraft.date}
                      onChange={(e) => set({ date: e.target.value })}
                      className="block mt-1 w-full px-2 py-2 rounded-lg border border-line text-sm"
                    />
                  </label>
                  <label className="text-[12px] text-ink-2">
                    {tr('activities.fromLabel')}
                    <input
                      type="time"
                      value={customDraft.startTime}
                      onChange={(e) => set({ startTime: e.target.value })}
                      className="block mt-1 w-full px-2 py-2 rounded-lg border border-line text-sm"
                    />
                  </label>
                  <label className="text-[12px] text-ink-2">
                    {tr('activities.toLabel')}
                    <input
                      type="time"
                      value={customDraft.endTime}
                      onChange={(e) => set({ endTime: e.target.value })}
                      className="block mt-1 w-full px-2 py-2 rounded-lg border border-line text-sm"
                    />
                  </label>
                </div>
                <label className="text-[12px] text-ink-2">
                  {tr('activities.placeOptional')}
                  <input
                    value={customDraft.location}
                    onChange={(e) => set({ location: e.target.value })}
                    className="block mt-1 w-full px-3 py-2 rounded-lg border border-line text-sm"
                    placeholder={clubInfo?.name || tr('activities.venueFallback')}
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[12px] text-ink-2">
                    {tr('activities.maxParticipantsLabel')}
                    <input
                      type="number"
                      min="1"
                      value={customDraft.maxParticipants}
                      onChange={(e) => set({ maxParticipants: e.target.value })}
                      className="block mt-1 w-full px-3 py-2 rounded-lg border border-line text-sm"
                      placeholder={tr('activities.egEight')}
                    />
                  </label>
                  <label className="text-[12px] text-ink-2">
                    {tr('activities.maxWaitlistLabel')}
                    <input
                      type="number"
                      min="0"
                      value={customDraft.maxReserves}
                      onChange={(e) => set({ maxReserves: e.target.value })}
                      disabled={!customDraft.allowWaitlist}
                      className="block mt-1 w-full px-3 py-2 rounded-lg border border-line text-sm disabled:opacity-40"
                      placeholder={tr('activities.egFour')}
                    />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-[13px] text-ink-2">
                  <input
                    type="checkbox"
                    checked={customDraft.allowWaitlist}
                    onChange={(e) => set({ allowWaitlist: e.target.checked })}
                  />{' '}
                  {tr('activities.allowWaitlist')}
                </label>
                <label className="flex items-center gap-2 text-[13px] text-ink-2">
                  <input
                    type="checkbox"
                    checked={customDraft.reserveCourts}
                    onChange={(e) => set({ reserveCourts: e.target.checked })}
                  />{' '}
                  {tr('activities.reserveCourtsForThis')}
                </label>
                {customDraft.reserveCourts && (
                  <p className="text-[11px] text-ink-3 -mt-1.5">
                    {tr('activities.reserveCourtsLater')}
                  </p>
                )}
                <label className="text-[12px] text-ink-2">
                  {tr('activities.descriptionOptional')}
                  <textarea
                    value={customDraft.description}
                    onChange={(e) => set({ description: e.target.value })}
                    rows={3}
                    className="block mt-1 w-full px-3 py-2 rounded-lg border border-line text-sm"
                    placeholder={tr('activities.descriptionPlaceholder')}
                  />
                </label>
              </div>
              <div
                style={{
                  padding: '12px 20px',
                  borderTop: '1px solid #e7eae3',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setCustomDraft(null);
                    setShowCreatePicker(true);
                  }}
                  className="btn-outline text-sm"
                >
                  {tr('activities.backArrow')}
                </button>
                <button
                  type="button"
                  onClick={createCustomActivity}
                  disabled={!customDraft.name.trim()}
                  className="btn-lime text-sm disabled:opacity-40"
                >
                  {tr('activities.createActivity')}
                </button>
              </div>
            </Dialog>
          );
        })()}

      {activities.length === 0 ? (
        <div className="card-elev p-8 text-center">
          <div className="text-3xl mb-2">🎾</div>
          <h3 className="font-display text-lg font-semibold text-ink-1 mb-1">
            {tr('activities.emptyTitle')}
          </h3>
          <p className="text-sm text-ink-3 max-w-md mx-auto">{tr('activities.emptyLead')}</p>
        </div>
      ) : (
        <>
          {/* Filter + sort */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <label
                htmlFor={dlg.sort}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
              >
                {tr('activities.filter.sort')}
              </label>
              <select
                id={dlg.sort}
                value={actSort}
                onChange={(e) => setActSort(e.target.value)}
                className="input text-sm"
                style={{ width: 'auto', paddingRight: '1.75rem' }}
              >
                <option value="new">{tr('activities.sort.newest')}</option>
                <option value="old">{tr('activities.sort.oldest')}</option>
                <option value="date">{tr('activities.sort.courtTime')}</option>
                <option value="name">{tr('activities.sort.name')}</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <label
                htmlFor={dlg.status}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
              >
                {tr('activities.filter.status')}
              </label>
              <select
                id={dlg.status}
                value={actStatus}
                onChange={(e) => setActStatus(e.target.value)}
                className="input text-sm"
                style={{ width: 'auto', paddingRight: '1.75rem' }}
              >
                <option value="all">{tr('activities.filter.all')}</option>
                <option value="open">{tr('activities.status.openSignup')}</option>
                <option value="waitlist">{tr('activities.status.waitlistOpen')}</option>
                <option value="closed">{tr('activities.status.closed')}</option>
                <option value="cancelled">{tr('activities.status.cancelled')}</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <label
                htmlFor={dlg.type}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
              >
                {tr('activities.filter.type')}
              </label>
              <select
                id={dlg.type}
                value={actType}
                onChange={(e) => setActType(e.target.value)}
                className="input text-sm"
                style={{ width: 'auto', paddingRight: '1.75rem' }}
              >
                <option value="all">{tr('activities.filter.all')}</option>
                {presentTypeIds.map((id) => {
                  const t = getTournamentType(id);
                  return (
                    <option key={id} value={id}>
                      {t?.label || id}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <label
                htmlFor={dlg.venue}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
              >
                {tr('activities.filter.venue')}
              </label>
              <select
                id={dlg.venue}
                value={actVenue}
                onChange={(e) => setActVenue(e.target.value)}
                className="input text-sm"
                style={{ width: 'auto', paddingRight: '1.75rem' }}
              >
                <option value="all">Alle</option>
                {presentVenues.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            {(actStatus !== 'all' || actType !== 'all' || actVenue !== 'all') && (
              <button
                onClick={() => {
                  setActStatus('all');
                  setActType('all');
                  setActVenue('all');
                }}
                className="text-[11px] text-ink-3 underline"
              >
                {tr('activities.resetFilters')}
              </button>
            )}
            <span className="text-[11px] text-ink-3 ml-auto">
              {tr('activities.visibleOfTotal', {
                shown: String(visibleActivities.length),
                total: String(activities.length),
              })}
            </span>
            {adminMode && onDeleteAllActivities && (
              <button
                onClick={() => setWipeConfirm(true)}
                className="text-xs font-semibold px-2.5 py-1 rounded-lg border flex items-center gap-1"
                style={{ borderColor: '#fca5a5', color: '#b91c1c', background: '#fff' }}
              >
                <span
                  className="font-mono text-[9px] font-bold px-1 py-0.5 rounded"
                  style={{ background: '#a16207', color: '#fff' }}
                >
                  DEMO
                </span>
                <Trash2 size={12} /> {tr('activities.deleteAll')}
              </button>
            )}
          </div>

          {wipeConfirm && (
            // A `Dialog` (#900): named by its title, described by its lead.
            // Escape is its «Avbryt» and focus starts there; neither Escape nor
            // the backdrop closes it while the wipe is running, as the backdrop
            // never did. Focus goes back to «Slett alle» on close.
            <Dialog
              labelledBy={dlg.wipeTitle}
              describedBy={dlg.wipeBody}
              onClose={() => {
                if (!wiping) setWipeConfirm(false);
              }}
              showClose={false}
              initialFocus={wipeCancelRef}
              backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
              style={{ maxHeight: 'calc(100dvh - 2rem)' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: '#a16207', color: '#fff' }}
                >
                  DEMO
                </span>
                <h3
                  id={dlg.wipeTitle}
                  className="font-display text-lg font-semibold"
                  style={{ color: '#b91c1c' }}
                >
                  {tr('activities.wipeTitle')}
                </h3>
              </div>
              <p id={dlg.wipeBody} className="text-sm text-ink-2 mb-1">
                {tr('activities.wipeLeadPre', { count: String(activities.length) })}{' '}
                <strong>{tr('activities.wipeLeadBold')}</strong> {tr('activities.wipeLeadPost')}
              </p>
              <p className="text-sm font-semibold mb-4" style={{ color: '#b91c1c' }}>
                {tr('activities.cannotBeUndone')}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={wiping}
                  onClick={async () => {
                    setWiping(true);
                    await onDeleteAllActivities?.();
                    setWiping(false);
                    setWipeConfirm(false);
                    setSelectedId(null);
                  }}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
                  style={{ background: '#dc2626' }}
                >
                  {tr(wiping ? 'activities.deleting' : 'activities.yesDeleteAll')}
                </button>
                <button
                  ref={wipeCancelRef}
                  type="button"
                  disabled={wiping}
                  onClick={() => setWipeConfirm(false)}
                  className="text-sm px-3 py-2 rounded-lg text-ink-3"
                >
                  {tr('home.common.cancel')}
                </button>
              </div>
            </Dialog>
          )}

          {visibleActivities.length === 0 ? (
            <div className="card-elev p-6 text-center text-sm text-ink-3">
              {tr('activities.noMatchFilter')}
            </div>
          ) : (
            <div className="space-y-6">
              {groupedActivities.map((seg) => (
                <div key={seg.key} className={seg.key === 'past' ? 'opacity-55' : ''}>
                  {seg.key === 'past' ? (
                    <button
                      onClick={() => setPastExpanded((v) => !v)}
                      className="flex items-center gap-2 mb-2 mt-1 w-full text-left"
                    >
                      <ChevronRight
                        size={13}
                        className={`text-ink-3 transition-transform ${pastExpanded ? 'rotate-90' : ''}`}
                      />
                      <h3 className="font-mono text-[11px] font-bold tracking-[0.14em] uppercase text-ink-3">
                        {seg.label}
                      </h3>
                      <span className="text-[11px] text-ink-3">· {seg.items.length}</span>
                      {!pastExpanded && (
                        <span className="text-[10px] text-ink-3 ml-1 normal-case tracking-normal">
                          {tr('activities.clickToShow')}
                        </span>
                      )}
                    </button>
                  ) : (
                    <div className="flex items-baseline gap-2 mb-2 mt-1">
                      <h3 className="font-mono text-[11px] font-bold tracking-[0.14em] uppercase text-ink-2">
                        {seg.label}
                      </h3>
                      <span className="text-[11px] text-ink-3">· {seg.items.length}</span>
                    </div>
                  )}
                  <div className="space-y-3">
                    {(seg.key !== 'past' || pastExpanded) &&
                      seg.items.map((a) => {
                        const c = cap(a);
                        const type = typeOf(a);
                        const full = c.free === 0;
                        const waitlistOpen = full && c.max != null && !actIsClosed(a);
                        const lMin = a?.config?.levelMin ?? null;
                        const lMax = a?.config?.levelMax ?? null;
                        const hasLvl = lMin != null || lMax != null;
                        const lvlText = hasLvl
                          ? lMin != null && lMin === lMax
                            ? tr('activities.levelOnly', { level: String(lMin) })
                            : tr('activities.level.range', {
                                min: String(lMin != null ? lMin : 1),
                                max: String(lMax != null ? lMax : 9),
                              })
                          : null;
                        const lvlBadge = levelBadgeOf(a);
                        const statusBadge = statusBadgeOf(a);
                        const aBookings = (bookings || []).filter((b) => b.seriesId === a.id);
                        const aSaDraft = a.bookingDraft;
                        const aIsStandalone = aSaDraft?.slotMode === 'standalone';
                        const aSaRoom = aIsStandalone
                          ? (aSaDraft.slotRooms || [])[0] || null
                          : null;
                        const aWhen = (() => {
                          const shortDate = (date) => {
                            let dl = date;
                            try {
                              const [y, m, d] = date.split('-').map(Number);
                              const dt = new Date(y, m - 1, d);
                              const wd = wdShort[dt.getDay()];
                              const mo = moShort[m - 1];
                              dl = tr('activities.date.dayMonth', { wd, day: String(d), mo });
                            } catch {
                              /* ignore: best-effort, non-critical */
                            }
                            return dl;
                          };
                          if (aBookings.length) {
                            const date = aBookings
                              .map((b) => b.date)
                              .filter(Boolean)
                              .sort()[0];
                            const start = aBookings
                              .map((b) => b.startTime)
                              .filter(Boolean)
                              .sort()[0];
                            const end = aBookings
                              .map((b) => b.endTime)
                              .filter(Boolean)
                              .sort()
                              .slice(-1)[0];
                            return { dateLabel: shortDate(date), start, end };
                          }
                          if (aIsStandalone && aSaRoom?.singleDate && aSaRoom?.startTime) {
                            return {
                              dateLabel: shortDate(aSaRoom.singleDate),
                              start: aSaRoom.startTime,
                              end: aSaRoom.endTime || aSaRoom.startTime,
                            };
                          }
                          // Seriespill season: "season start" is the date; the duration is a whole season (period).
                          if (
                            a?.config?.tournamentType === 'seriespill_season' &&
                            a?.config?.activityDate
                          ) {
                            return { dateLabel: shortDate(a.config.activityDate), period: true };
                          }
                          // Custom activity: date + optional time from config.
                          if (a?.config?.isCustomActivity && a?.config?.activityDate) {
                            return {
                              dateLabel: shortDate(a.config.activityDate),
                              start: a.config.activityTime || null,
                              end: a.config.activityEndTime || null,
                            };
                          }
                          // Padel24 (and others with an explicit time in config): use activityDate/Time.
                          if (a?.config?.activityDate) {
                            return {
                              dateLabel: shortDate(a.config.activityDate),
                              start: a.config.activityTime || null,
                              end: a.config.activityEndTime || null,
                            };
                          }
                          // Older Padel24 activities without activityDate: derive from the setup's days.
                          {
                            const _pd = (a?.config?.p24Snapshot?.days || [])
                              .filter((x) => x && x.date)
                              .sort((x, y) => x.date.localeCompare(y.date));
                            if (_pd[0])
                              return {
                                dateLabel: shortDate(_pd[0].date),
                                start: _pd[0].startTime || null,
                                end: _pd[0].endTime || null,
                              };
                          }
                          return null;
                        })();
                        const aVenue =
                          aIsStandalone && (aSaDraft.venueName || '').trim()
                            ? aSaDraft.venueName.trim()
                            : clubInfo?.name || tr('activities.venueFallback');
                        const snippet =
                          (a?.config?.description || '')
                            .split('\n')
                            .map((s) => s.trim())
                            .filter(Boolean)[0] || '';
                        return (
                          <button
                            key={a.id}
                            onClick={() => {
                              setSelectedId(a.id);
                              setEditing(false);
                              setConfirmDel(false);
                              setPickerSearch('');
                            }}
                            className="card-elev p-4 w-full text-left hover:shadow-lg transition-all hover:-translate-y-0.5"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  {type && (
                                    <span
                                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                                      style={{ background: type.color }}
                                    >
                                      {type.label || type.abbrev}
                                    </span>
                                  )}
                                  <span
                                    className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                                    style={{ background: lvlBadge.bg, color: lvlBadge.color }}
                                    title={
                                      hasLvl
                                        ? tr('activities.eloLevelTitle', { label: lvlText })
                                        : tr('activities.openToAllLevels')
                                    }
                                  >
                                    {lvlBadge.label}
                                  </span>
                                  <span
                                    className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                                    style={{ background: statusBadge.bg, color: statusBadge.color }}
                                  >
                                    {statusBadge.label}
                                  </span>
                                </div>
                                <h3 className="font-display text-base font-semibold text-ink-1 truncate">
                                  {a?.config?.name || tr('activities.activityFallback')}
                                </h3>
                                {snippet && (
                                  <p className="text-[12px] text-ink-3 truncate mt-0.5">
                                    {snippet}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 mt-1.5 text-[12px] text-ink-2 flex-wrap">
                                  <span className="flex items-center gap-1">
                                    <Clock size={13} className="text-ink-3" />
                                    {aWhen ? (
                                      aWhen.period ? (
                                        <>
                                          <strong>
                                            {tr('activities.seasonStartsOn', {
                                              date: aWhen.dateLabel,
                                            })}
                                          </strong>
                                          <span className="text-ink-3">
                                            {' '}
                                            {tr('activities.seasonPeriodSuffix')}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <strong>{aWhen.dateLabel}</strong>
                                          {aWhen.start ? (
                                            <span className="text-ink-3">
                                              {' '}
                                              · {aWhen.start}
                                              {aWhen.end ? `–${aWhen.end}` : ''}
                                            </span>
                                          ) : null}
                                        </>
                                      )
                                    ) : (
                                      tr('activities.when.notSet')
                                    )}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MapPin size={13} className="text-ink-3" />
                                    {aVenue}
                                  </span>
                                  {a?.config?.pricePerPlayer ? (
                                    <span>
                                      {tr('activities.priceKr', {
                                        amount: String(a.config.pricePerPlayer),
                                      })}
                                    </span>
                                  ) : (
                                    <span className="text-ink-3">{tr('activities.free')}</span>
                                  )}
                                  {a.recurringId && (
                                    <span
                                      className="flex items-center gap-1 text-ink-3"
                                      title={tr('activities.partOfSeriesTitle', {
                                        name: a.seriesName
                                          ? tr('activities.seriesNameSuffix', {
                                              name: a.seriesName,
                                            })
                                          : '',
                                      })}
                                    >
                                      {tr('activities.seriesTag')}
                                      {a.seriesCode ? ` #${a.seriesCode}` : ''}
                                      {a.recurringCount
                                        ? ` · ${(a.recurringIndex ?? 0) + 1}/${a.recurringCount}`
                                        : a.recurringIndex != null
                                          ? tr('activities.occurrenceShort', {
                                              index: String(a.recurringIndex + 1),
                                            })
                                          : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0 flex items-center gap-2">
                                <div>
                                  <div className="text-[12px]">
                                    <span
                                      className="font-semibold"
                                      style={{ color: full ? '#b45309' : 'var(--lime-deep)' }}
                                    >
                                      {c.confirmed}
                                      {c.max != null ? `/${c.max}` : ''}
                                    </span>
                                    <span className="text-ink-3">
                                      {' '}
                                      {tr('activities.signedUpLower')}
                                    </span>
                                  </div>
                                  {c.mix && (
                                    <div className="text-[11px] text-ink-3">
                                      {tr('activities.mixCounts', {
                                        men: String(c.men.confirmed),
                                        menMax: String(c.men.max),
                                        women: String(c.women.confirmed),
                                        womenMax: String(c.women.max),
                                      })}
                                    </div>
                                  )}
                                  {c.club && (
                                    <div className="text-[11px] text-ink-3">
                                      {c.clubs
                                        .map((cl) => `${cl.confirmed}/${cl.max} ${cl.name}`)
                                        .join(' · ')}
                                    </div>
                                  )}
                                  {c.max != null && (
                                    <div className="text-[11px]">
                                      {full ? (
                                        <span style={{ color: '#b45309', fontWeight: 600 }}>
                                          {tr('activities.fullWord')}
                                          {waitlistOpen ? tr('activities.waitlistOpenSuffix') : ''}
                                        </span>
                                      ) : (
                                        <span className="text-ink-3">
                                          {tr(
                                            c.free === 1
                                              ? 'activities.freeCountOne'
                                              : 'activities.freeCountMany',
                                            { count: String(c.free) },
                                          )}
                                        </span>
                                      )}
                                      {waitlistOpen ? (
                                        <span className="text-ink-3">
                                          {tr('activities.onWaitlistSuffix', {
                                            count: String(c.waitlist),
                                            max:
                                              a.config?.maxReserves != null
                                                ? `/${a.config.maxReserves}`
                                                : '',
                                          })}
                                        </span>
                                      ) : c.waitlist > 0 ? (
                                        <span className="text-ink-3">
                                          {tr('activities.onWaitlistSuffix', {
                                            count: String(c.waitlist),
                                            max: '',
                                          })}
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                                <ChevronRight size={16} className="text-ink-3 flex-shrink-0" />
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
